// Hebrew source strings. This file is the reference: every key here must exist
// in ru.ts, and a missing translation falls back to the Hebrew text rather than
// blanking the screen.
//
// Grouped by screen, starting with the shop floor — that is where the
// Russian-speaking workers are, and where a language they cannot read costs
// production time.
export const he: Record<string, string> = {
  // ── shared ──
  'common.back': 'חזרה',
  // ── ⚠ מה ששומר רכיבי הבת חשף, 28/09/2026 ────────────────────────────────
  //
  // `i18n-child-components.test.ts` הולך אחרי ה-`import` של מסכי השטח
  // והקיוסק בעומק, וגם קורא **טקסט JSX** בין תגים — שתי צורות
  // שהשומר הקיים לא ראה. המשפטים כאן היו עברית קשיחה בלוח החתימה
  // של הלקוח, בחלון „חסר שרטוט" ובשלושה מסכים.
  // ראו edal-i18n-child-components, edal-field-techs-speak-russian.
  'common.gotIt': 'הבנתי',
  // לוח החתימה — הטאבלט שהטכנאי מושיט ללקוח.
  'sigpad.title': 'חתימת נציג הלקוח',
  'sigpad.signerName': 'שם החותם *',
  'sigpad.signerPlaceholder': 'מי מטעם הלקוח אישר את הטיפול',
  'sigpad.signature': 'חתימה',
  'sigpad.clear': 'נקה',
  'sigpad.signHere': 'חתום כאן באצבע',
  'sigpad.submitDefault': 'סגור את הביקור',
  'sigpad.hintDefault': 'אחרי החתימה הדוח נעול. שינוי אפשרי רק דרך מנהל השירות.',
  // „חסר שרטוט, דורש אישור מנהל" — חלון שחי בשני קיוסקים.
  'dhold.failed': 'האישור נכשל',
  'dhold.title': 'חסר שרטוט',
  'dhold.heading': 'חסר שרטוט — דורש אישור מנהל',
  'dhold.step1': '1 · אישור מנהל',
  'dhold.managerCode': 'קוד מנהל',
  'dhold.codePlaceholder': 'קוד',
  'dhold.orScan': 'או סריקת כרטיס מנהל',
  'dhold.scanHere': 'סרוק כאן',
  'dhold.step2': '2 · מה עושים עם החלק',
  'dhold.waive': 'אינו דורש עקיבות',
  'dhold.waiveHint': 'אפשר לעבוד עליו עכשיו, גם בלי שרטוט.',
  'dhold.wait': 'המתן לשרטוט',
  'dhold.waitHint': 'החלק עוצר. ברגע שהשרטוט יעלה — הקיוסק יודיע שאפשר לדווח.',
  'dhold.needId': 'יש להזין קוד או לסרוק כרטיס כדי לבחור.',
  'dhold.back': 'חזרה לרשימה',
  // „עלה שרטוט" והמתנה לשרטוט, בקיוסק הגמר ובתחנת העובד.
  'dhold.arrived': 'עלה שרטוט',
  'dhold.arrivedCanReport': '— אפשר לדווח',
  'dhold.waitingKiosk': 'מנהל קבע שלא להתחיל עד שהשרטוט יעלה. ברגע שהוא יגיע — המסך יודיע כאן.',
  'dhold.waitingStation': 'מנהל קבע שלא להתחיל עד שהשרטוט יעלה. ברגע שהוא יגיע — המסך יודיע.',
  'common.cancel': 'ביטול',
  'common.confirm': 'אישור',
  'common.save': 'שמור',
  'common.close': 'סגור',
  'common.loading': 'טוען…',
  'common.error': 'שגיאה',
  'common.search': 'חיפוש',
  'common.yes': 'כן',
  'common.no': 'לא',
  'common.quantity': 'כמות',
  'common.language': 'שפה',
  'common.units': 'יחידות',
  'common.minutes': 'דקות',
  'common.hours': 'שעות',
  'common.project': 'פרויקט',
  'common.part': 'חלק',
  'common.department': 'מחלקה',
  'common.worker': 'עובד',
  'common.none': 'אין',

  // ── דף מודפס מעוצב — משותף לכל הדוחות (PrintReport) ──
  'print.print': 'הדפס',
  'print.dialog': '{title} — תצוגת הדפסה',
  'print.hint': 'כך ייראה הדף המודפס. „הדפס" פותח את חלון ההדפסה — שם אפשר גם לשמור כ-PDF.',
  'print.printedAt': 'הודפס',
  'print.printedBy': 'על ידי',
  'print.customer': 'לקוח',
  'print.page': 'עמוד',
  'print.of': 'מתוך',
  'print.signName': 'שם',
  'print.signature': 'חתימה',
  'print.date': 'תאריך',

  // ── „כל מה שנדרש לפרויקט" — דוח הרכש של הפרויקט ──
  'reqReport.title': 'כל מה שנדרש לפרויקט',
  'reqReport.rows': 'שורות',
  'reqReport.here': 'כאן',
  'reqReport.state.ISSUED': 'נופק לייצור',
  'reqReport.state.RECEIVED': 'הגיע — ממתין',
  'reqReport.state.ORDERED': 'הוזמן',
  'reqReport.state.REQUESTED': 'טרם הוזמן',
  'reqReport.basis.RECEIVED': 'התקבל',
  'reqReport.basis.PROMISED': 'הובטח',
  'reqReport.basis.LEAD_TIME': 'לפי זמן אספקה',
  'reqReport.viewProduction': 'ייצור',
  'reqReport.viewWarehouse': 'מחסן',
  'reqReport.print': 'הדפס',
  'reqReport.statusTitle': 'סטטוס דרישות רכש',
  'reqReport.printView': 'תצוגה',
  'reqReport.sigPicked': 'ליקט (מחסן)',
  'reqReport.sigReceived': 'קיבל (ייצור)',
  'reqReport.printWarehouse': 'ליקוט מחסן',
  'reqReport.empty': 'אין עדיין דרישות רכש לפרויקט הזה.',
  'reqReport.loadError': 'לא ניתן לטעון את דרישות הרכש — זו תקלת טעינה, לא „אין דרישות". רענן את הדף.',
  'reqReport.unknown': 'לא ידוע',
  'reqReport.col.sku': 'מק״ט',
  'reqReport.col.name': 'תיאור',
  'reqReport.col.qty': 'כמות',
  'reqReport.col.status': 'סטטוס',
  'reqReport.col.eta': 'מתי יגיע',
  'reqReport.col.requester': 'מי דרש',
  'reqReport.col.orderedAt': 'הוזמן',
  'reqReport.col.promised': 'מובטח',
  'reqReport.col.neededBy': 'נדרש לייצור',
  'reqReport.col.late': 'איחור',
  'reqReport.late': 'באיחור',
  'reqReport.supplierNotYet': 'טרם נבחר ספק',
  'reqReport.supplierNotRecorded': 'לא נרשם ספק',
  'unplanned.PENDING': 'לא מתוכנן',
  'unplanned.PENDING.hint': 'נקלט בלי הזמנה — הרכש טרם השלים מחיר והזמנה',
  'unplanned.NEEDS_SUPPLIER': 'יש לפתוח ספק',
  'unplanned.NEEDS_SUPPLIER.hint': 'נקלט בלי הזמנה — הספק רשום כטקסט ועדיין לא נפתח במערכת',
  'reqReport.noNeededBy': 'לא נקבע',
  'reqReport.col.picked': 'נלקט',
  'reqReport.col.action': 'פעולה',

  // ── דוח חלקי הפרויקט — הדפסה וייצוא (U-0924-12) ──
  'partsReport.open': 'הדפס / ייצא',
  'partsReport.title': 'דוח חלקי פרויקט וסטטוס',
  'partsReport.dialogLabel': 'דוח חלקי פרויקט {project}',
  'partsReport.print': 'הדפס',
  'partsReport.excel': 'ייצא לאקסל',
  'partsReport.printedAt': 'הודפס:',
  'partsReport.printedBy': 'על ידי:',
  'partsReport.summary': 'סיכום',
  'partsReport.sum.parts': 'מק״טים',
  'partsReport.sum.finished': 'הושלמו',
  'partsReport.sum.noDrawing': 'בלי שרטוט',
  'partsReport.sum.withNc': 'עם אי-התאמה פתוחה',
  'partsReport.sum.blocked': 'עצורים / חסומים',
  'partsReport.partsSection': 'חלקים — מעץ המוצר',
  'partsReport.purchaseSection': 'רכש — כל מה שנדרש לפרויקט',
  'partsReport.noParts': 'אין עדיין חלקים בעץ המוצר של הפרויקט.',
  'partsReport.treeError': 'לא ניתן לטעון את עץ המוצר — זו תקלת טעינה, לא „אין חלקים".',
  'partsReport.extrasError': 'אי-התאמות וחסימות לא נטענו — העמודות האלה ריקות בדוח הזה, ולא „אין".',
  'partsReport.retry': 'נסה שוב',
  'partsReport.col.sku': 'מק״ט',
  'partsReport.col.name': 'שם',
  'partsReport.col.qty': 'כמות',
  'partsReport.col.status': 'מצב',
  'partsReport.col.stage': 'שלב נוכחי',
  'partsReport.col.progress': 'יחידות לפי שלב',
  'partsReport.col.drawing': 'שרטוט',
  'partsReport.col.nc': 'אי-התאמות פתוחות',
  'partsReport.col.blocked': 'עצור / חסום',
  'partsReport.col.unit': 'יחידה',
  'partsReport.col.received': 'התקבל',
  'partsReport.col.supplier': 'ספק',
  'partsReport.noDrawing': 'אין שרטוט',
  'partsReport.hasDrawing': 'יש שרטוט',
  'partsReport.revision': 'רוויזיה {rev}',
  'partsReport.version': 'גרסה {v}',
  'partsReport.allStagesDone': 'כל השלבים הושלמו',
  'partsReport.status.done': 'הושלם',
  'partsReport.status.in_work': 'בעבודה',
  'partsReport.status.not_started': 'טרם התחיל',
  'partsReport.status.stuck': 'תקוע',
  'partsReport.blocked.HOLD': 'עצירת ייצור',
  'partsReport.blocked.ECO': 'שינוי הנדסי',
  'partsReport.blocked.NC': 'אי-התאמה',

  // ── כל המק״טים עם השרטוט — כרטיס הפרויקט בלוח ההנדסה ──
  // ⚠ לשונית אחת, 28/09/2026: „חלק = מק״ט = שרטוט, דבר אחד" — ולכן שתי לשוניות
  // („מק״טים" ו„שרטוטים") היו הפגם עצמו. ראו edal-part-is-sku.
  'partsGallery.tab': 'מק״טים ושרטוטים',
  'partsGallery.tabHint': 'כל המק״טים של הפרויקט עם תמונת השרטוט — להעלות, לבטל ולהכריע מאותו מקום',
  // ── כרטיס ההנדסה: חמש לשוניות + תת-מבטים (E2, אלי 30/10/2026) ──
  'engCard.tabs': 'לשוניות כרטיס הפרויקט',
  'engCard.views': 'מבטים בלשונית',
  'engCard.tab.overview': 'סקירה',
  'engCard.tab.drawings': 'שרטוטים',
  'engCard.tab.purchase': 'רכש',
  'engCard.tab.quality': 'איכות',
  'engCard.tab.thread': 'שרשור',
  'engCard.view.state': 'מצב ועריכה',
  'engCard.view.brief': 'תיק',
  'engCard.view.times': 'זמנים והיסטוריה',
  'engCard.view.family': 'תתי-פרויקטים',
  'engCard.view.parts': 'מק״טים ושרטוטים',
  'engCard.view.eco': 'ECO / שינויים',
  'engCard.view.media': 'תמונה, תלת-מימד, חיתוך',
  'engCard.view.purchase': 'רכש',
  'engCard.view.qa': 'FAT, דרישות ואי-התאמות',
  'engCard.view.reviews': 'סקרים ואישורים',
  'engCard.view.thread': 'שרשור',
  'engCard.view.meetings': 'פגישות',
  'engCard.count.missing': '{n} חסר',
  'engCard.count.eco': '{n} ECO',
  'engCard.count.fat': '{n} פערים',
  'engCard.count.mySign': 'החתימה שלך',
  'engCard.count.ask': 'ממתין לך',
  'engCard.reportHours': 'דווח שעות',
  'engCard.more': 'עוד',
  'engCard.moreHint': 'עוד פעולות: קשר פרויקט זהה, אחד לפרויקט אחד, הצע לקטלוג, תלת-מימד, הרחב',
  'engCard.section.hours': 'דיווח שעות',
  'engCard.section.timeline': 'ציר זמן',
  'engCard.section.history': 'מה קרה בפרויקט',
  'partsGallery.title': 'מק״טים ושרטוטים',
  'partsGallery.search': 'חפש מק״ט או שם…',
  'partsGallery.emptyCanUpload': 'אין עדיין חלקים בעץ המוצר. גרור למטה את השרטוט הראשון — הוא זה שיבנה אותו.',
  'partsGallery.empty': 'אין עדיין חלקים בעץ המוצר — הם נוצרים מהשרטוט הראשון שעולה.',
  'partsGallery.noPreview': 'אין תצוגה מקדימה',
  'partsGallery.openDrawing': 'פתח שרטוט',
  'partsGallery.openDrawingOf': 'פתח את השרטוט של {sku}',
  'partsGallery.thumbAlt': 'שרטוט {sku}',

  // ── תמונת השרטוט על כל מק״ט, בכל עץ (אלי 26/09/2026) ──
  'drawingThumb.none': 'אין שרטוט',
  'drawingThumb.noneTip': 'למק״ט {sku} אין שרטוט פעיל. ההנדסה מעלה אותו בכרטיס הפרויקט.',
  'drawingThumb.noPreview': 'אין תצוגה',
  'drawingThumb.open': 'הגדל את השרטוט של {sku}',
  'drawingThumb.dialogTitle': 'שרטוט {sku}',
  'drawingThumb.newTab': 'פתח בלשונית חדשה',
  'drawingThumb.openFull': 'פתח את הגיליון המלא',
  'drawingThumb.failed': 'התמונה לא נטענה',
  'drawingThumb.failedTip': 'תמונת השרטוט של {sku} לא נטענה — זו תקלת טעינה, לא „אין שרטוט". לחץ כדי לנסות שוב.',
  'drawingThumb.retry': 'נסה שוב',
  'drawingThumb.rev': 'מהדורה {rev}',
  'drawingThumb.ver': 'גרסה {v}',
  'drawingThumb.previewOnly': 'מוצגת התצוגה המקדימה — קובץ ה-PDF עצמו לא נמצא. פנה להנדסה כדי להעלות אותו מחדש.',
  'drawingThumb.noPdf': 'למק״ט יש שרטוט, אבל הקובץ שלו לא נמצא על הדיסק. זו תקלת קובץ ולא „אין שרטוט" — פנה להנדסה כדי להעלות את הגיליון מחדש.',

  // ── badge / identification ──
  'badge.title': 'העבר תג או הקלד מספר עובד',
  'badge.placeholder': 'מספר עובד',
  'badge.enter': 'כניסה',
  'badge.notFound': 'תג לא מזוהה — פנה למנהל המשמרת',
  'badge.hello': 'שלום {name}',
  'badge.logout': 'יציאה',

  // ── worker station ──

  // ── fault reporting ──
  'fault.title': 'דיווח תקלה',
  'fault.type': 'סוג התקלה',
  'fault.describe': 'תאר את הבעיה במשפט אחד',
  'fault.markOnDrawing': 'סמן על השרטוט',
  'fault.send': 'שלח דיווח',
  'fault.sent': 'הדיווח נשלח. המנהל רואה אותו עכשיו.',
  'fault.timerStopped': 'השעון על החלק נעצר — אינך מפסיד זמן בגלל התקלה',


  // ── warehouse kiosk ──
  'warehouse.subtitle': 'מחסן — ניפוק / החזרה',
  'warehouse.stationSignedOut': 'העמדה אינה מחוברת למערכת. יש להתחבר בעמדה זו כדי לנפק או להחזיר פריטים.',
  'warehouse.issue': 'ניפוק',
  'warehouse.returnToStock': 'החזרה למלאי',
  'warehouse.worker': 'עובד (סרוק תג)',
  'warehouse.workerPlaceholder': 'מספר עובד',
  'badge.scanPlaceholder': 'סרוק תג עובד',
  'badge.change': 'החלף',
  'badge.typingRefused': 'יש לסרוק את התג — הקלדת מספר עובד אינה מתאפשרת כאן. אם התג אינו נסרק, בחר מהרשימה.',
  'warehouse.project': 'פרויקט (סרוק / הקלד מספר)',
  'warehouse.projectPlaceholder': 'מספר פרויקט',
  'warehouse.projectNotFound': 'לא נמצא פרויקט במספר זה',
  'warehouse.item': 'פריט (סרוק מק״ט / חפש בשם)',
  'warehouse.itemPlaceholder': 'מק״ט או שם — למשל SCRE-000100 / מנוע',
  'warehouse.inStockN': '(במלאי {n})',
  'warehouse.stockNow': 'במלאי {n}',
  'saw.fullBar': 'מוט שלם',
  'saw.pieces': 'חתיכות',
  'saw.notOnRack': 'לא נמצא במדף',
  'saw.tryAgain': 'נסה שוב',
  'saw.barClosedWhileShown': 'המוט הזה נסגר — לא נחתך ממנו דבר במשך 30 דקות, ולכן לא נספר כעבודה. לחץ „המוט הבא" כדי להתחיל מוט.',
  'saw.outOf': 'מתוך',
  'saw.item': 'פריט',
  'saw.drawing': 'שרטוט',
  'saw.cutDone': 'חתכתי',
  'saw.release': 'לא חותך עכשיו — החזר את המוט',
  'saw.releaseConfirm': 'להחזיר את המוט? השעון נעצר ולא נרשם זמן, והחלקים חוזרים לתור.',
  // F4 (אלי 30/10/2026): משוב אחרי „החזר מוט" — היה: אין שינוי נראה.
  'saw.releaseYes': 'כן, החזר את המוט',
  'saw.releaseNo': 'ביטול',
  'saw.released': 'המוט חזר לתור ✓',
  'saw.releasedBody': 'השעון נעצר — 0 דקות. החלקים חזרו לתור החיתוך.',
  'saw.addRemnant': 'הוסף שארית ידנית',
  'saw.remnantProfile': 'פרופיל',
  'saw.remnantSearch': 'חיפוש פרופיל…',
  'saw.remnantPick': 'בחר פרופיל',
  'saw.remnantLength': 'אורך שנמדד (מ״מ)',
  'saw.remnantSave': 'שמור ותכנן מחדש',
  'saw.remnantAdded': 'נוספה שארית {sku} · {mm} מ״מ — התכנון עודכן',
  'saw.remnantScrapped': '{mm} מ״מ קצר מהשארית המינימלית — נרשם כגריטה',
  'saw.finishedBar': 'סיימתי את המוט',
  'saw.closeBar': 'סגור מוט',
  'saw.uncutAsk': 'נשארו {n} חתיכות שלא סימנת. מה קרה להן?',
  'saw.uncutAsk_one': "נשארה חתיכה אחת שלא סימנת. מה קרה לה?",
  'saw.uncutAsk_many': "נשארו {n} חתיכות שלא סימנת. מה קרה להן?",
  'saw.uncutAllCut': 'חתכתי את כולן',
  'saw.uncutAllCutHint': 'ייסגרו כחתוכות ולא יתוכננו שוב',
  'saw.uncutNone': 'לא חתכתי אותן',
  'saw.uncutNoneHint': 'יחזרו לתכנון ויחתכו ממוט אחר',
  'saw.answerFirst': 'בחר תשובה כדי לסגור את המוט',
  'saw.history': 'היסטוריית חיתוך',
  'saw.historyEmpty': 'עוד לא נחתך כלום.',
  'saw.searchCuts': 'חפש פרויקט, פרופיל, מק״ט או אורך',
  'saw.missingConfirm': 'לסמן שהשארית {sku} לא נמצאה במדף? התוכנית תחושב מחדש בלעדיה ותקבל מוט שלם.',
  'saw.skipConfirm': 'לדלג על החתיכה הזאת? היא תחזור לתכנון ותתוכנן שוב על מוט אחר.',
  'saw.callManager': 'פנה למנהל הייצור.',
  'saw.subtitle': 'עמדת מסור — חיתוך פרופילים',
  'saw.emptyBody': 'התור ריק. כשמהנדס יעלה רשימת חיתוך חדשה היא תופיע כאן לבד, כבר ממוטבת מול שאר הפרויקטים.',
  'saw.replanLead': 'התוכנית חושבה מחדש. ',
  'saw.replanTail': ' — השינוי משפיע על מה שאחריו.',
  'saw.replanSame': 'המוט שאתה חותך עכשיו לא השתנה',
  'saw.replanTitle': 'הגיעה רשימת חיתוך חדשה',
  'saw.scrapNote': 'מה שנשאר קצר מדי לשימוש — נרשם כגרוטאה.',
  'saw.fullyUsed': 'המוט נוצל במלואו. לא נשארה שארית.',
  'saw.stockOffNote': 'ניהול מלאי המסור כבוי — השארית לא נרשמת במדף.',
  'saw.closeHint': 'נשאר לסגור את המוט ולרשום את השארית.',
  'saw.allCut': 'כל החתיכות במוט הזה נחתכו',
  'saw.returnToRack': 'והחזר אותה למדף — התוכנית כבר יודעת עליה.',
  'saw.markRemnant': 'סמן על השארית:',
  'saw.scanBadge': 'סרוק תג עובד',
  'saw.nothingToCut': 'אין מה לחתוך',
  // בחירת פרויקט במסור (אלי 01/10/2026, U-1001-02)
  'saw.project': 'פרויקט',
  'saw.allProjects': 'כל הפרויקטים',
  'saw.pickProject': 'בחר פרויקט',
  'saw.clearProject': 'כל הפרויקטים',
  'saw.projectSearch': 'מספר או שם פרויקט',
  'saw.projectNone': 'אין פרויקט שירד לייצור ונשאר בו מה לחתוך. נסה מספר אחר, או חזור לכל הפרויקטים.',
  'saw.projectPieces': '{n} חתכים · {p} פרופילים',
  'saw.projectCut': 'הפרויקט {number} נחתך',
  'saw.projectCutBody': 'לא נשאר בו מה לחתוך. חזור לכל הפרויקטים כדי להמשיך בתור.',
  'saw.otherProject': 'פרויקט אחר — נחתך עם המוט',
  'saw.replanProject': 'תכנן מחדש רק לפרויקט הזה',
  'saw.replanProjectHint': '{n} מוטות משותפים עם פרויקט אחר. ברירת המחדל: לחתוך את כל המוט.',
  'saw.replanProjectConfirm': 'לתכנן מחדש כך שלפרויקט {number} יהיו מוטות משלו? החלקים של שאר הפרויקטים יעברו למוטות אחרים.',
  'saw.replanRunning': 'מוטות שכבר על המסור לא שונו ({n}) — חתוך אותם כרגיל.',
  'saw.projectsError': 'רשימת הפרויקטים לא נטענה. בדוק חיבור ונסה שוב.',
  'saw.switchWorker': 'החלף עובד',
  'saw.whichSaw': 'על איזה מסור אתה עובד?',
  'saw.whichSawHint': 'נשאל פעם אחת ביום. אפשר להחליף בכל רגע ב„החלף מסור".',
  'saw.changeSaw': 'החלף מסור',
  'saw.sawsError': 'רשימת המסורים לא נטענה. בדוק חיבור ונסה שוב.',
  'saw.workNow': 'העבודה עכשיו',
  'saw.notFound': 'עובד לא נמצא',
  'saw.takeFromRack': 'קח מהמדף',
  'saw.updateNow': 'עדכן עכשיו',
  'saw.wholeQueue': 'כל התור',
  'saw.barClosed': 'המוט נסגר',
  'saw.doNotCut': 'אל תחתוך.',
  'saw.wholeBar': 'כל המוט',
  'saw.notUploaded': '· לא הועלה',
  'saw.piece': 'חתיכה',
  'saw.scrapShort': '· פחת',
  'saw.continue': 'המשך',
  'saw.error': 'שגיאה',
  'saw.skip': 'דלג',
  'saw.miscut': 'טעיתי במידה',
  'saw.miscutTitle': 'החתך יצא במידה אחרת',
  'saw.miscutRequired': 'נדרש',
  'saw.miscutActual': 'מה נחתך בפועל? (מ״מ)',
  'saw.miscutHint': 'החתיכה תחזור לתכנון ותתוכנן שוב, ומה שנחתך יירשם במדף באורך שמדדת.',
  'saw.miscutSave': 'רשום ותכנן מחדש',
  'saw.miscutSaved': 'נרשם — החתיכה חזרה לתכנון',
  'saw.miscutRemnant': 'נרשם במדף',
  'saw.miscutScrapped': 'קצר מהמינימום — נרשם כגרוטאה',
  'saw.metres': 'מ׳',
  'saw.mm': 'מ״מ',
  'saw.running': 'זמן על המסור',
  'saw.bar': 'מוט',
  'warehouse.discipline': 'לאיזו עלות זה נכנס',
  'warehouse.disciplineAuto': 'לפי הקטלוג',
  'warehouse.notInCatalog': 'לא בקטלוג — {verb} כטקסט חופשי (בלי עדכון מלאי)',
  'warehouse.willIssue': 'ינופק',
  'warehouse.willReturn': 'יוחזר',
  'warehouse.overStock': 'הכמות ({qty}) גדולה מהמלאי הרשום ({stock}). אפשר לנפק בכל זאת.',
  'warehouse.issueButton': 'נפק לפרויקט',
  'warehouse.returnButton': 'החזר למלאי',
  'warehouse.currentSession': 'בפעולה הנוכחית',
  'warehouse.wasIssued': 'נופק',
  'warehouse.wasReturned': 'הוחזר',
  'warehouse.stock': 'מלאי',

  // ── scan kiosk ──
  'scan.title': 'תחנת סריקה',
  'scan.scanPart': 'סרוק ברקוד של חלק',
  'scan.startStage': 'התחל שלב',
  'scan.endStage': 'סיים שלב',
  'scan.coWorkers': 'עובדים נוספים',
  'scan.addCoWorker': 'הוסף עובד',
  'scan.success': 'נרשם בהצלחה',

  // ── NC kiosk (несоответствия) ──
  'nc.subtitle': 'דיווח אי-התאמה',
  'nc.switchWorker': 'החלף עובד',
  'nc.badgeTitle': 'סרוק או הקלד תג עובד',
  'nc.badgePlaceholder': 'מספר תג',
  'nc.continue': 'המשך',
  'nc.badgeNotFound': 'תג לא זוהה',
  'nc.photoFailed': 'לא ניתן לטעון את התמונה',
  'nc.photoNotSaved': 'הדיווח נשמר, אבל התמונה לא נשמרה. יש לדווח שוב עם תמונה.',
  'nc.sendFailed': 'שגיאה בשליחה',
  'nc.sent': 'האי-התאמה נשלחה',
  'nc.sentNote': 'ההנדסה והמנהלים קיבלו התראה.',
  'nc.reportAnother': 'דווח על אי-התאמה נוספת',
  'nc.searchPlaceholder': 'חפש מק״ט או מספר פרויקט',
  'nc.scan': 'סרוק',
  'nc.noParts': 'לא נמצאו חלקים',
  'nc.type': 'סוג אי-ההתאמה',
  'nc.photo': 'צילום',
  'nc.takePhoto': 'צלם מהמצלמה (רשות)',
  'nc.notePlaceholder': 'תיאור (לא חובה)',
  'nc.send': 'שלח אי-התאמה',

  // ── badge steps (scan kiosk) ──
  'badge.scanBadge': 'סרוק תג עובד',
  'badge.scanOrType': 'סרוק או הקלד והקש Enter',
  'badge.badgeNumber': 'מספר תג',
  'badge.ok': 'אישור',
  'badge.scanWithCamera': 'סרוק תג עם מצלמה',
  'badge.tapCard': 'הצמד כרטיס NFC',
  'badge.greet.MORNING': 'בוקר טוב {name}',
  'badge.greet.NOON': 'צהריים טובים {name}',
  'badge.greet.EVENING': 'ערב טוב {name}',
  'badge.helloName': 'שלום, {name}',
  'badge.departmentOf': 'מחלקת {name}',
  'badge.scanDrawingHint': 'סרוק שרטוט להתחלה / סיום, או סרוק תג נוסף להוספת שותף',
  'badge.barcode': 'ברקוד',
  'badge.notMe': 'לא אני, חזור',
  'badge.scanDrawingCamera': 'סרוק שרטוט עם מצלמה',
  'units.howMany': '{stage} — כמה יחידות? (1–{max})',
  'units.sending': 'שולח…',
  'keypad.clear': 'נקה',

  // ── worker station ──
  'st.subtitle': 'עמדת עובד',
  'st.activeDept': 'מחלקה פעילה:',
  'st.logout': 'התנתק',
  // ״התנתק״ described what the screen did and not what the person is doing: a
  // worker handing the station to the next man. Eli, 2026-09-02, on the same
  // button failing to close the session behind it: "ברגע שהוא לוחץ החלף עובד,
  // הוא צריך להחליף עובד."
  'st.switchWorker': 'החלף עובד',
  'st.badgeNotFound': 'תג לא זוהה',
  'st.scanBadge': 'סרוק תג עובד',
  'st.badgePlaceholder': 'מספר תג',
  'st.enter': 'כניסה',
  'st.myParts': 'החלקים שלי ({n})',
  'st.crewToday': 'היום: {h} ש׳ על פרויקט',
  'st.crewTodayHint': 'חלק מהיום שלך על הפרויקט הזה — החלקים שלו ראשונים ברשימה למטה.',
  // מציג המודל בעמדת העובד. שלושת הבאים הם ה-title של כפתורי אייקון, כלומר
  // גם השם הנגיש היחיד שלהם.
  'st.modelLoading': 'טוען מודל תלת-מימד…',
  'st.modelLoadFailed': 'טעינת המודל נכשלה',
  'st.modelCenter': 'מרכז',
  'st.modelSection': 'חתך',
  'st.modelParts': 'חלקים',
  'st.modelPartsCount': 'חלקים ({n})',
  'st.modelShowAll': 'הצג הכל',
  'st.modelIsolate': 'בודד',
  // מציג ה-3D המאוחד (אלי 17/10/2026)
  'st.modelTools': 'כלים',
  'st.modelViews': 'מבט',
  'st.viewIso': 'איזומטרי',
  'st.viewFront': 'חזית',
  'st.viewTop': 'עליון',
  'st.viewSide': 'צד',
  'st.modelMeasure': 'מדוד בין 2 נקודות',
  'st.modelMeasureHint': 'לחץ על שתי נקודות במודל — נצמד לפינות',
  'st.modelMeasureSecond': 'לחץ על הנקודה השנייה',
  'st.modelMeasureAim': 'הזז את המודל כך שהצלב על הפינה — הנקודה נצמדת',
  'st.modelMarkPoint': 'סמן נקודה {n} מתוך 2',
  'st.modelDistance': 'מרחק ישיר',
  'st.modelAxisLen': 'אורך',
  'st.modelAxisH': 'גובה',
  'st.modelAxisW': 'רוחב',
  'st.modelFlip': 'הפוך',
  'st.modelClear': 'נקה',
  'st.modelExplode': 'פיצוץ הרכבה',
  'st.modelExplodeClosed': 'סגור',
  'st.modelExplodeSubs': 'תת־הרכבות',
  'st.modelExplodeAll': 'כל החלקים',
  'st.modelDisplay': 'תצוגה',
  'st.modelColorStatus': 'צבע לפי סטטוס',
  'st.modelXray': 'שקף שאר החלקים',
  'st.modelGhostDone': 'שקף מה שהושלם',
  'st.mDone': 'הושלם',
  'st.mWork': 'בעבודה',
  'st.mIssue': 'חוסר / אי-התאמה',
  'st.mTodo': 'טרם התחיל',
  'st.mBuy': 'רכש',
  'st.mNone': 'אין כרטיס במערכת',
  'st.modelSkus': '{n} מק״טים · {u} יחידות במודל',
  'st.modelMainAsm': 'הרכבה ראשית',
  'st.modelOutliers': '{n} חלקים רחוקים מהמכונה לא נכללו במסגור: {list}',
  'st.modelDims': 'מידות (מהמודל)',
  'st.modelQty': 'כמות במודל',
  'st.modelSubAsm': 'תת־הרכבה',
  'st.modelStatusFrom': 'סטטוס · מדיווחי העמדות',
  'st.modelReportNc': 'דווח אי-התאמה עם המדידה',
  'st.modelMeasureNote': 'מדידה בתלת מימד: {d} מ״מ (אורך {x} · גובה {y} · רוחב {z})',
  'st.modelMm': 'מ״מ',
  'st.modelUnnamed': 'חלק ללא שם',
  'st.modelOpen': 'תלת מימד',
  'st.modelLookReal': 'מציאותי',
  'st.modelLookStatus': 'סטטוס',
  'st.modelHideShow': 'הסתר / הצג',
  'st.modelSearch': 'חיפוש מק״ט',
  'st.modelNoMatch': 'לא נמצא מק״ט כזה במודל',
  'st.modelScopePart': 'רק החלק',
  'st.modelScopeAsm': 'ההרכבה שלו',
  'st.modelScopeAll': 'כל המודל',
  'st.modelXrayRest': 'שקף את השאר',
  'st.modelDetails': 'פרטים',
  'st.modelNotInModel': 'המק״ט {p} לא נמצא במודל — מוצג כל המודל. חפש אותו ברשימת החלקים.',
  // מציג השרטוט וכלי הסימון בעמדת העובד. ה-aria-label נפרדים מה-title
  // בכוונה: „הגדל" לבדו אינו אומר לקורא מסך מה מוגדל.
  'st.drawingAlt': 'שרטוט',
  'st.dimChecked': 'נבדק',
  'st.dimTapToVerify': 'הקש לאישור שהמידה נבדקה',
  'st.zoomIn': 'הגדל',
  'st.zoomInDrawing': 'הגדל שרטוט',
  'st.zoomOut': 'הקטן',
  'st.zoomOutDrawing': 'הקטן שרטוט',
  'st.fitToScreen': 'התאם למסך',
  'st.criticalDims': 'מידות קריטיות: {done}/{total} נבדקו',
  'st.tapCircleToVerify': 'הקש על עיגול לאישור',
  'st.markupClear': 'נקה',
  'st.markupSave': 'שמור סימון',
  'st.markupTitle': 'סמן על השרטוט',
  // פאנל עץ המוצר בעמדת העובד. 'st.productTree' כבר קיים ומשמש ככותרת.
  'st.treeProject': 'פרויקט {n}',
  'st.treeSearch': 'חפש מק״ט או שם בעץ',
  'st.treeSearchLabel': 'חיפוש בעץ המוצר',
  'st.treeNoMatch': 'לא נמצאו חלקים תואמים',
  'st.treeEmpty': 'אין חלקים נוספים בעץ',
  'st.treeDrawingOf': 'שרטוט {part}',
  'st.treeViewOnly': 'תצוגה בלבד',
  'st.treeBack': 'חזרה לעץ',
  'st.treeYourPart': 'החלק שלך',
  'st.inWorkNow': 'בעבודה כעת',
  'st.notStarted': 'טרם החל',
  'st.thisIsNc': 'זו אי-התאמה',
  'st.pinned': 'דחוף — נעץ על ידי מנהל',
  'st.completed': 'הושלם',
  'st.readyStage': 'מוכן · {stage}',
  'st.notReadyYet': 'עדיין לא מוכן · {dept}',
  'st.myDept': 'המחלקה שלי:',
  'st.searchPlaceholder': 'חפש מספר פרויקט (למשל 250039) או מק״ט — לכל השרטוטים בכל המחלקות',
  'st.scanDrawingPlaceholder': 'סרוק ברקוד של שרטוט — והעבודה מתחילה',
  'st.scanNotADrawing': 'זהו אינו ברקוד של שרטוט. סרוק את הברקוד שבפינת השרטוט.',
  'st.scanPartNotFound': 'מק״ט {part} לא נמצא. ייתכן שהשרטוט אינו מעודכן במערכת.',
  'st.searchResults': 'תוצאות חיפוש',
  'st.waitingInDept': 'ממתין במחלקה {dept}',
  'st.noPartsFound': 'לא נמצאו חלקים',
  'st.noPartsWaiting': 'אין חלקים ממתינים במחלקה — חפש מספר פרויקט למעלה כדי למצוא שרטוט ולמשוך אותו',
  'st.withYou': 'אצלך · {stage}',
  'st.inDept': 'במח׳ {dept}',
  'st.canPull': ' · ניתן למשוך',
  'st.tasks': 'משימות לביצוע ({n})',
  'st.taskDue': 'יעד {date}',
  'st.overdue': ' · באיחור',
  'st.taskDone': 'בוצע',
  'st.confirmDone': 'לאשר ביצוע?',
  'st.tryAgain': 'שגיאה — נסה שוב',
  'st.engUpdated': 'הנדסה עדכנה אי-התאמה',
  'st.ncOpen': 'אי-התאמה פתוחה: {cat} · נמצא ב{stage}',
  'st.ncOpenBlocking': 'אי-התאמה פתוחה · חוסמת: {cat} · נמצא ב{stage}',
  'st.reworkBack': 'חזר לתיקון — {stage}',
  'st.viewEngMarkup': 'ראה את הסימון על השרטוט',
  'st.drawingReplaced': 'השרטוט הוחלף',
  'st.drawingReplacedBody': 'ההנדסה העלתה שרטוט חדש למק״ט הזה. אין להמשיך לפי השרטוט הקודם.',
  'st.drawingReplacedRev': 'ההנדסה העלתה רוויזיה {rev}. אין להמשיך לפי השרטוט הקודם.',
  'st.showNewDrawing': 'הצג את השרטוט החדש',
  'st.workHalted': 'העבודה נעצרה — יש להיערך בהתאם.',
  'st.ownBadge': 'זה התג שלך',
  'st.coAlready': 'השותף כבר נוסף',
  'st.coMax': 'ניתן להוסיף עד {n} שותפים',
  'st.coMax_one': "ניתן להוסיף שותף אחד בלבד",
  'st.coMax_many': "ניתן להוסיף עד {n} שותפים",
  'st.coNotFound': 'תג לא נמצא',
  'st.badManagerCode': 'סיסמת מנהל שגויה',
  'st.drawingMissingTitle': 'החלק ממתין לשרטוט',
  'st.sessionClosedZero': 'השעון נסגר — 0 יחידות, החלק לא התקדם',
  'st.reported': '✓ דווח {done}/{total} · {stage}',
  'st.finishedWithMissing': ' · שים לב — חסרים עדיין חלקים: {list}',
  'st.stageCompleted': ' · השלב הושלם',
  'st.reportFailed': 'שגיאה בדיווח',
  'st.priorUpdated': '✓ עודכן {stage}: {units} · התחלת עבודה',
  'st.sessionClosedStarted': '✓ הסשן נסגר · התחלת עבודה',
  'st.closeSessionFailed': 'שגיאה בסגירת הסשן',
  'st.productTree': 'עץ המוצר',
  'st.fullScreen': 'מסך מלא',
  'st.threeD': 'תלת מימד',
  'st.no3d': 'לפרויקט {project} לא הועלה מודל תלת מימד. מהנדס מעלה אותו במסך „העלאת שרטוטים" ← „מודל תלת-מימד" (קובץ .glb).',
  'st.howTo': 'איך עושים את זה?',
  'st.howToTitle': 'הדרכה — החומר של הפרויקט הזה',
  'st.howToNone': 'אין חומר הדרכה שמתאים לפרויקט הזה.',
  'st.howToDocs': 'מסמכים',
  'st.howToNoDocs': 'עדיין לא הועלו מסמכים לדגם הזה.',
  'st.howToPage': 'עמ׳',
  'st.howToFaqs': 'טעויות נפוצות',
  'st.howToOpen': 'פתח',
  'st.print': 'הדפס',
  // התווית מתורגמת גם כשהמשפט עצמו מגיע מהשרת בעברית — אותו לקח מהודעת
  // „ברקוד מגרסה ישנה": עובד שהמסך שלו ברוסית צריך לפחות לדעת שזו חסימה.
  'st.blocked': 'חסום — אי אפשר להתחיל',
  'st.ncOpenMark': 'אי-התאמה פתוחה — לא עובר לתחנה הבאה',
  // ── „חלקים חסרים" (27/09/2026) ──────────────────────────────────────────
  //
  // אלי: „לאפשר חופשי, עם סימון „חלקים חסרים"." הסימון נקרא בכל המסכים מרכיב
  // אחד (`components/scan/MissingChildren.tsx`), ולכן גם מהמפתחות האלה בלבד.
  'st.missingChildren': 'חלקים חסרים ({n})',
  'st.missingChildrenMore': 'ועוד {n}',
  'st.missingChildrenHelp': 'אפשר להתחיל ולעבוד — הכנות, שבלונה, ריתוך חלקי. לסיום השלב צריך שכל החלקים יגיעו.',
  'st.comesFrom': 'מגיע מ:',
  'st.routeStart': '— תחילת מסלול',
  'st.goesTo': 'ממשיך אל:',
  'st.routeEnd': 'סיום — שלב אחרון',
  'st.process': 'תהליך',
  'st.currentStage': 'שלב נוכחי',
  // „אם אני בכיפוף, אני בכיפוף" — אלי, 27/09/2026 (F-0927-03).
  'st.myStage': 'כאן אתה עובד',
  'st.partners': 'שותפים לעבודה',
  'st.partnerPlaceholder': 'סרוק/הקלד תג שותף',
  'st.partnersMax': 'הגעת למקסימום שותפים ({n})',
  'st.working': 'בעבודה',
  'st.partnersNote': 'השותפים נרשמים על העבודה יחד איתך.',
  'st.finishAndReport': 'סיום עבודה ודיווח כמות',
  'st.notInYourDept': 'החלק אינו עובר במחלקה שלך',
  'st.joinAnyway': 'הצטרף לעבודה על החלק (אישור מנהל)',
  'st.joinCurrentStage': 'המחלקה שלך אינה בתהליך של החלק. באישור מנהל אפשר להצטרף לשלב שהחלק עומד בו עכשיו — הדקות יירשמו עליו.',
  'chk.remaining': 'לפני תחילת העבודה — נותרו {n}',
  'chk.allDone': 'הכול אושר · אפשר להתחיל',
  'chk.willNotStart': 'העבודה לא תתחיל עד שכל הסעיפים יאושרו.',
  'chk.markDone': 'סמן כבוצע',
  'chk.unmark': 'בטל סימון',
  'chk.requestEng': 'בקש בדיקת הנדסה',
  'chk.valuePlaceholder': 'הערך שנמדד',
  'chk.engHint': 'סעיף זה דורש בדיקת הנדסה. סימון כאן מזמין את המהנדס ואינו עוצר את העבודה שלך.',
  'chk.engSent': 'נשלחה בקשה לבדיקת הנדסה. אפשר להמשיך לעבוד — החלק לא יעבור לתחנה הבאה עד שמהנדס יאשר.',
  'gate.beforeStart': 'לפני שמתחילים',
  // נעילת שרטוט של חלק שאינו במחלקת העובד. לוח נעול שאינו אומר **למה** הוא
  // לוח שהעובד הולך לחפש עליו את מנהל המשמרת.
  'gate.notYoursTitle': 'החלק אינו במחלקה שלך',
  'gate.notYoursHint': 'החלק נמצא כעת ב{dept}. כדי לפתוח את השרטוט יש למשוך אותו למחלקה שלך באישור מנהל — והשעון יתחיל.',
  'gate.backToList': 'חזרה לרשימת השרטוטים',
  'gate.drawingLocked': 'השרטוט ייפתח אחרי הבדיקות',
  'gate.drawingLockedHint': 'יש להשלים את הבדיקות שלפני תחילת העבודה. הן נדרשות פעם אחת לכל חלק.',
  'gate.qaAfter': 'בדיקות איכות לפני שמעבירים הלאה',
  'gate.doneRecording': 'סיימתי לרשום',
  'meas.title': 'מידות למדידה',
  'meas.titleRef': 'מידות שיש למדוד',
  'meas.countOpen': "{n} מתוך {total} טרם נמדדו",
  'meas.countOpen_one': "{n} מתוך {total} טרם נמדדה",
  'meas.countOpen_many': "{n} מתוך {total} טרם נמדדו",
  'meas.countDone': '{total} נמדדו',
  'meas.countRef': '{total} מידות · נרשמות בסיום',
  'meas.unit': 'יחידה',
  'meas.ofTotal': 'מתוך {n}',
  'meas.critical': 'קריטי',
  'meas.scopeMine': 'התחנה שלך',
  'meas.scopeGeneral': 'לא צוינה תחנה',
  'meas.scopeOther': 'תחנה אחרת',
  'meas.calibratedOnly': 'מד מכויל בלבד',
  'meas.method': 'אמצעי מדידה: {m}',
  'meas.ok': 'תקין',
  'meas.fail': 'חריגה',
  'meas.record': 'רשום',
  'meas.laterAtFinish': 'יירשם בסיום העבודה',
  'meas.valueFor': 'הערך שנמדד — {label}',
  'meas.saveFailed': 'שמירת המדידה נכשלה',
  'hold.title': 'החלק ממתין לבדיקת הנדסה',
  'hold.requestedBy': 'ביקש: {name}',
  'hold.note': 'אפשר לעבוד עליו בתחנה שבה הבדיקה נדרשה. תחנה שאחריה לא תיפתח עד שמהנדס יאשר.',
  'notes.title': 'הערות על המק״ט',
  'notes.empty': 'אין הערות.',
  'notes.ackedBy': 'אישרו שקראו:',
  'notes.toNc': 'משהו לא תקין? פתח אי-התאמה',
  'notes.converted': 'הוסבה לאי-התאמה',
  'notes.mention': 'צרף אנשים להערה',
  'notes.send': 'שלח הערה',
  'notes.placeholder': 'הערה למי שיעבוד על זה אחריך',
  'notes.error': 'שגיאה',
  'meas.withTool': 'נמדד בכלי',
  'meas.pickTool': 'בחר כלי מדידה',
  'meas.noTool': 'אין כלי מדידה מכויל זמין — יש לפנות לאיכות.',
  'meas.notConfirmed': 'ממתין לאישור מהנדס — לא ניתן לרשום מדידה עדיין',
  'meas.onDrawing': 'המידה על השרטוט',
  'meas.seeOnDrawing': 'הצג על השרטוט',
  'meas.required': 'הערך הנדרש',
  'meas.measuredUnit': 'הערך שהתקבל · יחידה {n}',
  'meas.zoomIn': 'הגדל',
  'meas.zoomOut': 'הקטן',
  'meas.noPosition': 'מיקום הבלון על השרטוט אינו ידוע לחלק הזה',
  'st.startWork': 'התחל עבודה',
  'st.pullToMyDept': 'משוך למחלקה שלי',
  'st.reportNc': 'דיווח אי-התאמה',
  'st.workingNow': 'עובדים על החלק כעת',
  'st.you': ' (אתה)',
  'st.close': 'סגור',
  'st.model3d': 'מודל תלת מימד',
  'st.qty': 'כמות:',
  'st.rev': 'מהד׳:',
  'st.finishWork': 'סיום עבודה',
  'st.minimize': 'מזער',
  'st.pullTitle': 'משיכת {part} למחלקת {dept}',
  'st.ncReported': '✓ האי-התאמה דווחה — תופיע על לוח אי-ההתאמות',
  // F1 (אלי 30/10/2026): אחרי אי-התאמה — הצעד הבא של העובד, לא מקום הרישום.
  'st.ncStoppedTitle': 'האי-התאמה נרשמה — החלק לא יעבור לתחנה הבאה',
  'st.ncStoppedHint': 'התחנה הבאה לא תוכל להתחיל עליו עד שההנדסה תחליט. אם אין מה לעשות בו כאן — עבור לחלק אחר.',
  'st.ncStateHeld': 'אי-התאמה חוסמת',
  'st.ncLoggedTitle': 'האי-התאמה נרשמה — אפשר להמשיך לעבוד על החלק',
  'st.ncOtherPart': 'עצור שעון ועבור לחלק אחר',
  'st.ncBackToList': 'חזרה לרשימה',
  'st.deptOther': 'מחלקה אחרת',
  'st.deptOtherHide': 'הסתר מחלקות',
  'st.ncWaitEng': 'ממתין להנדסה',
  'st.ncWaitQa': 'ממתין לבדיקה',
  'st.ncOpenedBy': 'פתח: {name} · {time}',
  'sl.missing': 'חסר',
  'sl.owner': 'אצל',
  'sl.next': 'הצעד הבא',
  'st.closeSessionOf': 'סגירת הסשן של',
  'st.managerCode': 'קוד מנהל',
  'st.closeMinutes': 'כמה דקות לרשום לעובד',
  'st.closeMinutesHint': 'עברו {n} דקות מתחילת העבודה. אפשר לשנות — 0 לא ירשום כלום.',
  'st.closeMinutesHint_one': "עברה דקה אחת מתחילת העבודה. אפשר לשנות — 0 לא ירשום כלום.",
  'st.closeMinutesHint_many': "עברו {n} דקות מתחילת העבודה. אפשר לשנות — 0 לא ירשום כלום.",
  'st.closeSession': 'סגור סשן',
  'st.managerApproval': 'אישור מנהל',
  'st.managerUser': 'שם משתמש מנהל',
  'st.password': 'סיסמה',
  'st.approveAndPull': 'אשר ומשוך',
  // ── שתי אפשרויות למנהל, לא אישור יחיד ─────────────────────────────────
  // אלי, 17/09/2026: „מנהל יכול לבחור האם למשוך מקט מתוך מחלקה אחרת, אבל אם
  // הוא מושך, הוא חייב לציין כמה זמן הם עבדו עליו בדקות כי הם לא סגרו, לא
  // סיימו במחשב, או לא התחילו במחשב… או שהם עובדים במקביל."
  // ── תעדוף פרויקט במחלקה ─────────────────────────────────────────────────
  // אלי, 17/09/2026. „מתועדף" ולא „נעוץ": נעיצה היא על חלק בודד, וזו החלטה
  // רחבה יותר שמזיזה את סדר העבודה של מחלקה שלמה.
  'dp.pinProject': 'תעדף במחלקה',
  'dp.unpinProject': 'בטל תעדוף',
  'dp.pinnedIn': 'מתועדף ב{dept}',
  'dp.pinnedInNone': 'לא מתועדף באף מחלקה',
  'dp.pinProjectHint': 'כל חלק של הפרויקט שמגיע למחלקה הזאת — גם חלק שייווצר מחר — יעלה לראש התור שלה.',
  'dp.chooseDept': 'באיזו מחלקה?',
  'st.pullChoose': 'מה לעשות עם החלק?',
  'st.pullTake': 'למשוך למחלקה שלי',
  'st.pullTakeHint': 'החלק עובר אליך. יש לרשום כמה זמן עבדו עליו במחלקה הקודמת — אחרת השעות שלהם נעלמות.',
  'st.pullParallel': 'לעבוד במקביל',
  'st.pullParallelHint': 'שתי המחלקות עובדות על החלק יחד. שום שעון לא נסגר.',
  // „אני אבצע את השלב הקודם" (אלי 29/09/2026)
  'st.pullSelf': 'אני אבצע את השלב הקודם',
  'st.pullSelfHint': 'השלב הקודם לא בוצע — אתה מבצע אותו בעצמך, בלי קוד מנהל. הזמן נרשם על השלב הקודם, ובסיום לוחצים „עובר לשלב שלי".',
  'st.pullSelfWhich': 'איזה שלב תבצע?',
  'st.pullSelfNone': 'אין שלב קודם שאפשר לבצע בעצמך. לייזר, כיפוף ועיבוד שבבי נשארים במחלקה שלהם.',
  'st.pullSelfBusy': '{name} עובד על השלב הזה עכשיו ({min} דק׳). השעון שלו ייסגר — נדרש קוד מנהל וכמה דקות הוא באמת עבד.',
  'st.pullSelfStart': 'התחל: {stage}',
  'st.selfPriorBanner': 'אתה מבצע את {stage} (שלב קודם). כשתסיים — „עובר ל{own}".',
  'st.continueOwn': 'סיימתי {stage} — עובר ל{own}',
  'st.finishWithoutContinue': 'סיים בלי לעבור לשלב שלי',
  'st.continuedOwn': '✓ {stage}: דווחו {done}. השעון עבר ל{own}.',
  'st.continuedOwnBlocked': '✓ {stage}: דווחו {done}. {between} עוד לא הושלם — כדי להמשיך ל{own} צריך את המסלול הרגיל (אישור מנהל).',
  'st.forgotSwitch': 'שכחתי לעבור — סיימתי גם את {own}',
  'st.ownUnits': 'כמה יחידות סיימת ב{own}?',
  'st.priorMinutes': 'כמה דקות מתוך {total} היו {stage}?',
  'st.suggestFromStd': 'לפי התקן: {min} דק׳',
  'st.finishBoth': 'דווח את שני השלבים',
  'st.finishedBoth': '✓ {stage}: {prior} · {own}: {ownDone}',
  'st.continuedOwnDone': '✓ {stage}: דווחו {done}. {own} כבר הושלם — אין מה להמשיך.',
  'st.pullWorkedSoFar': 'נרשמו עד כה ב{dept}: {min} דק׳',
  'st.pullWorkedNone': 'ב{dept} לא נרשמו דקות כלל — כנראה לא דווח במחשב',
  'st.pullMinutesLabel': 'כמה דקות עבדו בפועל ב{dept}',
  'st.pullMinutesRequired': 'יש לרשום את הדקות לפני המשיכה',
  // ── דקות לכל מחלקה עד זו שמושכת ──────────────────────────────────────
  // אלי, 22/09/2026: „לרשום דקות עבודה לחלק בכל מחלקה עד המחלקה הנמשכת."
  'st.pullMinutesTitle': 'כמה דקות עבדו בפועל בכל מחלקה',
  'st.pullStageOpen': 'שעון פתוח · נרשמו {min} דק׳ — חובה לציין כמה באמת',
  'st.pullStageRecorded': 'נרשמו {min} דק׳ · אין שעון פתוח',
  'st.pullNoEarlierStages': 'אין מחלקות קודמות לדווח עליהן — זו התחנה הראשונה.',
  'st.afterFinish': 'לאחר סיום → {dept}',
  'st.lastStage': 'זהו השלב האחרון במסלול',
  'st.workTime': 'זמן עבודה:',
  'st.howManyDone': 'כמה חלקים סיימת?',
  'st.remainingHint': '(נותרו {n} · לא סיימת אף יחידה? דווח 0)',
  'st.unitsNotAccepted': '„{v}" אינו כמות — נותרו {n}. הקלד כמה סיימת (0 עד {n}). אם סרקת שרטוט — סגור את החלון ב-X.',
  'st.closeNoReport': 'סגור ללא דיווח',
  'st.zeroConfirm': '0 יחידות — החלק נשאר בשלב הזה ולא עובר הלאה. אם סיימת חלקים — הקלד כמה.',
  'st.zeroConfirmBtn': 'כן, סיימתי 0 — סגור',
  'st.enterQty': 'הקלד כמה סיימת',
  'st.arrivedFromPrev': 'הגיעו מ{stage}: {n}',
  'st.priorNotReported': '{stage} עוד לא דיווח — הדיווח שלך יישמר',
  'st.savedPriorToManager': ' · הדיווח נשמר. {stage} לא דיווח — מנהל הייצור יאשר את השלב',
  'st.doneHereCount': 'דווחו כאן: {n}',
  'st.overArrivedAll': 'כל מה שהגיע מ{stage} כבר דווח כאן. כדי לדווח יותר נדרש קוד מנהל.',
  'st.overArrived':'זה יותר ממה שהגיע מ{stage} — אפשר לדווח עד {n}. כדי לדווח יותר נדרש קוד מנהל.',
  'st.stageAlreadyFull': 'השלב הזה כבר דווח במלואו — אפשר רק לסגור ללא דיווח.',
  'st.scrapUnits': 'פסולים',
  // שם השדה שרושם את כמות הייצור. הוא היה חסר: לשדה לא היה `id`, `label`
  // ולא `aria-label` — כלומר השדה שקובע מה המפעל מאמין שייצר היה השדה
  // היחיד בטופס בלי שם, ומי שתלוי בקורא מסך לא ידע לאן הוא מקליד.
  'st.goodUnits': 'תקינים',
  // סיום יום עבודה — תקינים + פסולים ריקים, וזמן חסר (סקר עמדה #11, אלי 11/10)
  'st.closeFillAll': 'הקלד תקינים ופסולים בכל שעון — 0 אם אין.',
  'st.gapTitle': 'חסרות {hm} שעות — על מה עבדת?',
  'st.gapBody': 'היום בעמדה: {present} · נרשם על שעונים ועבודה תומכת: {recorded}',
  'st.gapLastPart': 'על {part} (פרויקט {project})',
  'st.gapCleaning': 'ניקיון וסידור',
  'st.gapWaiting': 'המתנה לעבודה/חומר',
  'st.gapOther': 'אחר',
  'st.gapOtherNote': 'על מה?',
  'st.gapSave': 'שמור והתנתק',
  'st.gapSkip': 'דלג',
  'st.gapSaved': 'נרשם למנהל. יום טוב!',
  'st.gapSaveFailed': 'לא נשמר — אפשר לדלג ולספר למנהל.',
  'st.targetIncludesScrap': 'כולל יחידות שנפסלו בשלב מאוחר יותר ויש להחליף',
  // עצירה שמישהו אחר עשה — מה קרה, וכמה הספקת עד אז.
  'st.stoppedTitle': 'העבודה נעצרה',
  'st.stoppedECO': 'נפתח שינוי הנדסי (ECO) על החלק — אין להמשיך לפי השרטוט הקודם',
  'st.stoppedDRAWING': 'השרטוט הוחלף — יש לעבוד לפי הגרסה החדשה',
  'st.stoppedROUTE': 'מנהל שינה את מסלול הייצור של החלק',
  'st.stoppedCANCELLED': 'הפרויקט בוטל — אין להמשיך לעבוד עליו',
  'st.stoppedNC': 'ההנדסה ענתה על אי-התאמה בחלק',
  'st.stoppedAsk': 'כמה יחידות הספקת עד העצירה?',
  'st.stoppedHint': 'הזמן שעבדת כבר נרשם. היחידות יירשמו על השלב הזה.',
  'st.stoppedSave': 'רשום',
  'st.stoppedNone': 'לא הספקתי כלום',
  'st.stoppedSaved': 'נרשמו {n} יחידות',
  'st.scrapHint': '{n} פסולים לא עוברים הלאה — תיפתח עליהם אי-התאמה.',
  'st.scrapHint_one': "פסול אחד לא עובר הלאה — תיפתח עליו אי-התאמה.",
  'st.scrapHint_many': "{n} פסולים לא עוברים הלאה — תיפתח עליהם אי-התאמה.",
  // ── סירובי העמדה (lib/stationError) — בעברית מוצגת הודעת השרת עצמה ──
  'err.projectCancelled': 'פרויקט {projectNumber} בוטל — אין להמשיך לעבוד עליו',
  'err.ecoOpen': 'השרטוט של החלק בשינוי — אין להתחיל: {text}',
  'err.faiPending': 'FAI — היחידה הראשונה ממתינה לבדיקת QA מלאה, אין להתחיל יחידה נוספת. קרא למנהל הייצור או למנהל ההנדסה.',
  'err.ncBlocking': 'אי-התאמה פתוחה עוצרת את החלק: {text}',
  'err.checklistIncomplete': 'יש להשלים לפני תחילת העבודה: {items}',
  'err.engInspectionPending': 'החלק ממתין לבדיקת הנדסה מתחנת {stageName} — אי אפשר להמשיך עד שמהנדס יאשר.',
  'err.stageComplete': '{stageName} כבר דווח במלואו ({done}/{target}) — דווח אחרון: {by}, {when}. אם הדיווח שגוי — מנהל הייצור מתקן אותו ב„היסטוריה" (יחידות 0), ואז אפשר להתחיל. שום דבר לא נרשם.',
  'err.oldVersion': 'השרטוט שנסרק אינו הגרסה העדכנית — יש להדפיס שרטוט מעודכן לפני העבודה',
  // מסך עצירה בסריקה (אלי 21/10/2026)
  'stop.title': 'עצור — לא לעבוד על החלק הזה',
  'stop.ok': 'הבנתי — לא מתחיל',
  'stop.who.nc': 'פנה למנהל הייצור או להנדסה. החלק ישתחרר כשההנדסה תענה על אי-ההתאמה.',
  'stop.who.hold': 'פנה למנהל הייצור — רק מי שעצר משחרר את העצירה.',
  'stop.who.eco': 'השרטוט עומד להשתנות — עבודה עכשיו תהיה גרוטאה. פנה להנדסה.',
  'stop.who.fai': 'היחידה הראשונה ממתינה לבדיקת QA. פנה ל-QA או למנהל ההנדסה.',
  'stop.who.inspection': 'החלק ממתין לאישור מהנדס. פנה למהנדס הפרויקט.',
  'stop.who.drawingWait': 'מנהל קבע לחכות לשרטוט. פנה למנהל הייצור.',
  // עבודה בלי שרטוט (אלי 21/10/2026)
  'nodraw.title': 'אין שרטוט לחלק הזה',
  'nodraw.body': 'אפשר להמשיך לעבוד בלי שרטוט — השעון ירוץ כרגיל, והעבודה תסומן „עבד בלי שרטוט". ההנדסה תקבל הודעה.',
  'nodraw.continue': 'המשך בלי שרטוט',
  'nodraw.cancel': 'לא עכשיו',
  'nodraw.managerWait': 'מנהל: עצור עד שיעלה שרטוט',
  'nodraw.flag': 'בלי שרטוט',
  'err.productionHold': 'הייצור על החלק הזה עצור — {text}',
  'err.revisionNotChanged': 'העבודה כבר על הרוויזיה העדכנית',
  // ── „עלתה רוויזיה — רענן" (אלי, 24/09/2026: „השעון רץ; האישור מפצל") ──
  'rev.title': 'עלתה רוויזיה {rev}',
  'rev.body': 'השרטוט עודכן מ-{from} ל-{to}. אין להמשיך לפי {from}.',
  'rev.clockRuns': 'השעון ממשיך לרוץ — הזמן עד האישור נרשם על {from}.',
  'rev.ask': 'כמה סיימת לפי {from}?',
  'rev.zeroOk': '0 היא תשובה תקינה.',
  'rev.scrap': 'פסולים (לא חובה)',
  'rev.confirm': 'רענן — עבור ל-{to}',
  'rev.unitsBad': 'יש לרשום מספר שלם, 0 או יותר',
  'rev.switched': 'עברת לרוויזיה {to} — השעון החדש רץ',
  'rev.notStartedComplete': 'השלב הושלם — לא נפתח שעון חדש',
  'rev.notStartedHold': 'הייצור על החלק עצור — {text}. לא נפתח שעון חדש.',
  'rev.refreshTo': 'רענן ל-{to}',
  'rev.titleFirst': 'עלה שרטוט — רוויזיה {rev}',
  'rev.bodyFirst': 'העבודה התחילה בלי שרטוט, ועכשיו עלה שרטוט ברוויזיה {to}. ממשיכים לפיו.',
  'rev.clockRunsFirst': 'השעון ממשיך לרוץ — הזמן עד האישור נרשם על העבודה בלי שרטוט.',
  'rev.askFirst': 'כמה סיימת בלי שרטוט?',
  'rev.mineBanner': 'עלתה רוויזיה {to} — פתח את החלק ורענן',
  'err.approvalPending': 'הבקשה נשלחה — ממתינה לאישור מנהל. סרוק שוב לאחר שהמנהל יאשר.',
  'err.approvalAssembly': 'חלקים בהרכבה זו עדיין לא הושלמו — נדרש אישור מנהל',
  'err.approvalSkip': 'שלב קודם לא הושלם — נדרש אישור מנהל',
  // ⚠ 27/09/2026: הרכבה עם חלקים חסרים אינה דורשת עוד אישור להתחלה, והסירוב
  // עבר ל**סיום** השלב. `err.approvalAssembly` נשאר מפני שבקשות pending שכבר
  // בייצור יחזירו את הקוד הישן עד שיפוגו.
  'err.assemblyChildrenMissing': 'לא ניתן לסיים את השלב — חסרים חלקים: {missingLabel}. אפשר לדווח פחות יחידות או לסגור ללא דיווח; לסיום צריך שכל החלקים יגיעו.',
  'err.notSessionOwner': 'לא ניתן לשנות עבודה של עובד אחר',
  'err.partNotFound': 'חלק לא נמצא',
  'err.partCutNoDrawingParent': 'החלק {partNumber} נחתך בלייזר ב-{cutAt}, אבל השרטוט שלו עוד לא הועלה — ההנדסה כבר קיבלה בקשה. בינתיים אפשר לעבוד על ההרכבה {parent}, או לפנות למנהל הייצור.',
  'err.partCutNoDrawing': 'החלק {partNumber} נחתך בלייזר ב-{cutAt}, אבל השרטוט שלו עוד לא הועלה — ההנדסה כבר קיבלה בקשה. בינתיים פנה למנהל הייצור.',
  'err.deptRequired': 'חסרה מחלקה לעובד',
  'err.deptMismatch': 'המחלקה שנבחרה אינה בתהליך של חלק זה',
  'err.employeeInactive': 'עובד אינו פעיל',
  'err.sessionClosedByManager': 'העבודה נסגרה זה עתה על ידי מנהל',
  'err.machineRequired': 'יש לבחור על איזו מכונה העבודה מתבצעת',
  'err.machineNotActive': '{machineName} מושבתת — לא ניתן לדווח עליה עבודה. יש לבחור מכונה אחרת.',
  'err.machineWrongDept': '{machineName} אינה מכונה של מחלקת {departmentName} — יש לבחור מכונה מהרשימה.',
  'err.maintenanceDue': 'לא ניתן להתחיל עבודה — {items}. יש לבצע את האחזקה ולאשר אותה במסך המכונות.',
  // ── סירובי שמונת הקיוסקים האחרים ──────────────────────────────────────
  // בעברית מוצגת הודעת השרת עצמה; אלה קיימים בשביל הצד הרוסי.
  'err.coworkersDisabled': 'עבודה משותפת אינה מאופשרת',
  'err.coworkerInvalid': 'לא ניתן להוסיף את העובד עצמו כשותף',
  'err.tooManyCoworkers': 'ניתן להוסיף עד {max} שותפים',
  'err.noDepartment': 'אין לך מחלקה משויכת — פנה למשרד',
  'err.drawingNotYours': 'החלק נמצא כעת ב{departmentName} — כדי לפתוח את השרטוט יש למשוך אותו למחלקה שלך באישור מנהל',
  'err.drawingForbidden': 'יש לסרוק תג לפני פתיחת שרטוט',
  'err.badgeTokenInvalid': 'הזיהוי פג תוקף — יש לסרוק תג או לבחור מהרשימה שוב',
  'err.cardNotEnrolled': 'הכרטיס אינו משויך לעובד. יש להנפיק אותו במסך כרטיסי עובד, או להזדהות בינתיים מהרשימה.',
  'err.employeeNotFound': 'עובד לא נמצא',
  'err.sessionNotYours': 'הסשן אינו שלך',
  'err.taskNotYours': 'המשימה אינה שלך',
  'err.alreadyReported': 'כבר דיווחת מה הספקת בעצירה הזאת',
  'err.sessionAlreadyOpen': 'סשן פתוח כבר קיים',
  'err.invalidPayload': 'ברקוד לא חוקי',
  'err.laserExcelOnly': 'מחלקת לייזר מדווחת דרך העלאת קובץ אקסל בעמדת הלייזר, לא בסריקה',
  'err.laserNotConfigured': 'מחלקת לייזר אינה מוגדרת בהגדרות המערכת',
  'err.invalidFile': 'הקובץ ריק או פגום',
  'err.noPhoto': 'לא צולמה תמונה',
  'err.noParts': 'לא נמצאו חלקים בקובץ — ודא שזה דוח הנפרש של מכונת הלייזר',
  'err.noReportableParts': 'אין חלקים לדיווח בקובץ — כל החלקים דולגו',
  'err.itemNotDeletable': 'לא ניתן למחוק סעיף מטופס בדיקת האיכות. סעיף שאינו חל על הפרויקט מסומן ״לא רלוונטי״ עם הסבר — כך הוא נשאר בדוח ורואים מי החליט ולמה.',
  'err.badVehicleCode': 'התג לא זוהה, או שהמדבקה ברכב הוחלפה. סרקו שוב את התג, ואם זה חוזר — סרקו מחדש את המדבקה שברכב.',
  'err.tripIncomplete': 'לא ניתן לסגור את הנסיעה. מלאו מטרה וקילומטר בשני הקצוות ולחצו שוב.',
  'err.odometerBackwards': 'הקילומטר בחזרה ({back}) נמוך מהקילומטר ביציאה ({out}). בדקו את המונה ברכב והקלידו שוב.',
  'err.odometerLow': 'הקלדתם {typed}, והקילומטר האחרון שנרשם לרכב הוא {last}. בדקו את המונה ברכב. אם זה מה שרשום בו, לחצו „זה מה שרשום ברכב".',
  'err.vehicleFormInvalid': 'אחד השדות לא תקין. בדקו שהקילומטר הוא מספר שלם, בלי נקודה, ונסו שוב.',
  'err.tripNotFound': 'הנסיעה לא נמצאה ברכב הזה. סרקו שוב את המדבקה ובחרו מהרשימה.',
  'err.tripOtherVehicle': 'זו נסיעה של רכב אחר. סרקו את המדבקה של הרכב שבו נסעתם.',
  'err.tripOtherDriver': 'זו נסיעה של נהג אחר. כדי לדווח עליה בחרו „דווח חזרה במקומו".',
  'err.tripProjectRequired': 'נסיעה מסוג ״{purpose}״ חייבת לציין פרויקט. בחרו פרויקט ושמרו שוב.',
  'err.refuelIncomplete': 'סימנתם שהרכב תודלק — כתבו כמה ליטרים וכמה שולם, ושמרו שוב.',
  'err.checkFutureDay': 'לא ניתן לדווח בדיקה על יום שטרם הגיע. רעננו את המסך ונסו שוב.',
  'err.checkNoteRequired': 'בדיקה שנכשלה חייבת הסבר — {item}. כתבו מה נמצא ושמרו שוב.',
  'err.monthAlreadyReported': 'המונה של החודש כבר נרשם. תיקון נעשה במשרד — פנו למנהלת המשרד.',
  'err.monthReadingLow': 'הקילומטר נמוך מתחילת החודש ({start}). בדקו את המונה ברכב והקלידו שוב.',
  'err.notMonthlyVehicle': 'הרכב הזה מדווח נסיעה-נסיעה, לא פעם בחודש. סרקו שוב את המדבקה.',
  'err.tripClosed': 'הנסיעה כבר נסגרה. תיקון נעשה מהמשרד, עם סיבה שנשמרת על הנסיעה.',
  'err.monthClosed': 'החודש נסגר — שווי השימוש כבר מדווח. יש לפתוח אותו מחדש לפני עדכון.',
  'err.monthIncomplete': 'לא ניתן לסגור חודש בלי קילומטר בתחילתו ובסופו',
  'err.mustSignIn': 'יש להתחבר',
  // ── עברית קשיחה שנשארה על מסכי הקיוסק עצמם ────────────────────────────
  //
  // הסירובים נסגרו; אלה הן המחרוזות שעובד רוסי קורא **כשהכול עובד**.
  // נמדדו 65 שורות בחמישה קיוסקים, ולרובן כבר היה מפתח שלא חובר.
  // כאן רק מה שלא היה.
  'saw.continueThisBar': 'המשך את המוט הזה',
  'saw.overRangeAngle': 'זווית {deg}° מהניצב — מעבר למהלך ראש המסור ({max}°).',
  'saw.overRangeComplement': 'המשלים הוא {comp}°, אבל לחייג אותו ייתן חלק אחר — גלגול הפרופיל במלחציים לא משנה את זווית החיתוך לציר.',
  'saw.leftoverAsk': 'כמה נשאר מהמוט? ({unit})',
  'saw.expectedHint': 'החישוב ציפה ל־{n} {unit}. מדוד בפועל — המדידה שלך גוברת.',
  'saw.willScrap': 'קצר מהמינימום שהוגדר לפרופיל ({len}) — יירשם כגרוטאה ולא ייכנס לתכנון.',
  'saw.barsCount': '{bars} מוטות · {pieces} חתיכות',
  'rem.belowMinimum': '{len} מתחת לאורך המינימלי שהוגדר לפרופיל הזה. נרשם, אבל לא ייכנס לתכנון.',
  'rem.belowMinProfile': 'קצר מהאורך המינימלי ({len}) — יירשם כגרוטאה ולא ייכנס לתכנון.',
  'rem.saveAndGetSku': 'שמור וקבל מק״ט',
  'rem.endAngleShort': 'קצה {deg}°',
  'rem.fromProject': 'מפרויקט {n}',
  'wh.waitingPick': 'ממתין ללקט',
  'wh.waitingPickWarehouse': 'ממתין ללקט במחסן',
  'wh.staleStock': 'מספרי המלאי הם מקובץ שהועלה לפני {n} ימים — לא בזמן אמת.',
  'wh.bindQuestion': 'לאיזה מק״ט הוא שייך? נקשור אותו פעם אחת — ומכאן והלאה סריקה של הקופסה הזאת תזהה את הפריט לבד.',
  'wh.noSkuMatch': 'לא נמצא מק״ט מתאים. אפשר לסגור ולהמשיך בשם חופשי — קישור אפשר לעשות אחר כך.',
  'veh.vehicleSubtitle': 'רכב {plate}',
  'veh.checkSavedFailures': 'הבדיקה נשמרה. {n} ליקויים דווחו למנהל.',
  'veh.failList': '{items} — אין לצאת ברכב הזה. פנה למנהל.',
  'st.openWorks': 'יש לך {n} עבודות פתוחות',
  'st.openWorkOne': 'יש לך עבודה פתוחה',
  // עבודה במקביל — עד 4 שרטוטים (אלי 30/09)
  'err.parallelLimit': 'יש לך כבר 8 שרטוטים פתוחים ({parts}). סיים או סגור אחד מהם — ואז התחל את זה.',
  'st.drawingOf': 'שרטוט {i} מתוך {n}',
  'st.weldIntoParent': 'ריתוך אל',
  'st.childOpenStage': 'שלב פתוח: {stages}',
  'st.prevDrawing': 'השרטוט הקודם',
  'st.nextDrawing': 'השרטוט הבא',
  'st.pageOf': 'עמוד {i} מתוך {n}',
  'st.workDept': 'נרשם על מחלקה:',
  'st.whatDidYouDo': 'מה ביצעת בחלק?',
  'st.skippedPending': 'דולג — ממתין לדיווח 07:00',
  'st.resumeWork': 'חזור לעבודה ›',
  'st.myWorkOne': 'העבודה הפתוחה שלך',
  'st.myWorkMany': 'העבודה הפתוחה שלך — {n} שרטוטים',
  'st.clickToEnter': 'לחץ כדי להיכנס ›',
  'st.pickCard':'סמן לפתיחה יחד',
  'st.openPicked': 'פתח את המסומנים ({n})',
  'st.clearPicked': 'נקה סימון',
  'st.prevPage': 'העמוד הקודם',
  'st.nextPage': 'העמוד הבא',
  'st.needQualification': 'יש לפנות למשרד להסדרת ההסמכה.',
  'st.priorEmpty': 'ב{stage} עוד לא דווחו יחידות על החלק הזה. אפשר לעבוד ולדווח כרגיל — הדיווח שלך יישמר, ומנהל הייצור יאשר את השלב {stage}.',
  'st.idleNudge': 'השעון ממשיך לרוץ — {n} דק׳ בלי מגע במסך. עדיין עובד על זה?',
  'st.priorClockTitle': 'סיים את הקודם',
  'st.priorClockFinish': 'סיים את הקודם ({n} יח׳)',
  'st.priorClockSkip': 'דלג',
  'st.finishAll': 'סיים הכל',
  'st.finishAllTitle': 'לסיים את כל השעונים הפתוחים',
  'st.finishAllBody': 'כמה סיימת בכל אחד? 0 תקין. אחרי הסגירה תישאר מחובר ותוכל לבחור עבודה חדשה.',
  'st.finishAllConfirm': 'סיים הכל',
  'st.finishAllDone': 'כל השעונים נסגרו',
  'st.finishAllNone': 'אין לך שעונים פתוחים',
  'st.idleTitle': 'טוק טוק — אתה עדיין כאן?',
  'st.idleBody': 'השעון רץ {n} דקות בלי שנגעת במסך. שרטוט פתוח נספר כזמן עבודה, אבל הוא אינו תשובה לשאלה הזו.',
  'st.idleWarning': 'בלי אישור הסשן ייסגר וידווח 0 יחידות. השעות עד עכשיו יירשמו, ומנהל הייצור יידרש לאשר את הכמות ואת השעות.',
  'st.idleYes': 'כן, אני כאן',
  'saw.cutLabel': 'נחתך:',
  'saw.cutPieceLength': '{n} מ״מ',
  'rem.realLengthPrompt': 'אורך אמיתי של {sku} במ״מ:',
  'st.switchOpenTitle': 'יש לך עבודה פתוחה',
  'st.switchOpenBody': 'לפני שמחליפים עובד צריך לסגור אותה — אחרת השעון ממשיך לרוץ על השם שלך, והשעות ייפלו על העבודה של הבא אחריך. 0 = סגור בלי דיווח.',
  'st.switchClosing': 'סוגר…',
  'st.switchCloseAndSwitch': 'סיים עבודה והחלף עובד',
  'st.switchOpenSince': 'פתוח מ־{time} · הושלמו עד כה {done} מתוך {qty}',
  'st.switchHowManyNow': 'כמה סיימת עכשיו?',
  // ── בחירת מכונה ואחזקה בעמדה (MachineGateDialog) ──
  'mg.loadFailed': 'לא ניתן לטעון את מצב המכונות',
  'mg.blockedTitle': 'לא ניתן להתחיל — אחזקה שלא בוצעה במחלקה',
  'mg.whichMachine': 'על איזו מכונה?',
  'mg.requiredBeforeStart': 'נדרש לפני תחילת עבודה.',
  'mg.maintenanceMissing': 'אחזקה חסרה',
  'mg.dialogLabel': 'בדיקות לפני הפעלת מכונה',
  'mg.close': 'סגור',
  'mg.maintenance': 'אחזקה {freq}',
  'mg.ok': 'תקין',
  'mg.notOk': 'לא תקין',
  'mg.whatIsWrong': 'מה לא תקין?',
  'mg.saving': 'שומר…',
  'mg.remaining': 'נותרו {n} סעיפים',
  'mg.writeWhatWrong': 'יש לכתוב מה לא תקין',
  'mg.confirmDone': 'אישור ביצוע',
  'st.reportAndFinish': 'דווח וסיים',
  'st.saved': 'נרשם ✓',
  'st.todayReport': 'דיווח היום',
  'st.production': 'ייצור',
  'st.support': 'תומכת',
  'st.missing': 'חסר',
  'st.filledGap': 'מילאנו {time} — בחר קטגוריה ולחץ «רשום» כדי לסגור את היום.',
  'st.time': 'זמן:',
  'st.minutesShort': 'דק׳',
  'st.projectOptional': 'מס׳ פרויקט (רשות)',
  'st.incoming': 'בדרך אליי ({n})',
  'st.incomingHint': '— לחץ על חלק לצפייה/משיכה',
  'st.incomingMore': 'הצג עוד {n}',
  'st.incomingLess': 'הצג פחות',
  // אותה עברית שגויה שנתפסה בכותרות הקיבוץ — „1 חלקים" — קיימת גם כאן,
  // בפאנל „בדרך אליי". אין סיבה שהמסך יאמר נכון בחצי אחד ושגוי בשני.
  'st.nowInDeptOne': 'כעת במחלקת {dept} · חלק אחד',
  'st.nowInDept': 'כעת במחלקת {dept} · {n} חלקים',
  // ── קיבוץ התור לפי פרויקט, עם זמן האספקה ──────────────────────────────
  // אלי, 17/09/2026: „בקיבוץ צריך להיות זמן אספקה של הפרויקט, כמה ימים נותר,
  // או כמה ימים עבר." שלושה נוסחים ולא אחד: „נותרו 0 ימים" (היום), „באיחור"
  // ו„ללא תאריך" הן שלוש עובדות שונות — והיעדר תאריך אינו אפס ימים.
  // „מתוכנן להיום" / „לא בוצע אתמול" — נבחר למחלקה בלוח הקיבולת (אלי 29/09/2026).
  'st.dayPlanToday': 'מתוכנן להיום',
  'st.dayPlanCarried': 'לא בוצע אתמול',
  'st.pinnedFirst': 'נעוץ בידי מנהל עבודה — קודם לכול',
  // „1 חלקים" נראה בצילום המסך של הקיבוץ (17/09/2026). עברית שגויה על מסך
  // שעובד קורא בכל בוקר — ולכן צורת יחיד משלה, ולא ניסוח שמתחמק („חלקים: 1").
  'st.projectGroupOne': 'פרויקט {n} · חלק אחד',
  'st.projectGroup': 'פרויקט {n} · {parts} חלקים',
  // קיפול התור לפי המספור (U-1003-03, אלי 10/10)
  'st.tree.byProject': 'לפי פרויקט',
  'st.tree.openCount': '{n} פתוחים',
  'st.tree.allClosed': 'הכל מקופל',
  'st.tree.foldAll': 'קפל הכל',
  'st.tree.project': 'פרויקט',
  'st.tree.assembly': 'הרכבה',
  'st.tree.queueOne': 'חלק אחד אצלך',
  'st.tree.queueMany': '{n} חלקים אצלך',
  'st.tree.blocked': '{n} עצורים',
  'st.tree.progress': '{done}/{total} גמורים',
  'st.tree.progressTitle': '{done} מתוך {total} החלקים מתחת גמורים — בכל המחלקות',
  'st.tree.otherAt': 'ב{dept}',
  'st.tree.otherDone': 'גמור',
  'st.tree.otherNoRoute': 'בלי מסלול',
  'st.tree.otherOpen': 'פתח {n} — חלק ממחלקה אחרת; עבודה עליו במשיכה באישור מנהל',
  'st.tree.notHere': 'לא בעמדה שלך — מקבץ את החלקים מתחת',
  'st.tree.openWork': 'פתח עבודה',
  'st.tree.fold': 'קפל {n}',
  'st.tree.unfold': 'פתח {n}',
  // אלי, 17/09/2026: „לא — נשאר 5 ימים. אלא **אספקת הפרויקט בעוד**. אחרת הוא
  // יחשוב שהחלקים צריכים להיות מסופקים בעוד…" — המשפט יושב מעל כרטיסי חלקים,
  // ו„נותרו 5 ימים" נקרא כאילו החלקים האלה לאספקה. המשפט אומר עכשיו על מה הוא.
  'st.deliveryIn': 'אספקת הפרויקט בעוד {d} ימים · {date}',
  'st.deliveryToday': 'אספקת הפרויקט היום · {date}',
  'st.deliveryLate': 'אספקת הפרויקט באיחור {d} ימים · {date}',
  'st.deliveryLateOne': 'אספקת הפרויקט באיחור יום אחד · {date}',
  'st.deliveryNone': 'לפרויקט אין תאריך אספקה',
  'st.noDrawing': 'אין שרטוט',
  'st.nextStage': 'השלב הבא!',
  'st.stagesToGo': 'עוד {n} שלבים',
  'st.now': 'כעת:',
  'st.arrivesAfter': 'מגיע אליך אחרי «{stage}»',
  'st.arrivesNext': 'מגיע אליך בשלב הבא',
  'st.hoursShort': 'ש׳',
  'st.minsShort': 'ד׳',
  'st.openSessionExists': 'כבר יש סשן פתוח',
  'st.part': 'חלק:',
  'st.stage': 'שלב:',
  'st.heldBy': 'אצל:',
  'st.openFor': 'פתוח כבר {time}',
  'st.continueSession': 'המשך את הסשן',
  'st.closeSessionApproval': 'סגור את הסשן (אישור מנהל)',
  'st.howManyArrived': 'כמה חלקים הגיעו לתחנה שלך?',
  'st.arrivedHint': '(סיים {stage} · עד {max})',
  'st.managerCodeLabel': 'קוד מנהל לאישור',
  'st.approveClose': 'אשר סגירה',
  'st.ncTitle': 'דיווח אי-התאמה',
  'st.ncPart': 'חלק',
  'st.noCategories': 'אין קטגוריות מוגדרות',
  'st.whichStation': 'איזו תחנה? (לא חובה)',
  'st.descOptional': 'תיאור (לא חובה)',
  'st.markupAttached': 'הסימון צורף — לחץ לעריכה',
  'st.markOnDrawing': 'סמן על השרטוט (רשות)',
  'st.sendReport': 'שלח דיווח',

  // ── language switcher ──
  'lang.switch': 'שפה',
  'lang.he': 'עברית',
  'lang.ru': 'Русский',
  'lang.saved': 'השפה נשמרה',
  'st.add': "הוסף",
  'st.minShort': "דק׳",
  'st.hourShort': "ש׳",
  'st.minShort2': "ד׳",
  'st.closeSessionTitle': "סגירת סשן — אישור מנהל",
  'st.closeSessionTail': "יירשם בהיסטוריה (0 יחידות).",
  'st.leaveDrawing': "יציאה מהשרטוט",
  'st.afterFinishTo': "לאחר סיום → העבר למחלקת",
  'st.supportWork': "תומכת",
  'st.covered': "מכוסה ✓",
  'st.completeDay': "השלם את היום",
  'st.reportSupport': "+ דווח עבודה תומכת",
  'st.filledHint': "מילאנו {v} — בחר קטגוריה ולחץ «רשום» כדי לסגור את היום.",
  'st.record': "רשום",
  'st.sessionOpenTitle': "כבר יש סשן פתוח",
  'st.partLabel': "חלק:",
  'st.currentPart': "(החלק הנוכחי)",
  'st.stageLabel': "שלב:",
  'st.atLabel': "אצל:",
  'st.closeSessionMgr': "סגור את הסשן (אישור מנהל)",
  'st.cancel2': "ביטול",
  'st.finishedUpTo': "(סיים {stage} · עד {max})",
  'st.back': "חזרה",
  'st.confirmClose': "אשר סגירה",
  'st.ncReport': "דיווח אי-התאמה",
  'st.handled': "טופל",
  // ── identify without a badge (shared by every kiosk) ──
  'id.cantScan': "לא מצליח לסרוק? בחר מהרשימה",
  'id.pickYourself': "בחר את עצמך מהרשימה",
  'id.noMatch': "לא נמצא עובד מתאים",
  // ── דיווח תקלה במערכת, משותף לכל הקיוסקים ──
  // נמדד 18/09: 23 דיווחים הוגשו, אפס מעובד ייצור. המסלול היה סגור.
  'rep.button': "דווח תקלה במערכת",
  'rep.title': "משהו לא עובד?",
  'rep.badgeFirst': "סרוק תג כדי לדווח",
  'rep.what': "מה קרה?",
  // מרכאות בודדות בחוץ, בכוונה: הטקסט מכיל מרכאה, ומחרוזת עטופה ב-" נסגרה
  // כאן באמצע המשפט. tsc אמר `Unterminated string literal` ו-esbuild הפיל
  // את שומר ה-i18n לפני שהגיע לבדוק תוכן בכלל.
  'rep.placeholder': 'לחצתי על „סיימתי" ולא קרה כלום…',
  'rep.context': "נשלח עם שם המסך וגרסת המערכת, כדי שאפשר יהיה לשחזר בלי לשאול אותך.",
  'rep.send': "שלח",
  'rep.sent': "הדיווח נשלח. תודה — זה עוזר.",
  'rep.failed': "הדיווח לא נשלח. נסה שוב.",
  'ks.scanBadgeFirst': "סרוק תג עובד תחילה",
  'ks.deptMissing': "חסרה מחלקה לעובד",
  'ks.deptMismatch': "המחלקה שלך לא בתהליך החלק",
  'ks.badManagerPass': "סיסמת מנהל שגויה",
  'ks.stIdle': "סרוק תג עובד",
  'ks.stBadgeOk': "מוכן לסריקת שרטוט",
  'ks.stAwaitingUnits': "סשן פתוח — כמה יחידות?",
  'ks.stAwaitingApproval': "ממתין לאישור מנהל",
  'ks.stResolvingPrior': "סגור את הסשן הקודם תחילה",
  'ks.stResult': "✓ נשמר",
  'ks.stStarted': "✓ העבודה התחילה",
  'ks.subtitle': "מערכת דיווח עובד",
  'ks.configWarn': "⚠ הגדרות הקיוסק לא נטענו — פועל בברירות מחדל ({s}s)",
  // ── קיוסק גמר (אלי, 17/09/2026) ──
  // ── דוח בדיקה — השמיני והאחרון משמונת המסכים שאלי אישר ──
  'ir.intro': "כל המידות שנמדדו על מק״ט — הנדרש, מה שנמדד בפועל, והחריגות — בדוח אחד שאפשר להדפיס ולצרף למשלוח. חפש מספר פרויקט או מק״ט כדי להתחיל.",
  'ir.searchLabel': "חפש מספר פרויקט או מק״ט",
  'ir.typeTwoChars': "הקלד לפחות שני תווים. מק״טים של פרויקט מתחילים במספר הפרויקט, ולכן חיפוש מספר הפרויקט מציג את כולם.",
  'ir.noParts': "לא נמצאו מק״טים.",
  'ir.emptyTitle': "דוח בדיקה למק״ט",
  'ir.emptyLead': "הקלד מספר פרויקט או מק״ט למעלה — לפחות שני תווים — ובחר מהרשימה.",
  'ir.emptyB1': "· המידות שנקראו מהשרטוט, עם הטווח שאושר לכל אחת.",
  'ir.emptyB2': "· מה נמדד בפועל, מי מדד ומתי, ופסק הדין מול הטווח.",
  'ir.emptyB3': "· מה עדיין לא נמדד — זו השורה שבגללה פותחים את המסך.",
  'ir.emptyNote': "מק״ט בלי מידות מסומנות בשרטוט לא יציג שורות. ההסבר על הסימון נמצא ב„איך עובדת האיכות“.",
  'ir.projectPrefix': "· פרויקט {n}",
  'ir.revision': " · מהדורה {n}",
  'ir.quantity': " · כמות {n}",
  'ir.manualChar': "מאפיין ידני",
  'ir.printTitle': "דוח בדיקת מידות",
  'ir.printPart': "מק״ט",
  'ir.printVerdict': "תוצאה",
  'ir.printReadings': "מדידות",
  'ir.printFailed': "חריגות",
  'ir.print': "הדפס",
  'ir.changePart': "החלף מק״ט",
  'ir.noCharacteristics': "אין מאפייני בדיקה למק״ט זה. אפשר לקרוא אותם מהמידות המסומנות בצהוב בשרטוט, או להוסיף ידנית.",
  'ir.conforms': "תואם",
  'ir.notConforms': "לא תואם",
  'ir.inProgress': "בתהליך",
  'ir.summaryCounts': "{chars} מאפיינים · {readings} מדידות",
  'ir.summaryFailed': " · {n} חריגות",
  'ir.summaryUnconfirmed': " · {n} ממתינים לאישור מהנדס",
  'ir.noReadingsYet': "— טרם נרשמו מדידות",
  'ir.balloon': "בלון",
  'ir.colCharacteristic': "מאפיין",
  'ir.colRequirement': "דרישה",
  'ir.colMethod': "אמצעי",
  'ir.colUnitN': "יח׳ {n}",
  'ir.awaitingApproval': "ממתין לאישור",
  'ir.toolTitle': "כלי {code}",
  'ir.sigInspected': "בדק",
  'ir.sigApproved': "אישר",
  'ir.sigDate': "תאריך",
  'ir.typeDimensional': "מידה",
  'ir.typeSurface': "טיב פני שטח",
  'ir.typePressure': "לחץ",
  'ir.typeVisual': "ויזואלי",
  'ir.typeMaterial': "חומר",
  'ir.noTraceability': "לא הוגדרה עקיבות למק״ט הזה — הזיהוי הוא המק״ט והרוויזיה בלבד. להוספת מספר סידורי או מספר אצווה: עץ המוצר ← בחירת המק״ט ← ״זיהוי ועקיבות״.",
  'ir.resultAccepted': "התקבל",
  'ir.resultAcceptedDev': "התקבל בסטייה",
  'ir.resultRejected': "נפסל",
  'ir.identityMaterial': "זיהוי וחומר גלם",
  'ir.modeSerial': "מספר סידורי לכל יחידה",
  'ir.modeBatch': "מספר אצווה לריצה",
  'ir.modeNone': "ללא עקיבות",
  'ir.missingIdentity': "חסר זיהוי ל-{n} יחידות",
  'ir.missingIdentity_one': "חסר זיהוי ליחידה אחת",
  'ir.missingIdentity_many': "חסר זיהוי ל-{n} יחידות",
  'ir.hSerial': "מספר סידורי",
  'ir.hBatch': "מספר אצווה",
  'ir.hMaterial': "חומר גלם",
  'ir.hSupplier': "ספק",
  'ir.hDeliveryNote': "ת. משלוח",
  'ir.hIncomingCheck': "בדיקת קבלה",
  'ir.materialFootnote': "חומר הגלם נלקח מקבלת הסחורה שנקשרה ליחידה במסך ״זיהוי ועקיבות״ של המק״ט. שורה ריקה = לא נקשר חומר, לא ״אין חומר״.",
  'ir.instrumentsUsed': "כלי המדידה בדוח זה",
  'ir.dialogLabel': "רישום מדידה",
  'ir.balloonN': "בלון {n}",
  'ir.unitN': "יחידה {n}",
  'ir.requirement': "דרישה",
  'ir.pass': "עובר",
  'ir.fail': "נכשל",
  'ir.phMeasured': "הערך שנמדד",
  'ir.measuringTool': "כלי המדידה",
  'ir.notSpecified': "— לא צוין —",
  'ir.noUsableTools': "אין כלי מדידה פעילים ומכוילים במרשם. כלי שעבר את מועד הכיול לא יתקבל.",
  'ir.phNote': "הערה (לא חובה)",
  'ir.saveMeasurement': "שמור מדידה",
  'ir.measureFootnote': "התוצאה נקבעת מול הדרישה ונשמרת. תיקון של הדרישה בהמשך לא ישנה מדידה שכבר נרשמה.",
  'ir.saveError': "שגיאה בשמירת המדידה",
  'ir.close': "סגור",
  'ir.unconfirmedTitle': "נקראו מהשרטוט וממתינים לאישור ({n})",
  'ir.cannotMeasureUnconfirmed': "לא ניתן למדוד לפי מאפיין שלא אושר",
  'ir.identifiedAs': "זוהה כ:",
  'ir.noToleranceFound': " — לא זוהה טולרנס, יש להשלים ידנית",
  'ir.checkAndConfirm': "בדוק ואשר",
  'ir.delete': "מחק",
  'ir.specType': "סוג מפרט",
  'ir.specNominalTol': "נומינלי + טולרנס",
  'ir.specMinMax': "מינימום / מקסימום",
  'ir.specPassFail': "עובר / נכשל",
  'ir.nominal': "נומינלי",
  'ir.tolMinus': "טולרנס מטה",
  'ir.tolPlus': "טולרנס מעלה",
  'ir.min': "מינימום",
  'ir.max': "מקסימום",
  'ir.unit': "יחידה",
  'ir.method': "אמצעי מדידה",
  'ir.phMethod': "קליבר, CMM, מד חספוס…",
  'ir.phMethodShort': "קליבר…",
  'ir.confirmChar': "אשר את המאפיין",
  'ir.description': "תיאור",
  'ir.type': "סוג",
  'ir.spec': "מפרט",
  'ir.error': "שגיאה",
  'ir.save': "שמור",
  'ir.cancel': "ביטול",
  // ── מרשמי איכות (כלי מדידה / הסמכות) — השביעי משמונת המסכים ──
  //
  // התחילית נבדקה כפנויה לפני הכתיבה. `nc.*` לימדה שהתנגשות תחילית עולה
  // 36 החלפות בשלושה קבצים.
  'qr.calibrationOverdue': "כלי מדידה עברו את מועד הכיול — אין להשתמש בהם עד שיכוילו.",
  'qr.qualificationsExpired': "הסמכות פגו.",
  'qr.tabEquipment': "כלי מדידה",
  'qr.tabPeople': "הסמכות עובדים",
  'qr.tabChecklist': "בדיקות לפני עבודה",
  'qr.statusActive': "פעיל",
  'qr.statusOutOfService': "מושבת",
  'qr.statusRetired': "הוצא משימוש",
  'qr.error': "שגיאה",
  'qr.phCode': "מספר מזהה (חובה)",
  'qr.phName': "שם הכלי (חובה)",
  'qr.phKind': "סוג (קליבר, מד לחץ…)",
  'qr.phSerial': "מספר סידורי",
  'qr.phLocation': "מיקום",
  'qr.save': "שמור",
  'qr.cancel': "ביטול",
  'qr.colCode': "מזהה",
  'qr.colName': "שם",
  'qr.colKind': "סוג",
  'qr.colLocation': "מיקום",
  'qr.colLastCal': "כויל לאחרונה",
  'qr.colNextCal': "כיול הבא",
  'qr.colStatus': "מצב",
  'qr.notManaged': "(לא מנוהל)",
  'qr.close': "סגור",
  'qr.calibrations': "כיולים",
  'qr.recordCalibration': "רישום כיול",
  'qr.resultPass': "עבר",
  'qr.resultAdjusted': "כויל מחדש",
  'qr.resultFail': "נכשל",
  'qr.failWarning': "כישלון יעביר את הכלי למצב „מושבת\" מיד, ותאריך הכיול לא יזוז.",
  'qr.noCalibrations': "אין רישומי כיול.",
  'qr.nextDue': "→ הבא {date}",
  'qr.validityReviewOpened': "נפתחה פעולה מתקנת",
  'qr.noValidCal': " — אין כיול תקין ברישום, ולכן כל המדידות שבוצעו בו נכללות",
  'qr.balloonUnit': "בלון {balloon}, יח׳ {unit}",
  'qr.measOk': "תקין",
  'qr.measFailed': "נפסל",
  'qr.ownerPlaceholder': "אחראי…",
  'qr.openAction': "פתח",
  'qr.returnToService': "החזר לשימוש",
  'qr.retire': "הוצא משימוש",
  'qr.qualTypes': "סוגי הסמכה",
  'qr.noExpiry': " (ללא תפוגה)",
  'qr.phNewQual': "הסמכה חדשה (ריתוך TIG…)",
  'qr.add': "הוסף",
  'qr.grantRenew': "הענקה / חידוש",
  'qr.employeePlaceholder': "עובד…",
  'qr.qualPlaceholder': "הסמכה…",
  'qr.expiry': "תפוגה",
  'qr.expiryRequired': " (חובה)",
  'qr.colEmployee': "עובד",
  'qr.colQual': "הסמכה",
  'qr.colGranted': "הוענקה",
  'qr.colExpiry': "תפוגה",
  'qr.noExpiryValue': "ללא תפוגה",
  'qr.expired': " · פגה",
  'qr.expiringInDays': " · בעוד {n} ימים",
  'qr.revoke': "בטל",
  // שמונה־עשר מפתחות שהמיפוי המוקדם שלי החמיץ, ונמצאו רק בקריאת הקובץ במלואו.
  'qr.newTool': "כלי חדש",
  'qr.showRetired': "הצג גם כלים שהוצאו משימוש",
  'qr.emptyEquipment': "אין כלי מדידה במרשם. כל כלי שמודדים בו לצורך בדיקת איכות צריך להופיע כאן.",
  'qr.overdueDays': "(באיחור {n} ימים)",
  'qr.dueInDays': "(בעוד {n} ימים)",
  'qr.performed': "בוצע",
  'qr.result': "תוצאה",
  'qr.lab': "מעבדה",
  'qr.checkPastMeasurements': "בדיקת תוקף מדידות קודמות",
  'qr.toolUnfitPrompt': "הכלי נמצא לא תקין. מה שנמדד בו מאז הכיול הקודם עשוי להיות שגוי — מי בודק, ועד מתי?",
  'qr.searchingMeasurements': "מחפש מדידות שבוצעו בכלי…",
  'qr.noSuspect': "לא נמצאו מדידות שבוצעו בכלי זה בטווח הזה — אין מדידות בספק.",
  'qr.suspectCount': "{n} מדידות בוצעו בכלי הזה",
  'qr.sinceLastGood': " מאז הכיול התקין האחרון ({date})",
  'qr.disable': "השבת",
  'qr.peopleNotice': "המרשם מתעד ומתריע — הוא אינו חוסם עבודה. חסימה של עובד ללא הסמכה תיפתח רק אחרי שהמרשם מלא, והיא החלטה שלך.",
  'qr.expires': "פגה",
  'qr.grantedAt': "מועד ההסמכה",
  'qr.emptyQualifications': "אין הסמכות רשומות.",
  'qr.remove': "הסר",
  // ── דוח אי-התאמות — השישי משמונת המסכים ──
  //
  // `GROUP_LABEL` ברמת המודול הוא הפעם הרביעית שהמלכודת חוזרת בתרגום הזה
  // (אחרי `NC_LABEL`, `STANDBY_STATUS_OPTIONS` ו-`LANE`).
  'ncr.groupNone': "ללא",
  'ncr.groupProject': "פרויקט",
  'ncr.groupEngineer': "מהנדס",
  'ncr.groupReporter': "מדווח",
  'ncr.groupCategory': "סוג",
  'ncr.noEngineer': "— ללא מהנדס",
  'ncr.print': "הדפס",
  'ncr.printTitle': "דוח אי-התאמות",
  'ncr.printAll': "כל אי-ההתאמות, ללא סינון",
  'ncr.printRows': "{n} אי-התאמות",
  'ncr.filter': "סינון:",
  'ncr.filterProject': "סינון לפי פרויקט",
  'ncr.allProjects': "כל הפרויקטים",
  'ncr.filterEngineer': "סינון לפי מהנדס",
  'ncr.allEngineers': "כל המהנדסים",
  'ncr.filterReporter': "סינון לפי מדווח",
  'ncr.allReporters': "כל המדווחים",
  'ncr.filterCategory': "סינון לפי סוג",
  'ncr.allCategories': "כל הסוגים",
  'ncr.filterStatus': "סינון לפי סטטוס",
  'ncr.statusAll': "הכל",
  'ncr.statusOpen': "פתוחות",
  'ncr.statusResolved': "טופלו",
  'ncr.groupBy': "קבץ:",
  'ncr.groupByAria': "לפי מה לקבץ",
  'ncr.statTotal': "סה״כ",
  'ncr.statOpen': "פתוחות",
  'ncr.statResolved': "טופלו",
  'ncr.statAvgClose': "זמן ממוצע לסגירה",
  'ncr.noneMatch': "אין אי-התאמות תואמות",
  'ncr.colType': "סוג",
  'ncr.colProjectPart': "פרויקט · חלק",
  'ncr.colEngineer': "מהנדס / מנהל",
  'ncr.colOpenedBy': "מי פתח",
  'ncr.colDone': "מה בוצע",
  'ncr.colTime': "זמן",
  'ncr.colStatus': "סטטוס",
  'ncr.markup': "סימון",
  'ncr.resolved': "טופל",
  'ncr.open': "פתוח",
  // ── תור חיתוך (מסור) — החמישי משמונת המסכים ──
  //
  // ── יחידות מידה בתוך עזרים ──────────────────────────────────────────────
  //
  // `mm()` ו-`metres()` אפו „מ׳" ו„מ״מ". זה בדיוק הדפוס שנרשם ב-09/09
  // („`mm(v)` בשלושה עותקים"), והפעם הוא מקומי לקובץ ולכן בהיקף.
  'cq.metres': "{n} מ׳",
  'cq.mm': "{n} מ״מ",
  'cq.missingRemnants': "שאריות שלא נמצאו במדף",
  'cq.foundLater': " · {n} אותרו מאוחר יותר",
  'cq.error': "שגיאה",
  'cq.statBars': "מוטות שלמים",
  'cq.statClosedUncut': "נסגרו בלי חיתוך",
  'cq.statClosedUncutHint': "נפתחו בתג ולא נחתך מהם דבר — לא נספרים כעבודה",
  'cq.closedUncutCount': "נסגרו בלי חיתוך: {n}",
  'cq.statRemnants': "שאריות בשימוש",
  'cq.statRemnantsHint': "חומר ששולם עליו וחוזר לעבודה",
  'cq.statPieces': "חתיכות בתור",
  'cq.statKeep': "חוזר למדף",
  'cq.statWaste': "פחת",
  'cq.unplannable': "{n} חתיכות לא נכנסו לתוכנית.",
  'cq.unplannable_one': "חתיכה אחת לא נכנסה לתוכנית.",
  'cq.unplannable_many': "{n} חתיכות לא נכנסו לתוכנית.",
  'cq.unplannableWhy': " ברוב המקרים זו זווית שמעבר לטווח המסור (מעל 45°)",
  'cq.nothingToCut': "אין כרגע מה לחתוך",
  'cq.barsCount': "{n} מוטות",
  'cq.barsCount_one': "מוט אחד",
  'cq.barsCount_many': "{n} מוטות",
  'cq.piecesCount': "{n} חתיכות",
  'cq.piecesCount_one': "חתיכה אחת",
  'cq.piecesCount_many': "{n} חתיכות",
  'cq.wastePct': "פחת {n}%",
  // („חומר"/„עבודה" הוכנו לשורת המטבע ואינם בשימוש: `₪` עם
  //  `toLocaleString('he-IL')` הוא פורמט מטבע נעול, פריט נפרד מתרגום
  //  מסך. מפתח מוגדר שאינו נקרא הוא זבל שהשומר אינו רואה, ולכן הוסר.)
  'cq.completeIn': "השלימו ב",
  'cq.profilesLink': "הגדרות ← פרופילים לחיתוך",
  'cq.profileUnplannable': "חתיכות מהפרופיל הזה לא ניתנות לתכנון (זווית מעבר ל-45° או חתיכה ארוכה ממוט).",
  'cq.onSaw': "על המסור",
  'cq.barDone': "הושלם",
  'cq.barSeq': "מוט {n}",
  'cq.wholeBar': "מוט שלם · {size}",
  'cq.hidePieces': "הסתר חתיכות",
  'cq.pieceCut': "נחתך",
  'cq.pieceSkipped': "דולג",
  'cq.pieceWaiting': "ממתין",
  // ── ליקוט מחסן — הרביעי משמונת המסכים (אלי, 17/09/2026) ──
  //
  // ── ריבוי, ולא מפתח אחד עם {n} ─────────────────────────────────────────
  //
  // „יום" / „יומיים" / „{n} ימים" הם שלוש צורות בעברית, וברוסית שלוש
  // אחרות לגמרי (день / дня / дней) שנבחרות לפי הספרה האחרונה. מפתח אחד
  // עם מציין מקום היה מכריח את הרוסית לצורה אחת שגויה בשני שלישים מהמקרים.
  'wp.lessThanHour': "פחות משעה",
  // `hrs()` מחזיר גם „{n} ש׳" — יחידה מקוצרת שהחמצתי במניין המחרוזות,
  // ובלעדיה המסך היה נשאר עם „ש׳" עברי אחרי שכל השאר תורגם.
  'wp.hoursShort': "{n} ש׳",
  'wp.oneDay': "יום",
  'wp.twoDays': "יומיים",
  'wp.manyDays': "{n} ימים",
  'wp.manyDays_one': "{n} יום",
  'wp.manyDays_many': "{n} ימים",
  'wp.laneCheck': "לבדוק מלאי",
  'wp.lanePurchase': "ממתין לרכש",
  'wp.lanePick': "ללקט",
  'wp.laneArrival': "הוזמן — בדרך",
  'wp.promised': "הובטח {d}",
  'wp.noPromise': "הוזמן בלי תאריך אספקה",
  'wp.neededBy': "נדרש ל-{d}",
  'kit.actionFailed': "הפעולה נכשלה",
  'kit.issuedSome': "נופק לפרויקט הזה: {n}",
  'kit.noneYet': "טרם נופק קיט לפרויקט הזה",
  'kit.open': "פתח קיט ליקוט",
  'kit.title': "קיט {n}",
  'kit.openedAt': "נפתח {d}",
  'kit.inCart': "בעגלה: {n}",
  'kit.noLocation': 'אין מיקום',
  'kit.empty': "העגלה ריקה. הוסף אליה מה שמוכן ללקט.",
  'kit.outOf': "מתוך {n}",
  'kit.removeAria': "הסר {name} מהעגלה",
  'kit.issue': "נפק: {n}",
  'kit.cancel': "בטל קיט",
  'kit.emptyIsNotIssue': "עגלה ריקה אינה ניפוק",
  'kit.justIssued': "נופק: קיט {seq} · שורות: {n}",
  // מדבקה לכל פריט בעגלה. „פריט" ולא „שורה": מי שקורא את הכפתור מחזיק
  // ברגים ביד, לא טבלה.
  'kit.labelPerItem': 'מדבקה לכל פריט ({n})',
  'kit.labelPerItem_one': 'מדבקה לפריט',
  'kit.labelPerItem_many': 'מדבקה לכל פריט ({n})',
  'kit.closedLine': "קיט {seq} · שורות: {n}",
  'kit.issuedList': "קיטים שנופקו: {n}",
  'kit.clockChain': "שרשרת",
  'kit.clockWarehouse': "מחסן",
  'kit.clockWaiting': "המתנה לספק",
  'kit.clockPending': "טרם הכול הגיע",
  'kit.clockRunning': "רץ עכשיו",
  'kit.oneHour': "שעה",
  'kit.manyHours': "{n} ש׳",
  'kit.oneDay': "יום",
  'kit.twoDays': "יומיים",
  'kit.manyDays': "{n} ימים",
  'ui.loading': "טוען...",
  'ui.errorLoadingLabel': "שגיאה בטעינת {label}",
  'ui.errorLoading': "שגיאה בטעינת נתונים",
  'ui.errorHintNetwork': "בדוק את חיבור הרשת ונסה שוב",
  'ui.errorHintForbidden': "פנה למנהל המערכת אם אתה אמור לראות את המסך הזה.",
  'ui.retry': "נסה שוב",
  'wp.laneAll': "הכול",
  'wp.stockRecorded': "נרשם מלאי {n}",
  'wp.markedPurchase': "סומן: נדרש רכש",
  'wp.notSaved': "לא נשמר",
  'wp.searchPlaceholder': "מק״ט, שם, פרויקט, לקוח או מי שביקש",
  'wp.searchAria': "חיפוש בדרישות",
  'wp.noneMatch': "אין דרישות שמתאימות לסינון.",
  'wp.noneOpen': "אין דרישות פתוחות במחסן.",
  'wp.itemsCountOne': "פריט אחד",
  'wp.itemsCount': "{n} פריטים",
  'wp.itemsCount_one': "פריט אחד",
  'wp.itemsCount_many': "{n} פריטים",
  'wp.requestedBy': "ביקש: {name}",
  'wp.subAssembly': "תת-הרכבה: {name}",
  'wp.belowMin': " — מתחת",
  'wp.noMinimum': "אין מינימום מוגדר",
  'wp.requestedToStock': "ביקש למלאי:",
  'wp.awaitingPurchaseSuffix': " — ממתין לרכש",
  'wp.inStock': "יש במלאי",
  'wp.plusToStock': "+ למלאי",
  'wp.coversClose': "יש במלאי — סגור",
  'wp.needsPurchase': "נדרש רכש",
  // ── תפעול חי — המסך שמנהל הייצור עומד מולו ──
  //
  // השלישי משמונת המסכים שאלי אישר לתרגום (17/09/2026).
  // אינטרפולציה בתוך JSX — ספירת המחרוזות ב-grep לא תפסה אותה, וזה בדיוק
  // מה ש„ספרתי 27" מחמיץ. נמצאה בקריאת הקובץ, לא במניין.
  'ops.stuckBadge': "{n} תקוע",
  'ops.stuckBadge_one': "{n} תקוע",
  'ops.stuckBadge_many': "{n} תקועים",
  'ops.statusAll': "כל הסטטוסים",
  'ops.statusStuck': "תקועים",
  'ops.statusPending': "בעבודה חלקית",
  'ops.statusPaused': "מושהים",
  'ops.statusReady': "מוכנים להתחלה",
  // ── זמן יחסי: עזר שמחזיר טקסט ────────────────────────────────────────
  //
  // `relativeTimeShort` אפה „עכשיו" ו„לפני {n} ד׳" בתוך פונקציה — אותו
  // דפוס שמסתיר תרגום, אלא שזה מקומי לקובץ ולכן בהיקף. `lib/time.ts`
  // המשותף לשמונה מסכים נשאר פריט בפני עצמו.
  'ops.justNow': "עכשיו",
  'ops.agoMinutes': "לפני {n} ד׳",
  'ops.agoHours': "לפני {n} ש׳",
  'ops.agoDays': "לפני {n} י׳",
  'ops.updatedAt': "עודכן {when}",
  'ops.soundOn': "התראות קוליות פועלות — לחץ לכיבוי",
  'ops.soundOff': "התראות קוליות מושבתות — לחץ להפעלה",
  'ops.soundAria': "התראות קוליות",
  'ops.sound': "צליל",
  'ops.muted': "מושתק",
  'ops.fullscreenTitle': "מסך מלא — מתאים לטלוויזיה בקומה",
  'ops.fullscreenAria': "מסך מלא",
  'ops.kpiActive': "פעיל עכשיו",
  'ops.kpiStandby': "בהמתנה",
  'ops.kpiDone': "הושלמו",
  'ops.filterStatus': "סינון לפי סטטוס",
  'ops.allDepts': "כל המחלקות",
  'ops.filterDept': "סינון לפי מחלקה",
  'ops.allProjects': "כל הפרויקטים",
  'ops.filterProject': "סינון לפי פרויקט",
  'ops.searchPlaceholder': "חיפוש מק״ט, חלק, פרויקט או עובד…",
  'ops.searchAria': "חיפוש בתפעול חי",
  'ops.errorLabel': "התפעול",
  'ops.deptLabel': "מחלקה:",
  'ops.deptAll': "הכל",
  // ── מסך הקיר „מי עובד על מה" / „אי-התאמות" ──
  //
  // מסך קיר, כלומר נקרא מרחוק ובמשמרת שלמה. השני משמונת המסכים שאלי
  // אישר לתרגום (17/09/2026).
  'tv.matShortage': "חוסר חומר",
  'tv.prodNc': "אי-התאמת ייצור",
  'tv.prevStationNc': "אי-התאמה בתחנה קודמת",
  'tv.whoWorks': "מי עובד על מה",
  'tv.ncGaps': "אי-התאמות / פערים",
  'tv.showWhoWorks': "הצג מי עובד על מה",
  'tv.showNcGaps': "הצג אי-התאמות / פערים",
  'tv.noActiveWork': "אין עבודה פעילה כרגע",
  'tv.presentNotWorking': "נוכחים ולא בעבודה כרגע:",
  'tv.projectLine': "פרויקט {project}",
  'tv.revisionSuffix': " · מהד׳ {rev}",
  // ── שתי תוויות, ולא משפט אחד ──────────────────────────────────────────
  //
  // חשבתי שאלה שברי משפט ורציתי לאחד אותם למפתח אחד. קריאת ההקשר הראתה
  // שהן **שתי מדידות שונות**: מונה אחד לזמן העובד על החלק, ומונה שני לזמן
  // החלק בתחנה ולדקות שדווחו. איחוד היה יוצר משפט שקרי.
  'tv.workerTimer': "העובד עובד",
  'tv.partTimer': "על החלק · עבודה",
  'tv.noOpenNc': "אין אי-התאמות פתוחות ✓",
  'tv.ncOpenedChart': "אי-התאמות שנפתחו (8 שבועות)",
  'tv.ncCloseHoursChart': "זמן ממוצע לסגירה — שעות (8 שבועות)",
  // ── לוח „מה מגיע אליי" (F3) — מסך קיר במחלקה ──
  //
  // אלי אישר 17/09/2026 את החיתוך: שמונת המסכים שדוברי רוסית באמת נוגעים
  // בהם, 300 מחרוזות. זה הראשון — הקטן שבהם — ובו נבדקת התבנית כולה
  // (מצייני מקום, aria-label ש„אינו נראה", וסימנים) לפני שהיא מוכפלת פי שמונה.
  'inc.title': "מה מגיע ל{dept}",
  'inc.deptFallback': "מחלקה",
  'inc.subtitle': "חלקים בדרך למחלקה זו, מחולק לפי מחלקת המקור · {n} חלקים",
  'inc.subtitle_one': "חלקים בדרך למחלקה זו, מחולק לפי מחלקת המקור · חלק אחד",
  'inc.subtitle_many': "חלקים בדרך למחלקה זו, מחולק לפי מחלקת המקור · {n} חלקים",
  // לא נראה על המסך ולכן נשכח ראשון — ובלעדיו קורא-מסך אינו יודע מה הפקד.
  'inc.pickDept': "בחר מחלקה",
  'inc.pickDeptOption': "בחר מחלקה…",
  'inc.refresh': "רענן",
  'inc.pickToSee': "בחר מחלקה כדי לראות מה בדרך אליה.",
  'inc.loading': "טוען…",
  'inc.none': "אין כרגע חלקים בדרך למחלקה זו. ✓",
  'inc.fromDept': "ממחלקת {dept}",
  'inc.partsCount': "{n} חלקים",
  'inc.partsCount_one': "חלק אחד",
  'inc.partsCount_many': "{n} חלקים",
  'inc.noThumb': "אין תצוגה",
  'inc.revision': "מהד׳ {rev}",
  'inc.nowAt': "כעת:",
  'fin.subtitle': "קיוסק גמר",
  'fin.whichStation': "איזו עמדה זו?",
  'fin.whichStationHint': "נבחר פעם אחת ונזכר במחשב הזה",
  'fin.identify': "מי עובד?",
  'fin.identifyHint': "סרוק את התג שלך, או בחר את עצמך מהרשימה",
  'fin.identifyFirst': "סרוק תג עובד לפני סריקת החלק",
  'fin.scanPart': "סרוק את הברקוד שעל החלק — הזמן מתחיל מיד",
  'fin.notYou': "זה לא אני",
  'fin.cardPart': "חלק",
  'fin.cardOpenDrawing': "פתח שרטוט",
  'fin.cardNoDrawing': "אין שרטוט פעיל",
  'fin.cardNc': "אי התאמה",
  'fin.cardOf': "{done} מתוך {total}",
  'fin.boardEmptyNow': "אף אחד לא עובד כאן כרגע",
  'fin.finishedByBadge': "סרוק את הכרטיס שלך כדי לסיים ולדווח כמות",
  'fin.ncWhat': "מה נמצא?",
  'fin.ncNote': "פרטים (לא חובה)",
  'fin.ncNotePlaceholder': "מה בדיוק, ואיפה בחלק",
  'fin.ncSend': "פתח אי התאמה",
  'fin.ncScrapHint': "ההחלטה מה לעשות בחומר — תיקון, שימוש כפי שהוא או פסילה — נעשית בתהליך אי-ההתאמה, על ידי מי שמוסמך לכך.",
  'fin.ncOpened': "נפתחה אי התאמה על {part}",
  'fin.cardStateWorking': "בעבודה",
  'fin.cardStateNc': "אי התאמה פתוחה",
  'fin.scanHere': "סרוק כאן",
  'fin.clockRunning': "הזמן רץ. סרוק שוב את אותו חלק כדי לסיים",
  'fin.viewDrawing': "השרטוט",
  'fin.closeDrawing': "סגור",
  'fin.finishFirst': "יש לך שעון פתוח על {part} — סרוק אותו כדי לסיים",
  'fin.switchWorker': "עובד אחר",
  'fin.working': "רגע…",
  'fin.loading': "טוען…",
  'fin.boardTitle': "מי עובד עכשיו ב{dept}",
  'fin.nobody': "אף אחד לא עובד כאן כרגע",
  'fin.you': "זה אתה",
  'fin.forMinutes': "{n} דק׳",
  'fin.forMinutes_one': "דקה אחת",
  'fin.forMinutes_many': "{n} דק׳",
  'fin.alsoHere': "עובדים גם על החלק הזה: {names}",
  'fin.howMany': "כמה חלקים סיימת?",
  'fin.alreadyDone': "הושלמו כאן {done} מתוך {total}",
  'fin.outOf': "נותרו {n}",
  'fin.unitsInvalid': "הזן מספר חלקים",
  'fin.save': "שמור",
  'fin.v2DrawingOf': 'שרטוט {part}',
  'fin.v2ApprovalTitle': 'נדרש אישור מנהל',
  'fin.v2ManagerCode': 'קוד מנהל',
  'fin.v2BadCode': 'קוד מנהל שגוי — נסה שוב',
  'fin.cancel': "ביטול",
  'fin.stageOpen': "נשמר · {part}: {done} מתוך {total}. החלק ממשיך בגמר, והשאר עובר הלאה",
  'fin.stageClosed': "נשמר · {part}: כל {done} הושלמו. השלב נסגר",
  // „עובד יכול לדווח 0 אם הוא לא סיים" — אלי, 21/09/2026
  'fin.zeroReported': "נשמר · {part}: הזמן נרשם, טרם הושלמו חלקים. החלק נשאר כאן",
  // „לאן החלק עובר" — אלי, 20/09/2026
  'fin.handoffTitle': "לאן להעביר את החלק",
  'fin.handoffStage': "השלב הבא במסלול: {stage}",
  'fin.handoffParent': "מצטרף להרכבה {part} · {name}",
  'fin.handoffNone': "אין המשך רשום במסלול — שאל את המנהל לפני שאתה מניח",
  'fin.handoffNoDept': "לא שויכה מחלקה לשלב — שאל את המנהל",
  'fin.handoffGot': "הבנתי",
  // שותפים על אותו שעון — אלי, 20/09/2026, „שניהם"
  'fin.mates': "שותפים לעבודה",
  'fin.matesNone': "אין — העבודה נזקפת לך בלבד",
  'fin.mateAdd': "הוסף שותף לשעון",
  'fin.mateScanHint': "השותף מצמיד את התג שלו, או נבחר מהרשימה",
  'fin.mateScanHere': "תג השותף",
  'fin.mateRemove': "הסר את {name} מהשעון",
  'fin.mateCancel': "ביטול",
  'fin.v2RevChanged': 'הוחלפה רוויזיה — עבדת לפי {from}, העדכנית {to}',
  'fin.v2FinishWork': 'סיים ודווח כמות',
  'fin.v2DrawingArrived': 'עלה שרטוט לחלק — רוויזיה {to}',
  'fin.v2PickPart': 'בחר חלק מהרשימה',
  'fin.v2WhichPart': 'על איזה חלק אתה עובד?',
  'fin.v2NoQueue': 'אין כרגע חלקים שממתינים בעמדה הזאת',
  'fin.v2ManyOnPart': 'כמה עובדים על החלק הזה — לחץ „סיים ודווח כמות" על הכרטיס שלך',
  'fin.v2NoteAcked': 'אישרת',
  'fin.v2NoteNew': 'לאישור',
  'fin.v2NoteTitle': 'הערת איכות על החלק',
  'fin.v2NoteAck': 'קראתי ואני מאשר',
  'fin.v2NcTitle': 'אי-התאמה חוסמת — לא לעבוד עד שתיסגר',
  'fin.v2NcOpenTitle': 'אי-התאמה פתוחה — מותר להמשיך לעבוד',
  'fin.v2NcSince': 'נפתחה {when}',
  'fin.v2Remaining': 'נותרו',
  'fin.v2OutOf': 'מתוך {n}',
  'fin.v2Elapsed': 'זמן',
  'fin.v2Switch': 'החלף עמדה',
  'fin.v2InWork': '{n} בעבודה',
  'fin.v2ScanBadge': 'סרוק את התג שלך כדי להתחיל',
  'fin.v2NowScanPart': 'כעת סרוק את הברקוד של החלק',
  'fin.v2Started': '{name} — העבודה התחילה',
  'fin.v2Closed': 'נסגר ונרשם',
  'fin.v2BadgeFailed': 'התג לא זוהה — נסה שוב, או בחר את עצמך מהרשימה',
  'fin.v2PickFailed': 'לא הצלחנו לזהות — נסה שוב',
  'fin.v2Network': 'שגיאת רשת',
  'fin.v2ClosingOn': 'סוגר את העבודה על',
  'fin.v2ZeroOk': '0 זו תשובה תקינה',
  'fin.v2ThenOpens': 'אחרי זה תיפתח העבודה על {part}',
  'fin.v2UnitsLabel': 'כמות שבוצעה',
  'fin.v2UnitsBad': 'יש להזין מספר שלם. 0 תקין.',
  'fin.v2WhoAreYou': 'מי אתה?',
  'fin.v2Close': 'סגור',
  'ps.title': "לא ניתן להתחיל חלק חדש לפני סגירת הקודם",
  'ps.youHaveOpen': "יש לך סשן פתוח על חלק",
  'ps.openedAgo': "נפתח לפני",
  'ps.enterUnits': "הזן את מספר היחידות שהושלמו בסשן הקודם, ונסגור אותו לפני שמתחילים",
  'ps.partWord': "חלק",
  'ps.howMany': "כמה יחידות הושלמו על {part}?",
  'ps.closingOpening': "סוגר ופותח...",
  'ps.cancelPriorStart': "בטל את הקודם והתחל חלק חדש",
  'ps.saveStart': "שמור והתחל חלק חדש",
  'ps.cancel': "ביטול",
  'cam.scanWithCamera': "סרוק עם מצלמה",
  'cam.insecure': "הדפדפן חוסם מצלמה בחיבור לא מאובטח. פתח את המערכת בכתובת https://mes.edal.co.il — או, בחיבור מקומי באייפון, התקן את תעודת האבטחה.",
  'cam.policyBlocked': "המצלמה חסומה בהגדרת השרת, לא במכשיר שלך. אין מה לשנות בטלפון — יש לעדכן את הרשאת המצלמה ב-nginx.",
  'cam.blocked': "הגישה למצלמה נחסמה. אשר הרשאת מצלמה — ובאייפון ודא שתעודת האבטחה מותקנת.",
  'cam.notFound': "לא נמצאה מצלמה זמינה במכשיר.",
  'cam.openError': "שגיאה בפתיחת המצלמה.",
  'cam.close': "סגור",
  'cam.aim': "כוון את המצלמה לברקוד",
  'cam.auto': "הזיהוי אוטומטי",
  'cam.cancel': "ביטול",
  'ma.title': "אישור מנהל לדילוג",
  'ma.line': "שלב {n} ({stage}) — דורש אישור מנהל",
  // הקוד האישי של המנהל — לא סיסמת המערכת. ראה ManagerApprovalModal.
  'ma.code': "קוד אישור אישי",
  'ma.cancel': "בטל",
  'ma.ok': "אישור",
  // מסך ההתחלה. עד 19/09/2026 סריקת התחלה נחתה על מסך התוצאה והציגה
  // „נשמר · 00:00 · 0 יחידות" — סשן שהסתיים באפס תפוקה, ההפך ממה שקרה.
  'rs.started': "העבודה התחילה",
  'rs.startedAt': "התחלה {time}",
  'rs.startedApproved': "אושר מחוץ לסדר",
  'rs.stage': "שלב {stage}",
  'rs.saved': "נשמר בהצלחה!",
  'rs.workTime': "זמן עבודה",
  'rs.units': "{n} יחידות",
  'rs.part': "חלק {part}",
  'rs.partners': "שותפים: {names}",
  'rs.returning': "חוזר לבית בעוד {n} שניות…",
  'bo.scanDrawing': "סרוק שרטוט להתחלה / סיום, או סרוק תג נוסף להוספת שותף",
  'bo.notMe': "לא אני, חזור",
  'bo.ok': "אישור",
  'kq.deptQueue': "התור של מחלקת {dept} — מה הבא בדחיפות",
  'kq.queue': "מה הבא בדחיפות",
  'kq.due': "אספקה",
  'nk.reportAnother': "דיווח נוסף",
  'ls.errBadge': "שגיאה בזיהוי התג",
  'ls.errFile': "שגיאה בקריאת הקובץ",
  'ls.errSave': "שגיאה בשמירת הדיווח",
  'ls.subtitle': "מערכת דיווח לייזר",
  'ls.employeeNo': "מס׳ עובד {n}",
  'ls.scanToStart': "סרוק את התג שלך כדי להתחיל",
  'ls.identify': "הזדהה לפני העלאת דוח החיתוך",
  'ls.badgePlaceholder': "מספר עובד / סריקת תג",
  'ls.continue': "המשך",
  'ls.uploadTitle': "העלה את דוח החיתוך (Excel)",
  'ls.uploadHint': "בחר את קובץ הנפרש שיצא ממכונת הלייזר",
  'ls.reading': "קורא את הקובץ…",
  'ls.pickFile': "בחר קובץ אקסל",
  'ls.cancelBack': "ביטול וחזרה",
  'ls.saved': "הדיווח נשמר",
  'ls.reported': "דווחו",
  'ls.willReport': "ידווחו",
  'ls.partsWord': "חלקים",
  'ls.unitsWord': "יחידות",
  'ls.minutesWord': "דק׳",
  'ls.skippedDetail': "{n} חלקים דולגו (ראה פירוט קודם)",
  'ls.skippedDetail_one': "חלק אחד דולג (ראה פירוט קודם)",
  'ls.skippedDetail_many': "{n} חלקים דולגו (ראה פירוט קודם)",
  'ls.newReport': "דיווח חדש",
  'ls.cutReport': "דוח חיתוך",
  'ls.material': "חומר",
  'ls.thickness': "עובי",
  'ls.mm': "מ״מ",
  'ls.sheetWeight': "משקל גיליון",
  'ls.kg': "ק״ג",
  'ls.efficiency': "יעילות",
  'ls.setupNote': "זמן SETUP: {n} דק׳ — מתחלק בין החלקים לפי זמן חיתוך",
  'ls.colPart': "חלק",
  'ls.colQty': "כמות",
  'ls.colCutTime': "זמן חיתוך",
  'ls.colMinutes': "דקות דיווח",
  'ls.colStatus': "סטטוס",
  'ls.extraCut': 'חיתוך נוסף — מעבר לכמות',
  'ls.willBeReported': "✓ ידווח",
  'ls.skippedReason': "דולג — {reason}",
  'ls.skippedShort': "{n} דולגו",
  'ls.commitAnyway': "אפשר לאשר בכל זאת — הגיליון, החומר והזמן יירשמו, ולכל חלק חסר בפרויקט שקיים במערכת תיפתח בקשת שרטוט. פרויקט שאינו במערכת — מדולג.",
  'ls.otherFile': "קובץ אחר",
  'ls.confirmReport': "אשר ודווח",
  'warehouse.receive': "קליטה",
  'warehouse.receiveButton': "קלוט סחורה",
  'warehouse.wasReceived': "נקלט",
  'warehouse.partial': "התקבל {got} מתוך {need}",
  'warehouse.openOrder': "הזמנה פתוחה",
  'warehouse.orderPlaceholder': "פריט / מק״ט / ספק / פרויקט",
  'warehouse.remaining': "נותרו {n}",
  'warehouse.late': "באיחור",
  'warehouse.noOpenOrders': "אין הזמנות פתוחות",
  'warehouse.supplierDeliveryNote': "תעודת משלוח של הספק",
  'warehouse.inspection': 'בדיקת קבלה',
  'warehouse.inspectOk': 'תקין',
  'warehouse.inspectBad': 'לא תקין',
  'warehouse.rejectQty': 'כמה נפסלו',
  'warehouse.rejectReason': 'סיבת הפסילה',
  'warehouse.inspectNotePlaceholder': 'פירוט (לא חובה)',
  'warehouse.rejectExplains': 'הכמות שנפסלה לא תיכנס למלאי, והפריט ייחסם לניפוק עד שיטופל.',
  'warehouse.rejectedN': 'נפסלו {n}',
  // Serials at the counter. The gate existed on the server long before there
  // was anywhere to type one, so a serialised item simply could not be issued.
  'warehouse.serials': 'מספרים סריאליים',
  'warehouse.serialsWhy': '{label} — חובה מספר סריאלי לכל יחידה. בלעדיו לא ניתן יהיה לאתר את היחידה בקריאת שירות.',
  'warehouse.serialUnit': 'יחידה {n}',

  // ── ספירת מלאי (kiosk) ──
  // A shop-floor screen, so it is translated like every other one: the people
  // counting boxes are not the people who wrote the system.
  // ── טבלת חיתוך ליד השרטוט (02/11/2026) ──
  'stcut.title': 'טבלת חיתוך לשרטוט',
  'stcut.mm': 'מ״מ',
  'stcut.square': 'ישר',
  'stcut.cutOf': 'נחתכו {n} מתוך {total}',
  'stcut.cutAll': 'כל החתיכות נחתכו',
  'stcut.lineCut': 'נחתכו כולן',
  'stcut.notCut': 'טרם נחתכו במסור',
  'stcut.multiplied': 'הכמויות כבר כפול {n} — כמות השרטוט בפרויקט',
  'stqa.title': "דרישות איכות לחלק הזה",
  'stqa.openCount': "{n} ממתינות",
  'stqa.openCount_one': "אחת ממתינה",
  'stqa.openCount_many': "{n} ממתינות",
  'stqa.engineerOnly': "מהנדס חייב לאשר את הבדיקה הזו — יש לקרוא למהנדס.",
  'stqa.faiOnly': "FAI — מנהל הייצור או מנהל ההנדסה בודק את היחידה הראשונה וחותם. יש לקרוא לאחד מהם.",
  'stqa.whatIsWrong': "מה הבעיה שנמצאה? (חובה)",
  'stqa.confirm': "בדקתי — תקין",
  'stqa.problem': "יש בעיה",
  'stqa.reportProblem': "דווח על בעיה",
  // ── עבודת שטח (התקנות) — הטלפון של הצוות ──
  'field.title': 'עבודת שטח',
  'field.loginHint': 'הזן את קוד העבודה שקיבלת ממנהל השירות, ואת מספר העובד שלך.',
  'field.codeFromLink': 'קוד העבודה הגיע מהקישור. נשאר רק לסרוק את התג או להקליד את מספר העובד.',
  'field.changeCode': 'קוד אחר',
  'field.myInstallations': 'ההתקנות שלי',
  'field.codeOnce': 'טלפון חדש — הקלידו פעם אחת את קוד העבודה בן 6 הספרות (מהמייל / הווטסאפ). מהפעם הבאה מספיק לסרוק את התג.',
  'field.scanCode': 'סרוק קוד עבודה',
  'field.scanCodeOrBadge': 'סרוק קוד עבודה או תג',
  'field.jobCode': 'קוד עבודה',
  'field.badge': 'מספר עובד',
  'field.enter': 'כניסה',
  'field.loginFailed': 'לא ניתן להיכנס',
  'field.loadFailed': 'לא ניתן לטעון את פרטי העבודה',
  'field.saveFailed': 'השמירה נכשלה — נסה שוב',
  'field.photoFailed': 'העלאת התמונה נכשלה',
  // ── חתימת נציג הלקוח על סיום העבודה (21/09/2026) ────────────────────
  //
  // ⚠ הטכנאים דוברי רוסית, והמקטע הזה הוא שער: בלי תשובה אי אפשר לסגור
  // את היום. מחרוזת קשיחה כאן פירושה טכנאי שלא מבין למה הכפתור מסרב.
  'field.customerSignTitle': 'מי מטעם הלקוח מאשר שהעבודה הסתיימה?',
  'field.customerSignAction': 'החתם את נציג הלקוח',
  'field.customerAbsent': 'לא נחתם: לא נכח או סירב',
  'field.customerAbsentWhy': 'למה אין חתימה? (נרשם על שמך)',
  'field.customerAbsentExample': 'מנהל האתר עזב לפני סיום העבודה, או סירב לחתום',
  'field.customerSignedBy': 'נציג הלקוח חתם',
  'field.customerSignedByWho': 'החתים',
  'field.customerNoSignature': 'ללא חתימת לקוח',
  'field.customerNoSignatureBy': 'דיווח',
  'field.signatureSave': 'שמור את החתימה',
  'field.signatureHint': 'החתימה נשמרת על העבודה. הסגירה עצמה היא הכפתור שמתחת.',
  'field.signatureFailed': 'שמירת החתימה נכשלה — נסה שוב',
  'field.queueSent': 'כל מה שהמתין נשלח',
  'field.photoKept': 'התמונה לא עלתה ולא נשמרה במכשיר — היא עדיין בכפתור, נסה שוב כשתחזור הקליטה',
  'field.closePending': 'חלק מהדיווח עדיין לא הגיע לשרת — סגור את היום שוב כשתחזור הקליטה',
  'field.removePhoto': 'מחק את התמונה',
  'field.removePhotoConfirm': 'למחוק את התמונה?',
  'field.photoRemoveFailed': 'מחיקת התמונה נכשלה',

  // ── מה שהשרת שולח למסך בשטח ───────────────────────────────────────────────
  //
  // אלי, 19/09/2026: „הטכנאים שלי מדברים רוסית." המסכים תורגמו; מה שנשאר
  // עברית היה מה שהשרת **שולח** — 31 הודעות סירוב ו-17 תבניות „מה עוד חסר".
  //
  // ⚠ הערכים העבריים כאן הם **מראה של השרת**, לא מקור. `fieldErrorText` ו-
  // `stationErrorText` מקצרים דרך לעברית של השרת כשהשפה עברית ולעולם אינם
  // קוראים אותם; הם קיימים כדי לספק את שומר התאמת-המילונים
  // (`i18n.test.ts` — „כל מפתח עברי יש לו מקביל רוסי"). מי שמתקן משפט
  // בשרת אינו חייב לרדוף אחריו לכאן. הרוסית, לעומת זאת, נכתבת פעם אחת
  // ב-`ru.ts` והיא היחידה שבאמת מוצגת.

  // חסמים — `server/.../field-blockers.ts`
  'fb.DAY_NOTHING': 'לא דווח דבר',
  'fb.DAY_TIMES': '{parts}',
  'fb.DAY_TIME_LEFT_PLANT': 'שעת יציאה',
  'fb.DAY_TIME_ARRIVED': 'שעת הגעה',
  'fb.DAY_TIME_LEFT_SITE': 'שעת עזיבה',
  'fb.DAY_TIME_RETURNED': 'שעת חזרה',
  'fb.DAY_ODOMETER': 'קילומטר',
  'fb.CLOSE_SIGNATURE': 'חתימת נציג הלקוח — או למה לא נחתם: לא נכח או סירב',
  'fb.CLOSE_NO_TASKS': 'לא הוגדרו משימות לעבודה',
  'fb.CLOSE_OPEN_TASKS': '{n} משימות שטרם בוצעו',
  'fb.CLOSE_NO_CHECKS': 'לא הוגדרו בדיקות סופיות',
  'field.noChecksHint': 'את זה מגדיר המשרד (מנהל ההתקנות). התקשרו אליו; אין צורך לעשות כאן משהו.',
  'fb.CLOSE_VISIT_OPEN': 'צ׳קליסט האחזקה: {n} שורות בלי תשובה',
  'fb.CLOSE_VISIT_UNSIGNED': 'צ׳קליסט האחזקה טרם נחתם',
  'fb.CLOSE_PENDING_CHECKS': '{n} בדיקות סופיות שטרם בוצעו',
  'fb.CLOSE_FAILED_CHECKS': '{n} בדיקות שנכשלו וטרם טופלו',
  'fb.CLOSE_EQUIPMENT': '{n} פריטי ציוד שלא סומנו כנלקחו',
  'fb.CLOSE_NO_CREW': 'לא שובץ צוות',
  'fb.CLOSE_NO_DAYS': '{who} — לא דווח אף יום: יום שנסגר בטלפון של אחד מהצוות נסגר לכולם; מי שלא יצא — מנהל השירות מוריד אותו מהצוות',
  'fb.CLOSE_CREW_NOT_WITH': '{who} — לא היה בעבודה באף יום שדווח (סומן „לא היה איתי" או עבד בפרויקט אחר). לא חוסם את הסגירה; אם לא יצא אליה — הורידו אותו מהצוות',
  'fb.CLOSE_DAY_GAPS': '{who} · {date} — {parts}',
  'fb.CLOSE_DAY_OPEN': '{who} · {date} — היום לא נסגר: {who} לוחץ „סגור את היום“ בטלפון שלו, או שמנהל השירות סוגר אותו',

  // סירובים — `field.routes.ts` / `field*.service.ts`
  'fe.badFieldCode': 'קוד לא נכון, פג תוקף, או שאינך משובץ לעבודה הזו',
  'fe.fieldJobClosed': 'העבודה הזו כבר נסגרה — אין בה מה לדווח. שאלות — למנהל השירות.',
  'fe.noFinalChecks': 'להתקנה עוד לא הוגדרו בדיקות סופיות — אי אפשר להתחיל. התקשר למנהל ההתקנות.',
  'fe.loginIncomplete': 'יש להזין קוד עבודה ומספר עובד',
  'fe.notOnCrewStaff': 'אינך משובץ לצוות של העבודה הזו',
  'fe.jobNotFound': 'עבודה לא נמצאה',
  'fe.mustSignIn': 'יש להתחבר',
  'fe.noCode': 'יש לסרוק תג או להזין קוד עבודה',
  'fe.sessionEnded': 'ההתחברות הסתיימה. יש להתחבר מחדש.',
  'fe.jobDone': 'העבודה נסגרה.',
  'fe.notOnCrew': 'אינך משובץ לעבודה הזו.',
  'fe.codeExpired': 'הקוד אינו בתוקף עוד. יש לבקש קוד חדש ממנהל השירות.',
  'fe.dayClosed': 'היום {date} נסגר בידי {by} — הוא לקריאה בלבד. רק מנהל ההתקנות יכול לתקן אותו: פנו אליו.',
  'fe.dayFuture': 'היום {date} עוד לא הגיע — מדווחים רק על יום שכבר הגיע. בחרו את היום או יום קודם שעוד פתוח.',
  'fe.jobLocked': 'העבודה נסגרה — כל הימים שלה לקריאה בלבד. צריך לתקן? פנו למנהל ההתקנות: הוא פותח אותה מחדש בכרטיס ההתקנה.',
  'fe.departureOtherDay': 'שעת היציאה אינה ביום {date} — בחרו את השעה מהרשימה ביום הזה, או עברו ליום הנכון בבורר הימים.',
  'fe.hoursOverlap': 'השעות חופפות לדיווח ב-{projectNumber} ({range}). באותה שעה עובדים בעבודה אחת — תקנו את השעה.',
  'fe.previousStillOpen': '{projectNumber} עדיין פתוח אצלך. עברו לדיווח של {projectNumber}, רשמו שם עזיבה וחזרה, ואז התחילו כאן.',
  'fe.switchNeedsCode': 'הטלפון עוד לא מוכר — צאו והיכנסו עם קוד העבודה של הפרויקט השני.',
  'fe.crewReportedItself': 'העובד כבר דיווח את היום בעצמו — הוא רושם את שעת הסיום שלו.',
  'fe.crewNoDeparture': 'קודם רשמו את שעת היציאה שלכם — אז אפשר לסמן מי סיים מוקדם.',
  'fe.crewNotWithYou': 'העובד לא מסומן איתכם היום — סמנו אותו קודם.',
  'fe.dayReportedByOther': 'את היום שלך רשם חבר צוות. רוצה לדווח בעצמך? רשום שעה ביום שלך.',
  'fe.dayRemoved': 'היום הזה הוסר מההתקנה. עבדתם היום? בקשו ממנהל ההתקנות להחזיר אותו.',
  'fe.dayInvalid': 'נתונים לא חוקיים',
  'fe.dayIncomplete': 'לא ניתן לסגור את היום — {parts}',
  'fe.odometerBackwards': 'הקילומטר בחזרה נמוך מהקילומטר ביציאה',
  'fe.returnBeforeDeparture': 'שעת החזרה למפעל מוקדמת משעת היציאה',
  'fe.notOnDayCrew': 'העובד אינו בצוות של היום. מנהל ההתקנות מוסיף אותו ב„ערוך יום".',
  'fe.notInCrewToday': 'אינך בצוות של הפרויקט הזה היום — רעננו את המסך, או בקשו ממנהל ההתקנות לשבץ אתכם',
  'fe.badFieldRequest': 'בקשה לא חוקית — רעננו את המסך',
  'fe.badEndTime': 'השעה לא תקינה — לחצו „סיים עכשיו" או בחרו שעה',
  'fe.endBeforeDeparture': 'שעת הסיום מוקדמת משעת היציאה — בחרו שעה אחרי היציאה',
  'fe.endBeforeArrival': 'שעת הסיום ({end}) לפני ההגעה לאתר ({arrived}). מי שלא הגיע איתכם לאתר — הורידו את הסימון שלו („לא היה איתי").',
  'fe.dayTooLong': 'שעת החזרה רחוקה מדי משעת היציאה — יום שטח אחד אינו יכול לעלות על 24 שעות. יש לתקן את השעה או את התאריך.',
  'fe.naReasonRequired': 'כתוב בקצרה למה זה לא רלוונטי — למשל „הלקוח כבר החליף בעצמו".',
  'fe.naAlreadyDone': 'הפריט כבר בוצע — אין מה לסמן „לא רלוונטי".',
  'fe.naCheckFailed': 'בדיקה שנכשלה אינה „לא רלוונטית" — יש לתקן ולבדוק שוב, או לפתוח פער.',
  'fe.futureTime': 'השעה שנבחרה עוד לא הגיעה — מדווחים מה שכבר קרה. בחרו שעה עד עכשיו, או לחצו „עכשיו" כשזה קורה.',
  'fe.taskNotFound': 'משימה לא נמצאה',
  'fe.checkNotFound': 'בדיקה לא נמצאה',
  'fe.equipmentNotFound': 'פריט לא נמצא',
  'fe.documentNotFound': 'מסמך לא נמצא',
  'fe.photoNotFound': 'תמונה לא נמצאה',
  'fe.photoRemoved': 'התמונה הוסרה או שאינה שייכת לעבודה הזו.',
  'fe.checkNoteRequired': 'בדיקה שנכשלה חייבת הסבר — מה נמצא',
  'fe.noFile': 'לא נבחר קובץ',
  'fe.alreadySigned': 'נציג הלקוח כבר חתם על ההתקנה הזו',
  'fe.signatureRequired': 'נדרשת חתימה עם שם החותם, או סיבה לכך שהלקוח לא חתם',
  'fe.signatureMissing': 'אין חתימה',
  'fe.jobIncomplete': 'לא ניתן לסגור את ההתקנה — {parts}',
  'fe.jobNotStarted': 'העבודה מתחילה ב-{date} — אפשר לסמן מה לקחת, אבל יציאה, קילומטר, משימות וסגירה רק מיום העבודה',
  'fe.regulationNotAcked': 'יש לאשר את תקנון עבודת השטח לפני דיווח',
  'fe.regulationNone': 'לא פורסם תקנון',
  'fe.badRequest': 'בקשה לא חוקית',
  'fe.badFileType': 'הקובץ אינו תמונת JPEG / PNG / WebP',
  'fe.serverError': 'שגיאת מערכת',
  'fe.rateLimited': 'יותר מדי ניסיונות — נסה שוב בעוד מספר דקות',
  // ── תקלות תעבורה, 27/09/2026 ──
  //
  // מאז שהעלאות השטח יוצאות כ-`ApiError` (ולא כ-`Error` גולמי), הקודים
  // האלה מגיעים ל-`fieldErrorText` — וללא מפתח, הטכנאי הרוסי היה מקבל את
  // המשפט העברי של `lib/api.ts`. „רענן עם Ctrl+F5" גם אינו הוראה לטלפון.
  'fe.network': 'לא נשלח — אין חיבור לשרת. בדוק קליטה ונסה שוב.',
  'fe.staleClient': 'האפליקציה מריצה גרסה ישנה. סגור אותה, פתח מחדש ונסה שוב.',
  'fe.fileTooLarge': 'התמונה גדולה מדי לשליחה. צלם אותה מחדש ונסה שוב.',
  'fe.gapInvalid': 'בחר סוג פער וכתוב תיאור',
  'fe.gapTypeInvalid': 'בחר סוג פער מהרשימה.',
  'fe.gapStageInvalid': 'פער נפתח ב-FAT, במשלוח או בהתקנה.',
  'fe.gapSideInvalid': 'בחר צד אחראי: ייצור, הנדסה, רכש או ניהול פרויקט.',
  'fe.gapDescriptionShort': 'תאר את הפער — מה חסר או מה לא תקין.',
  'fe.gapProjectNotFound': 'הפרויקט לא נמצא. רענן את הכרטיס.',
  'fe.gapPartNotInProject': 'החלק שנבחר אינו בפרויקט הזה. בחר חלק מהרשימה, או השאר ריק.',
  'fe.gapJobMismatch': 'עבודת ההתקנה אינה של הפרויקט הזה. רענן את הכרטיס.',
  'fe.gapNoRecipient': 'אין מי שיטפל בפער: אין {side} פעיל ואין מנהל התקנות פעיל במערכת. פנה למנכ"ל להגדרת משתמשים.',
  'fe.gapCauseRequired': 'בחר את סיבת התקלה — ייצור, תכנון, התקנה, חלק של ספק, בלאי או שימוש לא נכון',
  'fe.gapCauseInvalid': 'בחר סיבה מהרשימה: ייצור, תכנון, התקנה, חלק של ספק, בלאי או שימוש לא נכון.',
  'fe.repairInvalid': 'בדוק את הבקשה — מה נמצא, ושעות כמספר (או ריק)',
  'fe.repairFindingShort': 'כתוב מה מצאת — מה התקלה ומה צריך לתקן',
  'fe.repairNotServiceCall': 'בקשת תיקון במקום נשלחת רק מעבודת שירות על קריאה',
  'fe.repairCallClosed': 'הקריאה כבר נסגרה במשרד — התקשר למנהלת השירות',
  'fe.repairNotPaid': 'הקריאה אינה בתשלום — באחריות או בחוזה מתקנים בלי לבקש אישור',
  'fe.repairAlreadyDecided': 'מנהלת השירות כבר החליטה על הקריאה — ההחלטה מופיעה בכרטיס העבודה',
  'fe.repairPendingExists': 'כבר נשלחה בקשת תיקון על הקריאה הזו — ממתינה לתשובת מנהלת השירות',
  'fe.side.PRODUCTION': 'ייצור',
  'fe.side.ENGINEERING': 'הנדסה',
  'fe.side.PURCHASING': 'רכש',
  'fe.side.PM': 'ניהול פרויקט',
  'fe.forbidden': 'אין הרשאה',
  'fe.sessionExpired': 'תוקף ההתחברות פג, יש להתחבר מחדש',
  'fe.visitItemInvalid': 'נתוני השורה אינם תקינים — רענן את הדף ונסה שוב',
  'fe.visitHoursInvalid': 'שעות המונה לא תקינות — כתבו את המספר שעל מונה המכונה (0 עד 1,000,000)',
  'fe.visitCancelled': 'הביקור בוטל — רשמו את שעות המונה בכרטיס המערכת',
  'fe.visitPhotoInvalid': 'התמונה אינה תקינה — צלם שוב',
  'fe.visitItemNotFound': 'השורה לא נמצאה — רענן את הדף',
  'fe.visitClosed': 'הביקור נסגר — אי אפשר לשנות שורה בדוח שכבר נחתם',
  'fe.visitAwaitingSignature': 'הדוח נשלח ללקוח לחתימה — אי אפשר לשנות בו שורות. לתיקון פנו למנהל השירות',
  'fe.visitAlreadyClosed': 'הביקור כבר נסגר — רענן את הדף כדי לראות את הדוח החתום',
  'fe.visitNotFound': 'הביקור לא נמצא — ייתכן שבוטל. חזור לרשימת הביקורים שלך',
  'fe.visitPhotoNotFound': 'אין תמונה לשורה הזו',
  'fe.visitSignatureNotFound': 'אין חתימה לביקור הזה',
  'fe.visitCallInvalid': 'יש לתאר מה נמצא ולבחור דחיפות',
  'fe.visitCallExists': 'כבר נפתחה קריאה מהשורה הזו',
  'fe.visitSignatureRequired': 'חסרים שם החותם או החתימה',
  'fe.visitPartNeedsPhoto': 'החלפת חלף מחייבת תמונה — צלם את מה שהוחלף לפני השמירה',
  'fe.visitPartNeedsCause': 'בחר למה החלף הוחלף — ייצור, תכנון, התקנה, חלק של ספק, בלאי או שימוש לא נכון',
  'fe.visitPartBadCause': 'סיבת ההחלפה לא מהרשימה — בחר אחת מהאפשרויות',
  'fe.visitQtyWithoutPart': 'צוינה כמות בלי חלף — בחר איזה חלף הוחלף',
  'fe.visitDocNotFound': 'המסמך לא נמצא — בקשו מהמשרד לבדוק אותו בספריית הדגם',
  'fe.visitPartNotInModel': 'החלף שנבחר אינו של הדגם הזה — רענן ובחר מהרשימה',
  'fe.visitNeedsPartInvalid': 'בחרו חלף מהרשימה וכמות של 1 לפחות',
  'fe.visitNoModel': 'למערכת הזו אין דגם בספריית הידע, ולכן אין לה תוכנית אחזקה. בקש ממנהל השירות לשייך דגם, או פתח קריאת שירות.',
  'fe.visitItemsPending': '{n} שורות עדיין בלי תשובה. סמן בוצע או לא בוצע בכל שורה לפני החתימה.',
  'fe.qrUnknown': 'המדבקה אינה מוכרת — צלם אותה שוב, או פנה למנהל השירות',
  'fe.badgeNotThisPhone': 'הטלפון הזה אינו מוכר לתג הזה — היכנסו פעם אחת עם קוד העבודה והתג',
  'fe.badgeIncomplete': 'יש לסרוק תג או להקליד מספר עובד',
  'fe.jobNotOpen': 'העבודה נסגרה או שאינך משובץ לה — רעננו את הרשימה',
  'fe.jobHasNoVisit': 'לעבודה הזו אין ביקור אחזקה',
  'fe.imageNotFound': 'התמונה לא נמצאה',
  'fe.deviceForgotten': 'הטלפון אינו מוכר עוד — היכנסו שוב עם קוד העבודה והתג.',
  'field.regNoTranslation': 'התקנון עדיין לא תורגם לשפה שלך — מוצג בעברית. בקש מהמנהל להסביר אותו לפני האישור.',
  'field.whereTitle': 'מה ואיפה',
  'field.call.title': 'קריאת שירות',
  'field.call.reported': 'מה הלקוח דיווח',
  'field.call.system': 'מערכת',
  'field.call.serial': 'מס׳ סידורי',
  'field.call.photo': 'התמונה שהלקוח צירף',
  'field.call.sev.PRODUCTION_STOPPED': 'עוצר ייצור',
  'field.call.sev.MACHINE_DOWN': 'מכונה מושבתת',
  'field.call.sev.NO_URGENCY': 'ללא דחיפות',
  'field.overnight': 'לנים הלילה במלון — לא חוזרים למפעל',
  'field.arrivedHotel': 'שעת הגעה למלון',
  'field.leftHotel': 'שעת יציאה מהמלון',
  'field.scanBadge': 'סרוק את ה-QR שעל כרטיס העובד',
  'field.navigate': 'נווט לאתר (Waze)',
  'field.regVersion': 'גרסה {v}',
  'field.regDeclare': 'קראתי את התקנון, הבנתי אותו, ואני מתחייב לפעול לפיו.',
  'field.regAccept': 'אני מאשר ומתחיל לעבוד',
  'field.noPhotoSite': 'אתר ללא צילום',
  'field.noPhotoWithoutPermit': 'אין לצלם באתר הזה ללא אישור צילום מהלקוח. אם אין אישור — פנה למנהל הפרויקט.',
  'field.photoPermitOn': 'התקבל אישור צילום מהלקוח: {note}. צלם רק את מה שהאישור מתיר.',
  'field.staffEntry': 'אתה מחובר למערכת ומשובץ לצוות — בחר עבודה, בלי קוד:',
  // ── טלפון מוכר: תג בלבד (U-1009-08) ──
  'field.badgeHello': 'שלום {name} — סרקו את התג',
  'field.badgeHint': 'סורק התג מקליד את המספר כאן. אפשר גם להקליד את מספר העובד ולהקיש Enter.',
  'field.useCode': 'כניסה עם קוד עבודה',
  'field.notMe': 'לא אני',
  'field.backToBadge': 'חזרה לסריקת תג',
  // ── „המשימות שלי" (אלי 16/10/2026, U-1016-02 שלב ב׳) ──
  'field.hub.hello': 'שלום {name}',
  'field.hub.title': 'המשימות שלי',
  'field.hub.loadFailed': 'לא הצלחנו לטעון את המשימות — בדקו קליטה ונסו שוב.',
  'field.hub.retry': 'נסו שוב',
  'field.hub.empty': 'אין כרגע משהו שמחכה לך',
  'field.hub.emptyBody': 'משימות, בדיקות איכות ו-FAT שמחכים לך יופיעו כאן.',
  'field.hub.floorTasks': 'משימות שלי',
  'field.hub.officeTasks': 'משימות משרד',
  'field.hub.qa': 'בדיקות איכות שמחכות לי',
  'field.hub.fatTitle': 'FAT',
  'field.hub.shipTitle': 'משלוח',
  'field.hub.fatEngSub': 'הייצור סימן — ממתין לאישור ההנדסה',
  'field.hub.fatMgrSub': 'ממתין לסימון מנהל הייצור',
  'field.hub.fromPhoneNote': 'כל אישור מכאן נרשם בשמך עם „אושר מהטלפון".',
  'field.hub.due': 'עד {d}',
  'field.hub.overdue': 'באיחור · {d}',
  'field.hub.done': 'בוצע',
  'field.hub.pass': 'תקין',
  'field.hub.fail': 'לא תקין',
  'field.hub.na': 'לא רלוונטי',
  'field.hub.confirm': 'מאשר',
  'field.hub.reject': 'לא מאשר',
  'field.hub.cancel': 'ביטול',
  'field.hub.failWhat': 'מה נמצא ומה צריך לתקן?',
  'field.hub.rejectWhat': 'מה לא תקין ומה הייצור צריך לבדוק שוב?',
  'field.hub.value': 'ערך נמדד',
  'field.hub.prodPass': 'תקין · {name}',
  'field.hub.prodNa': 'לא רלוונטי · {name}',
  'field.pickJob': 'העבודות הפתוחות שלך — בחר עבודה:',
  'field.noOpenJobs': 'אין לך עבודה פתוחה',
  'field.noOpenJobsBody': 'אם הגעת לעבוד — פנה למנהל השירות שישבץ אותך.',
  'field.kind.INSTALLATION': 'התקנה',
  'field.kind.SERVICE': 'שירות',
  'field.kind.MAINTENANCE': 'אחזקה',
  'field.visit.title': 'צ׳קליסט אחזקה',
  'field.visit.open': 'פתח את הצ׳קליסט',
  'field.visit.view': 'צפה בצ׳קליסט',
  'field.onSiteToday': 'באתר היום',
  'field.dayCrew': 'צוות היום',
  'field.dayRemoved': 'היום הוסר בידי {by}: {reason}. עבדתם היום? בקשו להחזיר אותו.',
  'field.taskForDay': 'ליום {date}',
  'field.fixInCard': 'פתח את כרטיס ההתקנה',
  'field.dayLocked': 'היום נסגר',
  'field.closeDay': 'סגירת יום העבודה',
  'field.dayStillMissing': 'כדי לסגור את היום חסר עדיין:',
  // I2 (אלי 30/10/2026): רשימה עם קפיצה לשדה, והכפתור תמיד גלוי.
  'field.closeDayMissingTitle': 'חסר כדי לסגור יום',
  'field.goToField': 'עבור לשדה',
  'field.closeDayLockedWhy': 'הכפתור ייפתח כשהרשימה תתרוקן · נשארו: {n}',
  'field.closeDayWarning': 'סגירת היום היא סופית — אחריה לא ניתן לשנות, ורק המנהל יכול לתקן.',
  'field.closeDayAction': 'סגור את היום',
  'field.closeDayConfirm': 'לסגור את היום {date}? {hours} · {km}. נסגר גם לשאר הצוות שלא דיווחו בעצמם. אחרי הסגירה רק המנהל יכול לתקן.',
  'field.closeDayYes': 'כן, סגור את היום',
  'field.closeDayNo': 'עוד לא',
  'field.naAsk': 'לא רלוונטי…',
  'field.naReason': 'למה? למשל: הלקוח כבר החליף בעצמו',
  'field.naMark': 'סמן לא רלוונטי',
  'field.naShown': 'לא רלוונטי · {by}: {reason}',
  'field.naUndo': 'החזר',
  'field.dayClosedAt': 'היום נסגר בשעה {time}',
  'field.dayChangeViaManager': 'לשינוי יש לפנות למנהל.',
  'field.dayOpen': 'לא נסגר',
  'field.hoursShort': 'ש׳',
  'field.kmShort': 'ק״מ',
  'field.dates': 'תאריכי העבודה',
  'field.site': 'אתר',
  'field.address': 'כתובת',
  'field.contact': 'איש קשר',
  'field.permits': 'הסמכות נדרשות',
  // ── שרטוטים וקבצים, לצוות שעומד מול המכונה ──────────────────────────────
  // אלי, 27/09/2026: „הצוות בחוץ צריך לראות את התמונות האלה." הרשימה הייתה
  // קיימת בלי כותרת ובלי מפתח — כלומר בלי שם בשום שפה.
  // וההודעה הריקה אומרת מה לעשות ולאן ללכת, ולא „אין".
  'field.drawings': 'מסמכים ושרטוטים',
  'field.noDrawings': 'לא צורפו שרטוטים לעבודה הזו. בקש מהמשרד לסמן אותם „גלוי לצוות בשטח" בתיק הפרויקט.',
  'field.equipment': 'ציוד לקחת',
  'field.eqStock': 'במלאי {n}',
  'field.eqNoStock': 'אין במלאי',
  'field.eqNotInCatalog': 'לא בקטלוג',
  'field.eqInMachine': 'במכונה {n}',
  'field.eqQty': '× {n}',
  // U-1010-06 — הכלים מהמחסן שאצל העובד (כרטיס השטח והעמדה)
  'field.myTools': 'הכלים שאצלך מהמחסן',
  'tools.temp': 'זמני',
  'tools.perm': 'קבוע',
  'tools.late': 'לא הוחזר בזמן',
  'tools.returnAtCounter': 'ההחזרה — בדלפק המחסן.',
  'field.departure': 'יציאה מהמפעל',
  'field.leftPlant': 'שעת יציאה מהמפעל',
  'field.crewWithTitle': 'מי איתי היום',
  'field.crewWithHint': 'כולם מסומנים: מה שתרשום נרשם גם להם, באותן שעות. מי שלא היה איתך — הורד את הסימון.',
  'field.crewSelf': 'דיווח בעצמו',
  'field.crewByOther': 'נרשם בידי {name}',
  'field.crewOverlap': 'בשעות האלה בפרויקט {n} — לא נרשם כאן',
  'field.crewLeftEarly': 'סיים מוקדם · עכשיו',
  'field.crewEndAt': 'סיים ב-{time}',
  'field.crewEndUndo': 'בטל',
  'field.crewEndClamped': '{name}: נרשם סיום ב-{time} — הסיום שלך. „עכשיו" מאוחר ממנו, וסיום מוקדם אינו אחרי הסיום שלך. סיים עוד קודם? מנהל ההתקנות מתקן ב„תקן שעות".',
  'field.crewNotWith': 'לא היה איתי',
  'field.crewNeedsDeparture': 'אחרי שתרשום יציאה אפשר לסמן מי סיים מוקדם.',
  'field.reportedBy': '{name} רשם את היום שלך — אותן שעות{hours}. עבדת בשעות אחרות? רשום כאן את השעות שלך, ומאז הן שלך.',
  'field.otherJobsToday': 'היום יש לך עוד פרויקט',
  'field.otherOpenJobs': 'עוד התקנות פתוחות שלך',
  'field.dayPicker': 'ימי העבודה',
  'field.dayToday': 'היום',
  'field.dayNotToday': 'לא היום',
  'field.dayStateOpen': 'פתוח',
  'field.dayStateClosed': 'נסגר',
  'field.dayStateReadOnly': 'לקריאה',
  'field.dayFuture': 'עוד לא הגיע',
  'field.dayClosedByShort': 'נסגר בידי {by}',
  'field.dayClosedBy': 'היום {date} נסגר בידי {by} — לקריאה בלבד. רק מנהל ההתקנות יכול לתקן אותו.',
  'field.pastDayOpen': 'אתם מדווחים על {date}, יום קודם שעוד לא נסגר. בחרו את השעות מהרשימות — „עכשיו" רק ביום של היום.',
  'field.jobClosedReadOnly': 'העבודה נסגרה — הכול לקריאה בלבד. לתיקון פנו למנהל ההתקנות.',
  'field.switchJob': 'עבור לדיווח של {n}',
  'field.multiDayHint': 'כל פרויקט מדווח בנפרד, עם השעות שלו. בפרויקט השני „יציאה" היא מהאתר הקודם.',
  'field.sharedDay': '{name} דיווח את היום — השעות שלך נרשמו לפי הדיווח שלו ({hours} ש׳). לא צריך להזין. עבדת בשעות אחרות או במקום אחר? הזן את השעות שלך כאן למטה.',
  'field.odoOut': 'קילומטר ביציאה',
  'field.arrival': 'הגעה לאתר',
  'field.arrivedSite': 'שעת הגעה לאתר',
  'field.tasks': 'משימות',
  'field.noTasks': 'לא הוגדרו משימות לעבודה הזו.',
  // ⚠ הערה, לא שגיאה: מנהל השירות רשם את השורה בשפה אחת בלבד, והיא
  // מוצגת כפי שנכתבה. מוטב טקסט בשפה הלא-נכונה מאשר שורה ריקה — שורה
  // ריקה ברשימת המשימות היא משימה שלא תבוצע.
  'field.untranslated': 'ללא תרגום — מוצג בשפה שבה נרשם',
  // ⚠ המקור נערך אחרי שהתרגום נכתב. הטקסט עדיין מוצג — שדה ריק גרוע
  // מהכול — אבל הטכנאי חייב לדעת שהוא עלול לא לתאר את מה שנרשם עכשיו.
  'field.translationStale': 'ההוראה עודכנה — התרגום עדיין הישן. בדוק מול מנהל השירות',
  'field.checks': 'בדיקות סופיות',
  'field.noChecks': 'לא הוגדרו בדיקות לעבודה הזו.',
  'field.photos': 'תמונות',
  'field.addPhoto': 'צלם או העלה תמונה',
  'field.return': 'חזרה',
  'field.leftSite': 'שעת יציאה מהאתר',
  'field.returned': 'שעת חזרה למפעל',
  'field.odoBack': 'קילומטר בחזרה',
  'field.km': 'נסועה',
  'field.refuelIls': 'תדלוק (₪)',
  'field.refuelLitres': 'תדלוק (ליטר)',
  'field.hoursGross': 'שעות ברוטו (יציאה עד חזרה)',
  'field.dayNote': 'הערות ליום העבודה',
  'field.close': 'סגירת ההתקנה',
  // הליכה 12/10/2026 (D/E) — שטח + עמדה.
  'field.closeTitle.INSTALLATION': 'סגירת ההתקנה',
  'field.closeTitle.VISIT': 'סגירת הביקור',
  'field.closeTitle.CALL': 'סגירת העבודה',
  'field.complete.INSTALLATION': 'ההתקנה הושלמה',
  'field.complete.VISIT': 'הביקור הושלם',
  'field.complete.CALL': 'העבודה הושלמה',
  'field.closedKind.INSTALLATION': 'ההתקנה נסגרה. תודה.',
  'field.closedKind.VISIT': 'הביקור נסגר. תודה.',
  'field.closedKind.CALL': 'העבודה נסגרה. תודה.',
  'field.kind.VISIT': 'ביקור אחזקה',
  'field.kind.CALL': 'עבודת שירות',
  'field.reqType.FAULT': 'תקלה',
  'field.reqType.MAINTENANCE': 'אחזקה',
  'field.reqType.DAY_WORK': 'עבודה לפי יומית / שעות',
  'field.reqType.BUILD': 'ייצור / שיפוץ',
  'field.officeNote': 'הערת משרד',
  'field.notStarted': 'מתחיל ב-{date}',
  'field.notStartedBody': 'אפשר כבר עכשיו לסמן מה לקחת. יציאה, קילומטר, משימות וסגירה — רק מיום העבודה.',
  'field.markAll': 'סמן הכל',
  'field.showList': 'הצג את הרשימה',
  'field.hideList': 'הסתר את הרשימה',
  'field.closeEquipmentConfirm': '{n} פריטים לא סומנו — לסגור בכל זאת?',
  'field.eqGroup.PARTS': 'חלקים (מק״ט)',
  'field.eqGroup.KIT': 'כלים וחומרים',
  'field.eqGroup.MANUAL': 'נוסף ביד',
  'field.visitSignInChecklist': 'חתימת הלקוח, או „אין נציג באתר“ עם סיבה — בסוף הצ׳קליסט.',
  'field.stillMissing': 'כדי לסגור את ההתקנה חסר עדיין:',
  'fb.CLOSE_OPEN_GAPS': '{n} פערים פתוחים — לסגור, או להחתים את הלקוח „התקבל עם פערים"',
  'field.gaps.title': 'פערים',
  'field.gaps.hint': 'חסר חלק, חלק פגום, לא מתאים לשרטוט, חסר פריט רכש או מסמך, או בקשת לקוח — פתח פער. הוא נשלח מיד למי שמטפל, והתשובה תופיע כאן.',
  'field.gaps.open': 'פתח פער',
  'field.gaps.type': 'סוג הפער',
  'field.gaps.type.MISSING_PART': 'חלק חסר',
  'field.gaps.type.DAMAGED_PART': 'חלק פגום',
  'field.gaps.type.DOESNT_FIT_DRAWING': 'לא מתאים לשרטוט',
  'field.gaps.type.MISSING_PURCHASE': 'פריט רכש חסר',
  'field.gaps.type.MISSING_DOC': 'מסמך חסר',
  'field.gaps.type.CUSTOMER_REQUEST': 'בקשת לקוח',
  'field.gaps.description': 'מה קרה',
  'field.gaps.descriptionExample': 'למשל: חסרים 4 ברגי עיגון M12',
  'field.gaps.part': 'חלק (לא חובה)',
  'field.gaps.noPart': 'בלי חלק',
  'field.gaps.photo': 'צילום (לא חובה)',
  'field.gaps.send': 'שלח',
  'field.gaps.sentTo': 'נשלח אל',
  'field.gaps.none': 'אין פערים פתוחים.',
  'field.gaps.answer': 'תשובה',
  'field.gaps.status.OPEN': 'פתוח',
  'field.gaps.status.IN_PROGRESS': 'בטיפול',
  'field.gaps.status.RESOLVED': 'נפתר',
  'field.gaps.status.ACCEPTED_BY_CUSTOMER': 'התקבל עם פערים',
  'field.gaps.failed': 'הפער לא נשלח. בדוק קליטה ונסה שוב.',
  'field.gaps.acceptedWithGaps': 'הלקוח מקבל את ההתקנה עם הפערים הפתוחים',
  'field.gaps.acceptedWithGapsHint': 'הפערים יישארו פתוחים לטיפול, ולא יעצרו את סגירת ההתקנה.',
  'field.gaps.cause': 'סיבה',
  'field.gaps.causeOptional': 'סיבה (רשות)',
  'field.gaps.titleService': 'ממצאים',
  'field.repair.title': 'בקשת תיקון במקום',
  'field.repair.hint': 'קריאה בתשלום: לפני שמתקנים — כתוב מה מצאת ומה אתה מציע. מנהלת השירות מאשרת או מבקשת הצעת מחיר, והתשובה תופיע כאן.',
  'field.repair.open': 'בקש לתקן במקום',
  'field.repair.finding': 'מה מצאת',
  'field.repair.findingExample': 'לדוגמה: מנוע ההנעה שרוף, צריך להחליף',
  'field.repair.hours': 'הערכת שעות',
  'field.repair.parts': 'חלקים נדרשים (שורה לכל חלק)',
  'field.repair.photo': 'צילום (רשות)',
  'field.repair.send': 'שלח למנהלת השירות',
  'field.repair.failed': 'הבקשה לא נשלחה. בדוק קליטה ונסה שוב.',
  'field.repair.status.PENDING': 'ממתין לתשובת מנהלת השירות',
  'field.repair.status.APPROVED': 'אושר — לתקן במקום',
  'field.repair.status.REJECTED': 'נדחה — צריך הצעת מחיר ללקוח. לא לתקן עכשיו.',
  'field.repair.decision.REPAIR_NOW': 'החלטה: תיקון במקום',
  'field.repair.decision.QUOTE': 'החלטה: צריך הצעת מחיר לפני תיקון',
  'field.repair.answerNote': 'הערת המנהלת',
  'field.repair.hoursShort': 'שעות',
  'field.markComplete': 'ההתקנה הושלמה',
  'field.closed': 'ההתקנה נסגרה. תודה.',
  'field.now': 'עכשיו',
  'field.pass': 'תקין',
  'field.fail': 'לא תקין',
  'field.whatWasFound': 'מה נמצא?',
  'field.saveFail': 'שמור ליקוי',
  // ── ביקורי אחזקה מונעת (MyVisitsPage, VisitPage) ─────────────────────
  // הספירות מנוסחות „תווית: מספר" ולא „N פעולות": ל-heCount יש שתי צורות
  // עבריות, ולרוסית שלוש (1 · 2-4 · 5+). מספר שנושא את עצמו נכון בשתיהן.
  'fv.myVisits': 'הביקורים שלי',
  'fv.myVisitsSub': 'אחזקה מונעת אצל לקוחות',
  'fv.pendingToSend': 'ממתין לשליחה: {n}',
  'fv.pending': 'ממתין: {n}',
  'fv.noOpenVisits': 'אין ביקורים פתוחים',
  'fv.noOpenVisitsBody': 'המשרד משבץ את הביקורים. כשישובץ לך אחד — הוא יופיע כאן.',
  'fv.loadVisitsFailed': 'לא ניתן לטעון את הביקורים',
  'fv.loadVisitFailed': 'לא ניתן לטעון את הביקור',
  'fv.visitNotFound': 'הביקור לא נמצא',
  'fv.saveRejected': 'השמירה נדחתה',
  'fv.closeFailed': 'הסגירה נכשלה',
  'fv.qrFailed': 'המדבקה לא נקראה',
  'fv.qrFailedBody': 'ייתכן שהמדבקה הוחלפה או שהמכונה נמחקה. פנה למשרד השירות.',
  'fv.serialNo': 'מס׳ סידורי',
  'fv.noOpenVisitForMachine': 'אין ביקור פתוח על המכונה הזו',
  'fv.noOpenVisitForMachineBody': 'המשרד משבץ ביקורים. אם הגעת לטפל — בקש שיפתחו לך אחד.',
  'fv.machineJobs': 'העבודות הפתוחות שלך על המערכת הזו:',
  'fv.noJobOnMachine': 'אין לך עבודה פתוחה על המערכת הזו — פנה למנהל השירות',
  'fv.toFieldApp': 'לעבודת שטח',
  'fv.backToCard': 'חזרה לכרטיס העבודה',
  'fv.result.DONE': 'בוצע',
  'fv.result.NOT_DONE': 'לא בוצע',  'fv.history': 'היסטוריה',
  'fv.priorVisits': 'טיפולים קודמים: {n}',
  'fv.neverServiced': 'המכונה הזו מעולם לא טופלה במערכת.',
  'fv.service': 'טיפול {period}',
  'fv.officeNote': 'מהמשרד',
  'fv.period.DAILY': 'יומי',
  'fv.period.BIWEEKLY': 'דו-שבועי',
  'fv.period.SEMIANNUAL': 'חצי-שנתי',
  'fv.period.ANNUAL': 'שנתי',
  'fv.period.BIENNIAL': 'דו-שנתי',
  'fv.period.HOURS': 'לפי שעות עבודה',
  'fv.period.CUSTOM': 'לפי היקף ההזמנה',
  'fv.answered': 'נענו',
  'fv.offline': 'אין קליטה — הכול נשמר במכשיר',
  'fv.backOnline': 'הקליטה חזרה, שולח…',
  'fv.beforeAnyWork': 'לפני כל עבודה',
  'fv.visitClosed': 'הביקור נסגר',
  'fv.signedBy': 'נחתם על ידי {name}',
  'fv.oneRowLeft': 'נותרה שורה אחת בלי תשובה',
  'fv.rowsLeft': 'נותרו {n} שורות בלי תשובה',
  'fv.notDoneIsValid': '„לא בוצע“ הוא תשובה תקפה. דוח שנחתם עם שורות ריקות לא יעזור כשיתעורר ויכוח על אחריות.',
  'fv.zoomImageOf': 'הגדל את תמונת ההמחשה של {action}',
  'fv.zoom': 'הגדל',
  'fv.techsRequired': 'טכנאים חובה: {n}',
  'fv.partReplaced': 'הוחלף חלף?',
  'fv.notReplaced': 'לא הוחלף',
  'fv.howMany': 'כמה',
  'fv.howManyUnits': 'כמה יחידות הוחלפו',
  'fv.whyReplaced': 'למה הוחלף?',
  'fv.chooseCause': 'בחר סיבה',
  'fv.causeRequired': 'בחר למה החלף הוחלף — בלי זה השורה לא נשמרת',
  'fv.cause.MANUFACTURING': 'ייצור',
  'fv.cause.DESIGN': 'תכנון',
  'fv.cause.INSTALLATION': 'התקנה',
  'fv.cause.SUPPLIER': 'חלק של ספק',
  'fv.cause.WEAR': 'בלאי',
  'fv.cause.MISUSE': 'שימוש לא נכון',
  'fv.photoRequired': 'החלפת חלף — חובה לצלם לפני השמירה',
  'fv.note': 'הערה',
  'fv.notePlaceholder': 'מה ראית, מה נשאר לטפל',
  'fv.photoAgain': 'צלם שוב',
  'fv.docs': 'מסמכים',
  'fv.docsHint': 'חוברות ושרטוטים של הדגם — נפתחים בטלפון',
  'fv.manualPage': 'מדריך · עמ׳ {n}',
  'fv.photoMust': 'צלם — חובה',
  'fv.photoMustMissing': 'חסר צילום חובה בפעולה הזו — צלם עכשיו. אפשר להמשיך, ובדוח למשרד יופיע „חסר צילום".',
  // „ב-1 פעולות" → „בפעולה אחת" דרך _one (סיור לילה 13/10/2026).
  'fv.photoMissingBeforeSign': 'ב-{n} פעולות חסר צילום חובה. אפשר לחתום — בכרטיס הקריאה במשרד יופיע „חסר צילום". להמשיך לחתימה?',
  'fv.photoMissingBeforeSign_one': 'בפעולה אחת חסר צילום חובה. אפשר לחתום — בכרטיס הקריאה במשרד יופיע „חסר צילום". להמשיך לחתימה?',
  'fv.photoMissingBeforeSign_many': 'ב-{n} פעולות חסר צילום חובה. אפשר לחתום — בכרטיס הקריאה במשרד יופיע „חסר צילום". להמשיך לחתימה?',
  'fv.startsOnLocked': 'מתחיל ב-{date} — סגירה רק מיום העבודה',
  'fv.needsPart': 'דורש חלק',
  'fv.needsPartPick': 'איזה חלף צריך?',
  'fv.needsPartChoose': 'בחר חלף',
  'fv.needsPartSave': 'שלח למשרד',
  'fv.needsPartSent': 'נשלח למשרד: {part} × {n}',
  'fv.needsPartHint': 'המשרד מקבל טיוטת הצעת חלקים לאישור מנהלת השירות. ללקוח לא נשלח דבר.',
  'fv.needsPartRemove': 'בטל סימון',
  'fv.needsPartFailed': 'הסימון „דורש חלק" לא נשמר — נסה שוב',
  'fv.photo': 'צלם',
  'fv.photoForRow': 'צלם תמונה לשורה',
  'fv.photoTaken': 'התמונה שצולמה',
  'fv.photoRemove': 'מחק את התמונה',
  'fv.photoRemoveConfirm': 'למחוק את התמונה?',
  'fv.photoRemoveFailed': 'מחיקת התמונה נכשלה',
  'fv.callOpened': 'נפתחה קריאה',
  'fv.openCall': 'פתח קריאה',
  'fv.answeredAt': 'נענה {time}',
  'fv.closePhoto': 'סגור את התמונה',
  'fv.anyTouchCloses': 'נגיעה כלשהי סוגרת',
  'fv.urgProductionStopped': 'עוצר ייצור',
  'fv.urgMachineDown': 'מכונה מושבתת',
  'fv.urgNone': 'ללא דחיפות',
  'fv.openServiceCall': 'פתיחת קריאת שירות',
  'fv.onAction': 'בפעולה: {action}',
  'fv.urgency': 'דחיפות',
  'fv.whatFound': 'מה נמצא *',
  'fv.whatFoundPlaceholder': 'בלאי שיניים חמור בגלגל ההינע, נדרשת החלפה',
  'fv.openCallFailed': 'פתיחת הקריאה נכשלה',
  'fv.callCarriesContext': 'הקריאה נפתחת עם המכונה, המספר הסידורי, הפעולה וההערה שלך — כדי שבמשרד לא יצטרכו לשאול מה בדיוק נמצא.',
  'fv.finishAndSign': 'סיום וחתימת הלקוח',
  'pv.back': '→ חזרה להזמנות',
  'pv.title': 'דוח ביקור אחזקה — לחתימתכם',
  'pv.loading': 'טוען את הדוח…',
  'pv.system': 'מערכת',
  'pv.serial': 'מספר סידורי',
  'pv.site': 'אתר',
  'pv.date': 'תאריך הביקור',
  'pv.service': 'טיפול',
  'pv.technician': 'טכנאי',
  'pv.action': 'פעולה',
  'pv.result': 'תוצאה',
  'pv.replaced': 'הוחלף',
  'pv.note': 'הערת הטכנאי',
  'pv.done': 'בוצע',
  'pv.notDone': 'לא בוצע',
  'pv.pending': 'לא נענה',
  'pv.signedBy': 'הדוח נחתם על ידי {name}',
  'pv.signIntro': 'לא היה באתר נציג שיחתום על הדוח. אם הדוח נכון — חתמו כאן. אם משהו אינו נכון, השיבו למייל שקיבלתם ונבדוק לפני החתימה.',
  'pv.signButton': 'חתימה על הדוח',
  'pv.signSubmit': 'חתום ואשר את הדוח',
  'pv.signHint': 'החתימה מאשרת שהפעולות בדוח בוצעו כפי שתוארו.',
  'pv.notAwaiting': 'הדוח הזה אינו ממתין לחתימה. לשאלות — פנו אלינו.',
  'fv.noRep': 'אין נציג באתר',
  'fv.noRepConfirm': 'אין באתר נציג שיחתום? הדוח יינעל, ומנהל השירות ישלח אותו ללקוח לחתימה בפורטל. אפשר לסגור את העבודה.',
  'fv.noRepWhy': 'למה אין נציג שיחתום? (למשל: הנציג יצא לפני הסיום)',
  'fv.noRepSend': 'שלח לחתימה מרחוק',
  'fv.noRepHint': 'הדוח יינעל, ומנהל השירות ישלח אותו ללקוח לחתימה בפורטל.',
  'fv.noRepFailed': 'הסימון „אין נציג באתר" נכשל',
  'fv.meterHours': 'שעות עבודה במונה המכונה',
  'fv.meterHoursHint': 'המספר שעל המונה עכשיו — ממנו נקבע מתי מגיע הטיפול הבא לפי שעות',
  'fv.meterSaveFailed': 'שמירת שעות המונה נכשלה — בדקו את המספר ונסו שוב',
  'fv.awaitingSignature': 'ממתין לחתימת לקוח',
  'fv.awaitingSignatureBody': 'לא היה נציג באתר. מנהל השירות ישלח את הדוח ללקוח לחתימה בפורטל — השורות נעולות. אפשר לסגור את העבודה.',
  'fv.hours': '{n} ש׳',
  'fv.minutes': '{n} דק׳',

  // ── laser board ──
  'laser.tabQueue': 'ממתין לחיתוך',
  'laser.tabHistory': 'מה נחתך',
  'laser.tabMissing': 'שרטוטים חסרים',
  'laser.queueEmpty': 'אין כרגע חלקים שממתינים לחיתוך בלייזר.',
  'laser.historyEmpty': 'עוד לא דווח חיתוך בלייזר.',
  'laser.missingEmpty': 'כל מה שנחתך מולא מול שרטוט במערכת.',
  'laser.missingNote': 'נחתכו בלייזר ואין להם שרטוט במערכת. ההנדסה קיבלה התראה; השורה נסגרת מעצמה כשהשרטוט עולה.',
  'laser.unknownMaterial': 'ל-{n} מק״טים עוד לא ידוע החומר והעובי — הם נלמדים משם הקובץ בחיתוך הראשון.',
  'laser.unknownMaterial_one': "למק״ט אחד עוד לא ידוע החומר והעובי — הוא נלמד משם הקובץ בחיתוך הראשון.",
  'laser.unknownMaterial_many': "ל-{n} מק״טים עוד לא ידוע החומר והעובי — הם נלמדים משם הקובץ בחיתוך הראשון.",
  'laser.material': 'חומר',
  'laser.materialThickness': 'חומר / עובי',
  'laser.thickness': 'עובי',
  'laser.sheetWeight': 'משקל פח',
  'laser.partsWeight': 'משקל חלקים',
  'laser.yield': 'ניצולת',
  'laser.operator': 'מפעיל',
  'laser.delivery': 'אספקה',
  'laser.unknownYet': 'טרם ידוע',
  'laser.revisedCheckNests': 'עלתה רוויזיה {rev} — בדוק נסטים מתוכנתים',
  'laser.toCut': 'לחיתוך',
  'laser.waiting': 'ממתין',
  'laser.parts': 'חלקים',
  'laser.projects': 'פרויקטים',
  'laser.skus': 'מק״טים',
  'laser.noDrawing': 'ללא שרטוט',
  'laser.outOf': 'מתוך {n}',
  'laser.mm': 'מ״מ',
  'laser.kg': 'ק״ג',
  'laser.groupLine': "{parts} · {units} · {projects}",
  'laser.partsN': "{n} מק״טים",
  'laser.partsN_one': "מק״ט אחד",
  'laser.partsN_many': "{n} מק״טים",
  'laser.unitsN': "{n} יחידות",
  'laser.unitsN_one': "יחידה אחת",
  'laser.unitsN_many': "{n} יחידות",
  'laser.projectsN': "{n} פרויקטים",
  'laser.projectsN_one': "פרויקט אחד",
  'laser.projectsN_many': "{n} פרויקטים",
  // ── הדפסת מדבקות ──
  'laser.tabLabels': 'הדפסת מדבקות',
  'laser.labelsPickUpload': 'בחר קובץ שהועלה',
  'laser.labelsSearchSku': 'או חפש מק״ט',
  'laser.labelsSearchPlaceholder': 'מק״ט — הקלד או סרוק',
  'laser.labelsEmpty': 'אין שורות להדפסה. בחר קובץ מהרשימה, או חפש מק״ט.',
  'laser.labelsSize': 'גודל מדבקה',
  'laser.labelsCount': 'כמה מדבקות',
  'laser.labelsPrint': 'הדפס',
  // ⚠ הסף נכתב ולא מקובע בטקסט: הוא הגדרה מ-22/09/2026, ו„חצי ק״ג" קבוע
  // במשפט היה משקר על המסך ברגע שהמשרד יזיז אותו.
  'laser.labelsHeavyHint': 'מעל {kg} ק״ג ליחידה — מוצע להדפיס מדבקה לכל חלק',
  'laser.labelsBulkHint': 'מעל {kg} ק״ג ליחידה — מדבקה לכל יחידה, תמיד',
  'laser.labelsOneWithQty': 'מדבקה אחת, ועליה הכמות',
  'laser.labelsPerPart': 'מדבקה לכל חלק, ממוספרת',
  'laser.labelsQtyOnLabel': 'כמות: {n}',
  'laser.labelsIndexOnLabel': 'מדבקה {i}/{n}',
  'laser.labelsNestOnLabel': 'בחיתוך {pos}',

  // ── laser board — table headers ──
  'laser.sku': 'מק״ט',
  'laser.date': 'תאריך',

  // ── חיפוש בכל חלון (אלי, 22/09/2026) ──
  'laser.searchPlaceholder': 'חיפוש — מק״ט, שם או חומר',
  'laser.searchClear': 'נקה חיפוש',
  'laser.searchCount': 'מוצגים {shown} מתוך {total}',
  'laser.searchNoMatch': 'אין שורה שמתאימה לחיפוש.',
  'laser.searchQueuePlaceholder': 'חפש בתור — מק״ט, פרויקט, לקוח או חומר',
  'laser.searchHistoryPlaceholder': 'חפש בחיתוכים — קובץ, חומר, מפעיל או פרויקט',
  'laser.searchMissingPlaceholder': 'חפש בשרטוטים החסרים — מק״ט, פרויקט או סיבה',
  'laser.searchLinesPlaceholder': 'חפש בשורות הקובץ — מק״ט או שם',
  'laser.searchNestPlaceholder': 'חפש בחלקי הניסט — מק״ט או שם',

  // ── להיכנס לניסט (אלי, 22/09/2026) ──
  'laser.nestOpen': 'פתח את הניסט וראה את החלקים',
  'laser.nestClose': 'סגור את הניסט',
  'laser.nestEmpty': 'לא נשמרו שורות חלקים לחיתוך הזה.',
  'laser.nestPartsCount': '{n} שורות על הפח',
  'laser.nestMissingCount': '{n} ללא שרטוט',
  'laser.unitWeight': 'משקל יחידה',
  'laser.size': 'גודל (מ״מ)',

  // ── „איך אני יודע אם העלו את השרטוטים?" ──
  // „שרטוט קיים" = יש לחלק גרסה פעילה במערכת, כלומר PDF שהועלה בהנדסה.
  // „נדרש" = אין גרסה, ויש בקשת שרטוט פתוחה. „חסר" = אין גרסה ואיש לא נשאל.
  'laser.drawing': 'שרטוט',
  'laser.drawingOk': 'שרטוט קיים',
  'laser.drawingOkVersion': 'שרטוט קיים · גרסה {n}',
  'laser.drawingArrivedAfter': 'הועלה ב-{d}, אחרי החיתוך',
  'laser.drawingRequested': 'נדרש שרטוט — נפתחה בקשה ב-{d}',
  'laser.drawingMissing': 'אין שרטוט, ולא נפתחה בקשה',

  // ── laser board — rows ──
  'laser.lateBy': '(באיחור {n})',
  'laser.waitingDays': '{n} ימים',

  // ── saw station ──
  // `saw.angle` / `saw.fromSquare` / `saw.headTravel` / `saw.expected` /
  // `saw.other` הוסרו 17/09/2026: הם נולדו כשברי משפט — אחד מהם נגמר
  // בסוגר פותח — ואיש לא חיבר אותם אף פעם, ולכן הטקסט נשאר קשיח במסך.
  // המשפטים השלמים הם עכשיו `saw.overRangeAngle` ו-`saw.overRangeComplement`.
  'saw.backToBar': 'חזרה למוט',
  'saw.nextBar': 'המוט הבא',
  'saw.idleLogoutHint': 'השעון עצר. בלי לחיצה תוך 5 דקות — העמדה מתנתקת, והבא ממשיך מכאן.',
  'saw.profile': 'פרופיל',
  'saw.length': 'אורך',
  'saw.total': 'סה״כ',
  'saw.queueEmpty': 'התור ריק.',
  'saw.table': 'טבלה',
  'saw.list': 'רשימה',
  'saw.noResults': 'אין תוצאות לחיפוש הזה.',
  'saw.onSaw': 'על המסור',
  'saw.remnantSku': 'שארית {sku}',
  'cut.straight': 'ישר',
  'cut.piece': 'החתיכה',
  'cut.barRest': 'המשך המוט',
  'cut.longFace': 'הפאה הארוכה — לפי זה מודדים',
  'cut.short': '(קצרה)',
  'cut.whatComesOut': 'מה צריך לצאת',
  'cut.bothInward': 'שתי הזוויות מצוירות פנימה — ודא מול השרטוט',
  'cut.notStated': 'לא צוין',
  'cut.inFile': 'בקובץ',
  'cut.without': 'בלי',
  'cut.toRotate': 'לסובב',
  'cut.rotateThe': 'סובב את',
  'cut.theProfile': 'הפרופיל',
  'cut.bothEndsFlat': 'שני הקצוות ישרים',
  'cut.headZero': 'ראש המסור על 0°. אין סיבוב ואין שיפוע.',
  'cut.noRotationCol': 'רשימת החיתוך הזאת לא כללה עמודת סיבוב, ולכן',
  'cut.unknown': 'לא ידוע',
  'cut.aroundAxis': 'סביב צירו.',
  'cut.dimsOnBar': 'מידות על המוט הזה',
  'cut.mm': 'מ״מ',
  'cut.first': 'ראשון',
  'cut.second': 'שני',
  'cut.remnantShelf': 'שארית מהמדף',
  'cut.wholeBar': 'מוט שלם',
  'cutrep.uncut': 'לא נחתך',
  'cutrep.uncutTitle': 'לא נחתך — החזר לתור',
  'cutrep.uncutHint': 'החתיכות חוזרות לתור המסור והפרופיל מתוכנן מחדש. נרשם מי החזיר, למה ומתי סומנו.',
  'cutrep.uncutHowMany': 'כמה לא נחתכו? (עד {max})',
  'cutrep.uncutWhy': 'למה (חובה)',
  'cutrep.uncutWhyPh': 'למשל: סומן נחתך ולא נחתך בפועל',
  'cutrep.uncutGo': 'החזר {n} לתור',
  'cutrep.uncutStage': 'החתיכות חזרו לתור. שלב המסגרות של החלק לא הוחזר אוטומטית — תקן בדיווח החלק.',
  'cutrep.title': 'דוח חיתוך',
  'cutrep.search': 'חפש פרויקט, פרופיל, מק״ט, שרטוט, עובד או אורך',
  'cutrep.exportCsv': 'ייצוא CSV',
  'cutrep.pieces': 'חתיכות',
  'cutrep.metres': 'מ׳',
  'cutrep.unpriced': 'לא מתומחר',
  'cutrep.cost': 'עלות',
  'cutrep.lastCut': 'נחתך לאחרונה',
  'cutrep.projName': 'שם פרויקט',
  'cutrep.sku': 'מק"ט',
  'cutrep.lengthMm': 'אורך מ"מ',
  'cutrep.totalMm': 'סה"כ מ"מ',
  'cutrep.costIls': 'עלות ₪',
  'cutrep.noPricing': 'ללא תמחור',
  'cutrep.fileName': 'דוח-חיתוך.csv',
  'cutrep.emptyAll': 'עוד לא נחתך חומר במסור.',
  'cutrep.emptyProject': 'עוד לא נחתך חומר לפרויקט הזה במסור.',
  'cutrep.projectTitle': 'חומר שנחתך במסור',
  'cutrep.projectSearch': 'חפש פרופיל, מק״ט, שרטוט או אורך',
  'cutrep.drawing': 'שרטוט',

  // ── vehicle kiosk ──
  'veh.title': 'דיווח נסיעה',
  'veh.exit': 'יציאה',
  'veh.preCheck': 'בדיקה לפני נסיעה',
  'veh.postCheck': 'בדיקה אחרי נסיעה',
  'veh.whatFound': 'מה נמצא?',
  'veh.siteOrCity': 'שם האתר או העיר',
  'veh.notes': 'הערות',
  'veh.unknownCode': 'הקוד שנסרק אינו מוכר, או שהרכב אינו פעיל בצי. פנה למנהל לקבלת מדבקה.',
  'veh.enterNumber': 'הזן את מספר העובד שלך כדי לדווח.',
  'veh.enter': 'כניסה',
  'veh.lastKm': 'ק״מ אחרון שנרשם:',
  'veh.noChecklist': 'לא הוגדרה רשימת בדיקה.',
  'veh.ok': 'תקין',
  'veh.notOk': 'לא תקין',
  'veh.odometer': 'קילומטראז׳',
  'veh.failNeedsNote': 'בדיקה שסומנה ״לא תקין״ חייבת הסבר.',
  'veh.trip': 'נסיעה',
  'veh.openTrips': 'נסיעות פתוחות שלך ברכב הזה:',
  'veh.newTrip': 'נסיעה חדשה במקום',
  'veh.purpose': 'מטרת הנסיעה',
  'veh.pickPurpose': 'בחר מטרה…',
  'veh.destination': 'יעד',
  'veh.kmOut': 'ק״מ ביציאה',
  'veh.kmBack': 'ק״מ בחזרה',
  'veh.fuelIls': 'תדלוק ₪',
  'veh.fuelLitres': 'תדלוק ליטר',
  'veh.distance': 'נסועה:',
  'veh.km': 'ק״מ',
  'veh.closeTrip': 'סגור נסיעה',
  'veh.closeNeeds': 'סגירה דורשת מטרה וקילומטר בשני הקצוות.',
  'veh.cannotEnter': 'לא ניתן להיכנס',
  'veh.insuranceExpired': 'הביטוח פג תוקף',
  'veh.testExpired': 'הטסט פג תוקף',
  'veh.checkSaved': 'הבדיקה נשמרה.',
  'veh.saveFailed': 'השמירה נכשלה',
  'veh.doneToday': '· בוצעה היום',
  'veh.updateCheck': 'עדכן את הבדיקה',
  'veh.saveCheck': 'שמור בדיקה',
  'veh.tripClosed': 'הנסיעה נסגרה. תודה.',
  'veh.tripSaved': 'הנסיעה נשמרה ונשארה פתוחה. בחזרה סרקו שוב את המדבקה, מלאו קילומטר בחזרה ולחצו על סגירת נסיעה.',
  'veh.othersOpen': 'נסיעות פתוחות של נהגים אחרים ברכב הזה:',
  'veh.reportForThem': 'דווח חזרה במקום {name}',
  'veh.onBehalfHint': 'הנסיעה נשארת של {name}, ושמך נרשם כמי שדיווח. מלאו קילומטר בחזרה, ודלק אם תדלקתם.',
  'veh.confirmLow': 'זה מה שרשום ברכב',
  'veh.outAtTime': '· יציאה {time}',
  'veh.noPurpose': 'ללא מטרה',
  'veh.requiredForPurpose': '(חובה למטרה הזו)',
  'veh.optional': '(לא חובה)',
  'veh.save': 'שמור',
  // סבב שני (אלי 02/11/2026): סריקת תג במצלמה, טיוטה, תדלוק, התקנה, חודשי
  'veh.scanTitle': 'סרקו את התג שלכם',
  'veh.scanHint': 'כוונו את המצלמה ל-QR שעל תג העובד. בפעם הבאה הטלפון יזכור אתכם.',
  'veh.scanButton': 'סריקת התג במצלמה',
  'veh.identifying': 'מזהה…',
  'veh.notMe': 'לא אני',
  'veh.refuelled': 'הרכב תודלק',
  'veh.refuelNeeds': 'סימנתם תדלוק — כתבו ליטרים וסכום.',
  'veh.draftSaved': 'נשמר כטיוטה ב-{time}. אפשר לסגור את הטלפון — הטיוטה מחכה עד שתסגרו את הנסיעה.',
  'veh.draftLoaded': 'המילוי האחרון נטען (טיוטה מ-{time}).',
  'veh.draftFailed': 'הטיוטה לא נשמרה — בדקו את החיבור. מה שהקלדתם עדיין במסך.',
  'veh.installTitle': 'נוסעים להתקנה?',
  'veh.installHint': 'נסיעה להתקנה מדווחת ביום ההתקנה: הרכב, הקילומטראז ביציאה ובחזרה והתדלוק — במקום אחד, בלי לרשום פעמיים.',
  'veh.installGo': 'מעבר ליום ההתקנה',
  'veh.hoursOut': 'שעות מנוע ביציאה',
  'veh.hoursBack': 'שעות מנוע בחזרה',
  'veh.lastHours': 'שעות מנוע אחרונות שנרשמו:',
  'veh.hours': 'שעות',
  'veh.kmOutWas': 'ביציאה: {km}',
  'veh.monthTitle': 'דיווח מונה חודשי',
  'veh.monthHint': 'הרכב הזה מדווח פעם בחודש. רשמו את הקילומטראז שעל המונה עכשיו.',
  'veh.monthSave': 'שמור את המונה',
  'veh.monthDone': 'המונה של החודש נרשם: {km}{by}. תיקון נעשה במשרד.',
  'veh.monthSaved': 'המונה נשמר. תודה.',
  'veh.byName': ' · {name}',

  // ── remnant station ──
  'rem.title': 'עמדת שאריות — חומר גלם לחיתוך',
  'rem.searchProfile': 'חיפוש פרופיל',
  'rem.shelfExample': 'למשל: מדף 3',
  'rem.searchAny': 'חיפוש לפי מק״ט שארית או פרופיל',
  'rem.switchWorker': 'החלף עובד',
  'rem.addRemnant': 'הכנסת שארית',
  'rem.managerOnlyNote': 'רשימה לקריאה בלבד. שארית מהחיתוך שלך נרשמת כשסוגרים את המוט במסור. הוספה מהמדף, מדידה, גריטה ו„חסרה" — אצל מנהל הייצור.',
  'rem.whatOnShelf': 'מה יש במדף',
  'rem.scanBadge': 'סרוק תג עובד',
  'rem.tooShort': 'קצר מדי — לגרוטאות',
  'rem.writtenOnShelf': 'רשום במדף',
  'rem.markOnMaterial': 'סמן על החומר:',
  'rem.anotherRemnant': 'שארית נוספת',
  'rem.fromThisCut': 'נשאר מחיתוך עכשיו',
  'rem.foundUnlisted': 'נמצא במדף, לא רשום',
  'rem.whichProfile': 'איזה פרופיל',
  'rem.change': 'החלף',
  'rem.noProfiles': 'לא הוגדרו פרופילים — מנהל הייצור מגדיר בהגדרות',
  'rem.measuredLength': 'אורך שנמדד (מ״מ)',
  'rem.measureReal': 'מדוד בפועל — לא לפי מה שהיה אמור לצאת.',
  'rem.endAngle': 'זווית בקצה (0 = ישר)',
  'rem.angleNote': 'קצה משופע ניתן לניצול מלא רק אם החיתוך הבא באותה זווית.',
  'rem.shelfPos': 'מיקום במדף (רשות)',
  'rem.none': 'אין שאריות רשומות.',
  'rem.notOnShelf': 'לא נמצא במדף',
  'rem.fixLength': 'תקן אורך',
  'rem.notFoundNote': '«לא נמצא במדף» לא עוצר אותך — קח מוט שלם והמשך. מנהל הייצור מקבל התראה.',
  'rem.workerNotFound': 'עובד לא נמצא',
  'rem.error': 'שגיאה',
  'rem.minNotSet': '· שארית מינ׳ טרם הוגדרה',
  'rem.manual': 'נרשם ידנית',
  'rem.fromCut': 'משארית חיתוך',

  // ── warehouse issue ──
  'wh.scanItem': 'סרוק פריט',
  'wh.unknownBarcode': 'הברקוד הזה לא מוכר',
  'wh.searchSku': 'חפש מק״ט או תיאור',
  'wh.cancel': 'ביטול',
  'wh.unbindBtn': 'זה לא החלק הזה — נתק ברקוד',
  'wh.unbindConfirm': 'לנתק את הברקוד מ-{sku}? הסריקה הבאה לא תזהה אותו.',
  'wh.unbindDone': 'הברקוד נותק. סרוק שוב כדי לקשר אותו נכון.',
  'wh.location': 'מיקום',
  // ⚠ „מאיפה לקחת?" ולא „הזן מיקום מדף": השאלה הראשונה היא שאלה שהמחסנאי
  // יודע לענות עליה בלי לחשוב; השנייה היא משימה של מישהו אחר.
  'wh.whereFrom': 'מאיפה לקחת? (לא חובה — יעזור לבא אחריך)',
  'wh.whereFromHint': 'מדף C-8 · ארגז כחול · מדף עליון ליד הדלת',
  'wh.saveLocation': 'שמור',
  'wh.locationNotSaved': 'המיקום לא נשמר',
  'wh.inStock': 'במלאי',
  'wh.minimum': 'מינימום',
  'wh.supplier': 'ספק',
  'wh.belowMin': 'מתחת למינימום — להזמין',
  'wh.needBadgeFirst': 'צריך לסרוק תג קודם — קישור נרשם על שם מי שעשה אותו.',
  'wh.noMatch': 'לא נמצא מק״ט מתאים. אפשר לסגור ולהמשיך בשם חופשי — קישור אפשר לעשות אחר כך.',
  'wh.notNow': 'לא עכשיו',
  'wh.scanFailed': 'הסריקה נכשלה',
  'wh.badgeBeforePhoto': 'צריך להעביר תג לפני צילום',
  'wh.uploadError': 'שגיאה בהעלאת התמונה',
  'wh.uploading': 'מעלה…',
  'wh.retakePhoto': 'צלם מחדש',
  // „צולם בטעות" — אלי, 20/09/2026
  'wh.deletePhoto': 'מחק את התמונה',
  'wh.deletePhotoConfirm': 'למחוק את התמונה מהמק״ט הזה?',
  'wh.takePhoto': 'צלם את הפריט',
  'wh.notLinked': 'לא נקשר',

  // ── vehicle kiosk — trip line ──
  'veh.leftAt': '· יצא ב-{km}',

  // ── remnant station — spec line ──
  'rem.bar': 'מוט {len}',
  'rem.minRem': 'שארית מינ׳ {len}',
  'rem.onShelfN': 'במדף {n}',

  // ── units ──
  'common.metres': 'מ׳',
  'common.millimetres': 'מ״מ',

  // ── רכיבי בת משותפים ─────────────────────────────────────────────────
  // כל מה שמתחת לכאן נוסף 21/09/2026: רכיבים תחת components/ שמרונדרים
  // בתוך מסכים מתורגמים ומעולם לא עברו תרגום בעצמם. ראו
  // edal-i18n-child-components.

  // ── באנר גרסה ישנה ──
  'build.newVersion': 'גרסה חדשה של המערכת פורסמה.',
  'build.stillOld': 'המסך הזה עדיין מריץ את הגרסה הקודמת',
  'build.reloadNow': 'רענן עכשיו',
  'build.notNow': 'לא עכשיו',

  // ── ui ──
  'ui.filterNoValues': '{label} — אין עדיין ערכים לסינון',
  'ui.help': 'מה מוצג כאן?',
  'ui.helpAria': 'מה מוצג כאן? {title}',
  'ui.helpHow': 'כדי שיופיעו נתונים:',
  'ui.helpSource': 'מאיפה המידע מגיע:',

  // ── מיתוג ──
  'brand.backToApp': 'חזרה למערכת (Esc)',
  'brand.exit': 'יציאה',
  'brand.tagline': 'מערכת ניהול ייצור',
  'brand.company': 'אדאל — פרויקטי רובוטיקה ואוטומציה',

  // ── מדבקות תהליך ──
  'lbl.printOne': 'הדפס מדבקה',
  'lbl.printMany': 'הדפס {n} מדבקות',
  // ── פעולות מנהל בתפעול חי (components/operations/actions) ──
  'common.unexpectedError': 'שגיאה לא צפויה',
  'common.saving': 'שומר…',
  'opsa.pickWorker': 'יש לבחור עובד',
  'opsa.pickStage': 'יש לבחור שלב',
  'opsa.pickRoute': 'יש לבחור מסלול',
  'opsa.badUnits': 'יש להזין כמות יחידות חוקית (0 ומעלה)',
  'opsa.badMinutes': 'יש להזין כמות דקות חוקית',
  'opsa.badQuantity': 'יש להזין כמות חוקית',
  'opsa.needsReason': 'יש לציין סיבה',
  'opsa.stopNeedsReason': 'יש לציין סיבה לסגירה',
  'opsa.editNeedsReason': 'יש לציין סיבה לתיקון',
  'opsa.stage': 'שלב',
  'opsa.stageColon': 'שלב:',
  'opsa.stageN': 'שלב {n}',
  'opsa.stageOptional': 'שלב (אופציונלי)',
  'opsa.chooseStage': '— בחר שלב —',
  'opsa.chooseWorker': '— בחר עובד —',
  'opsa.autoByDept': '— אוטומטי לפי מחלקה —',
  'opsa.units': 'כמות יחידות',
  'opsa.unitsShort': 'יח׳',
  'opsa.reason': 'סיבה',
  'opsa.reasonRequired': 'סיבה *',
  'opsa.startTitle': 'התחל סשן',
  'opsa.starting': 'מתחיל…',
  'opsa.start': 'התחל',
  'opsa.stopTitle': 'סגור סשן',
  'opsa.stopping': 'סוגר…',
  'opsa.zeroUnitsNote': '0 — נסגר בלי שהושלמה יחידה; הזמן עדיין נרשם.',
  'opsa.stopReasonPlaceholder': 'למשל: העובד עזב את המשמרת והשעון נשאר פתוח',
  'opsa.completeTitle': 'סמן שלב כהושלם',
  'opsa.completeNote': 'פעולה זו תסמן את כל יחידות השלב כהושלמו.',
  'opsa.overrideCode': 'קוד מנהל לסימון למרות החלקים החסרים',
  'opsa.overrideHint': 'העקיפה נרשמת עם שם בעל הקוד והחלקים שחסרו, ובעל הקוד מקבל הודעה.',
  'opsa.workMinutes': 'זמן עבודה (דקות)',
  'opsa.minutesShort': 'דק׳',
  'opsa.minutesPlaceholder': 'דקות',
  'opsa.clockRunning': 'רץ שעון על השלב —',
  'opsa.zeroClockWarn': '⚠ השעון רץ פחות מדקה — יירשם 0 דקות. בטוח?',
  'opsa.confirmOrEdit': 'אשר את הזמן הזה, או ערוך אותו אם הוא אינו נכון.',
  'opsa.confirmAndComplete': 'אשר זמן וסמן כהושלם',
  'opsa.confirmTimeFirst': 'אשר או ערוך את הזמן שהשעון מדד, ואז סמן כהושלם.',
  'opsa.noClockBlocked': 'אין שעון רץ על השלב — לכן צריך להקליד כמה דקות עבודה נדרשו. בלי זמן אין תקן.',
  'opsa.minutesRequired': 'חסר זמן עבודה. הקלד דקות; אם זו הערכה — סמן „זמן מוערך".',
  'opsa.estimatedTime': 'זמן מוערך',
  'opsa.estimatedHint': 'לא נמדד בשעון. יסומן כאומדן בכל דוח, כדי שלא יזהם את תקני הזמן.',
  'opsa.completing': 'מסמן…',
  'opsa.complete': 'סמן כהושלם',
  'opsa.editTimeTitle': 'ערוך זמן שלב',
  'opsa.currentTime': 'זמן נוכחי: {n} דקות',
  'opsa.newTotalMinutes': 'סה״כ דקות חדש',
  'opsa.editScanTitle': 'ערוך רשומת סריקה',
  'opsa.zeroScanNote': '0 — הדיווח מתבטל בפועל; הזמן נשאר לפי מה שיוזן בדקות.',
  'opsa.editReason': 'סיבת התיקון',
  'opsa.editReasonPlaceholder': 'למשל: דווח על החלק הלא נכון',
  'opsa.reworkTitle': 'החזרה לעבודה (Rework)',
  'opsa.reworkUnits': 'כמות להחזרה',
  'opsa.reworkReasonPlaceholder': 'תאר את הסיבה להחזרה…',
  'opsa.reworking': 'מבצע…',
  'opsa.reworkSubmit': 'החזר לעבודה',
  'opsa.qtyTitle': 'שינוי כמות',
  'opsa.qtyCurrent': 'הכמות הנוכחית:',
  'opsa.qtyNew': 'כמות חדשה *',
  'opsa.qtyReasonPlaceholder': 'למשל: הלקוח הגדיל את ההזמנה',
  'opsa.qtyMoreWarning': 'יותר יחידות הן יותר חומר — הרכש יקבל התראה. שלבים שכבר סומנו כגמורים בכמות הישנה ייפתחו מחדש.',
  'opsa.qtySubmit': 'שנה כמות',
  'opsa.routeTitle': 'החלף מסלול',
  'opsa.routeChanging': 'מחליף…',
  'opsa.routeChanged': 'המסלול הוחלף',
  'opsa.newRoute': 'מסלול חדש',
  'opsa.chooseRoute': '— בחר מסלול —',
  'opsa.routeConfirm': 'שלבים זהים במסלול החדש (אותה מחלקה ואותו שם) ישמרו את ההתקדמות; שאר השלבים יתאפסו. להמשיך?',
  'opsa.routeConfirmEvict': 'אשר שהסשן הפתוח ייסגר אוטומטית',
  'opsa.routeCarried': 'עבר למסלול החדש:',
  'opsa.routeNothingCarried': 'לא הייתה התקדמות בשלבים זהים להעביר.',
  'opsa.routeReset': 'התאפס (אין שלב מקביל במסלול החדש):',
  'opsa.routeOpenSession': 'סשן פתוח כעת על שלב „{stage}”',
  'opsa.routeEvictNote': 'שינוי המסלול יסגור את הסשן באופן אוטומטי עם 0 יחידות. הזמן שעבר יירשם בהיסטוריה עם תיוג „(החלפת מסלול)”.',
  'opsa.iUnderstand': 'אני מבין/ה ומאשר/ת',
  'opsa.routeHint': 'שלבים זהים במסלול החדש (אותה מחלקה ואותו שם) שומרים את ההתקדמות. שלב שאין לו מקביל מתאפס — ותראה בדיוק מה עבר ומה התאפס.',
  // ── תפעול חי: רכיבי הלוח, הטבלה והמגירה ──
  'ops.kpiWorkersActive': 'עובדים פעילים כעת',
  'ops.kpiWorkersOne': 'עובד אחד',
  'ops.kpiWorkersMany': '{n} עובדים',
  'ops.kpiQuietOne': 'עמדה שקטה אחת',
  'ops.kpiQuietMany': '{n} עמדות שקטות',
  'ops.kpiPartsOnFloor': 'חלקים באולם הייצור',
  'ops.kpiInDepts': 'ב־{n} מחלקות',
  'ops.kpiStuckParts': 'חלקים תקועים',
  'ops.kpiNoneStuck': 'אין חלק שעבר את הסף',
  'ops.kpiOldestOne': 'לא זזו — הוותיק ביותר יום אחד',
  'ops.kpiOldestMany': 'לא זזו — הוותיק ביותר {n} ימים',
  'ops.kpiCompletedToday': 'הושלמו היום',
  'ops.managerActions': 'פעולות מנהל',
  'ops.reworkMenu': 'Rework / החזרה',
  'ops.printQrLabel': 'הדפס מדבקת QR',
  'ops.dMinutes': '{n} ד׳',
  'ops.dHours': '{n} ש׳',
  'ops.dHoursMinutes': '{h}:{m} ש׳',
  'ops.dayTotals': 'סיכום יומי',
  'ops.sinceMorning': 'מהבוקר עד עכשיו',
  'ops.activeWorkers': 'עובדים פעילים',
  'ops.sessionsClosed': 'סשנים נסגרו',
  'ops.workTime': 'זמן עבודה',
  'ops.stageOfTotal': 'שלב {n} מתוך {total}',
  'ops.coworkerOne': 'עובד נוסף',
  'ops.coworkerMany': '{n} עובדים נוספים',
  'ops.floorQuiet': 'שקט בקומה',
  'ops.noActiveSession': 'אין סשן פעיל ברגע זה',
  'ops.floorQuietHint': 'אף עובד לא מבצע סשן פעיל ברגע זה. כשמישהו ייסרוק תג ויתחיל שלב — הלוח יתעדכן אוטומטית.',
  'ops.groupStuck': 'תקועים — דורשים טיפול',
  'ops.groupRunning': 'בעבודה כעת',
  'ops.groupWaiting': 'ממתינים למחלקה',
  'ops.groupDone': 'הושלמו',
  'ops.doneBadge': 'הושלם',
  'ops.waitingDays': '{n} ימים',
  'ops.noResults': 'לא נמצאו תוצאות',
  'ops.bbActive': 'פעיל',
  'ops.bbSessions': 'סשנים',
  'ops.bbTitle': 'תפעול חי',
  'ops.bbFooter': 'ESC כדי לצאת ממסך מלא · הלוח מתעדכן כל 15 שניות',
  'ops.refresh': 'רענן',
  'ops.exitFullscreen': 'צא ממסך מלא',
  'ops.exitFullscreenEsc': 'צא ממסך מלא (ESC)',
  'ops.workerActivity': 'פעילות עובד',
  'ops.noWorkerData': 'לא נמצאו נתונים',
  'ops.noWorkerDataHint': 'ייתכן שהעובד לא פעיל היום',
  'ops.unitsToday': 'יחידות היום',
  'ops.minutesToday': 'דקות היום',
  'ops.sessionsActiveNow': 'סשנים פעילים כעת',
  'ops.sessionsClosedToday': 'סשנים שנסגרו היום',
  'ops.noClosedSessions': 'אין סשנים סגורים היום',
  'ops.helperRole': 'עזר',
  'ops.clickToEditScan': 'לחץ לעריכת רשומה',
  // ── מעטפת האפליקציה: סרגל צד, חיפוש מסך, התראות, אבטחה ──
  'nav.mainNav': 'ניווט ראשי',
  'nav.searchScreen': 'חיפוש מסך',
  'nav.searchScreenEllipsis': 'חיפוש הכל…',
  'nav.searchPlaceholder': 'מה לחפש? מסך, מספר פרויקט, מק״ט, קריאה, ספק או עובד',
  'nav.closeSearch': 'סגור חיפוש',
  'nav.screens': 'מסכים',
  'nav.noScreenFound': 'לא נמצא מסך מתאים או רשומה. נסו מילה אחת, מספר פרויקט (260250), מק״ט, קריאה או שם ספק.',
  // ── חלון החיפוש V1: הדברים עצמם, לא רק מסכים (אלי 30/10/2026) ──
  'find.type.screen': 'מסכים',
  'find.type.project': 'פרויקטים',
  'find.type.part': 'חלקים',
  'find.type.call': 'קריאות שירות',
  'find.type.quote': 'הצעות מחיר',
  'find.type.supplier': 'ספקים',
  'find.type.stock': 'פריטי מלאי',
  'find.type.employee': 'עובדים',
  'find.kind.screen': 'מסך',
  'find.kind.project': 'פרויקט',
  'find.kind.part': 'חלק',
  'find.kind.call': 'קריאת שירות',
  'find.kind.quote': 'הצעת מחיר',
  'find.kind.supplier': 'ספק',
  'find.kind.stock': 'פריט מלאי',
  'find.kind.employee': 'עובד',
  'find.fact.area': 'תחום',
  'find.fact.customer': 'לקוח',
  'find.fact.site': 'אתר',
  'find.fact.delivery': 'אספקה מחויבת',
  'find.fact.project': 'פרויקט',
  'find.fact.qty': 'כמות',
  'find.fact.opened': 'נפתחה',
  'find.fact.created': 'נוצרה',
  'find.fact.validUntil': 'בתוקף עד',
  'find.fact.contact': 'איש קשר',
  'find.fact.phone': 'טלפון',
  'find.fact.email': 'מייל',
  'find.fact.family': 'משפחה',
  'find.fact.onHand': 'במלאי',
  'find.fact.department': 'מחלקה',
  'find.more': 'עוד {n} ב{label}',
  'find.open': 'פתיחה',
  'find.preview': 'תצוגה מקדימה',
  'find.previewEmpty': 'בחרו שורה כדי לראות כאן מה המצב שלה.',
  'find.inactive': 'סגור או לא פעיל',
  'find.loadError': 'החיפוש ברשומות נכשל — המסכים עדיין כאן. נסו שוב בעוד רגע.',
  'find.typesHint': 'מסכים, פרויקטים, חלקים, קריאות, הצעות, ספקים, מלאי, עובדים',
  'nav.favorites': 'מועדפים',
  'nav.pin': 'הוסף לעבודה שלי',
  'nav.unpin': 'הסר מהעבודה שלי',
  'nav.pinAria': 'הוסף את {label} לעבודה שלי',
  'nav.unpinAria': 'הסר את {label} מהעבודה שלי',
  'nav.favoritesFull': 'הרשימה מלאה',
  'nav.myWork': 'העבודה שלי',
  'nav.myWorkHint': 'כוכבים ולפי השימוש',
  'nav.editStars': 'ערוך כוכבים',
  'nav.editStarsDone': 'סיום',
  'nav.yourWork': 'העבודה שלך',
  'nav.mine': 'שלי',
  'nav.search': 'חיפוש',
  'nav.allScreens': 'כל המסכים',
  'nav.recent': 'פתחת לאחרונה',
  'nav.collapse': 'כווץ תפריט',
  'nav.collapseTitle': 'כווץ תפריט — יותר מקום למסך',
  'nav.expand': 'הרחב תפריט',
  'nav.expandAll': 'פתח הכל',
  'nav.collapseAll': 'כווץ הכל',
  'nav.expandAllAria': 'פתח את כל קבוצות התפריט',
  'nav.collapseAllAria': 'כווץ את כל קבוצות התפריט',
  'nav.hintOpen': 'פתיחה',
  'nav.hintMove': 'מעבר',
  'nav.hintClose': 'סגירה',
  'nav.hintPin': 'הכוכב מוסיף ל„העבודה שלי"',
  'nav.openMenu': 'פתח תפריט',
  'nav.closeMenu': 'סגור תפריט',
  'nav.logout': 'התנתק',
  'nav.user': 'משתמש',
  'nav.buildTitle': 'גרסת המערכת שנטענה במסך הזה',

  // ── שמות תפקידים בסרגל ──
  'role.ceo': 'מנכ״ל',
  'role.manager': 'מנהל ייצור',
  'role.engManager': 'מנהל הנדסה',
  'role.serviceMgr': 'מנהל שירות והתקנה',
  'role.purchaser': 'מנהלת רכש',
  'role.vp': 'סמנכ״ל',
  'role.pm': 'מנהל פרויקט',
  'role.accountManager': 'מנהלת אדמיניסטרציה',
  'role.warehouse': 'מחסנאי',
  'role.viewer': 'צופה',
  'role.engineer': 'מהנדס',
  'role.productionWorker': 'עובד ייצור',

  // ── בחירת עמדה (משתמש הרצפה המשותף) ──
  'choose.subtitle': 'בחירת עמדה',
  'choose.title': 'באיזו עמדה עובדים?',
  'choose.hint': 'בוחרים פעם אחת. אפשר לחזור לכאן בכל רגע בכפתור „החלף עמדה" בראש המסך.',
  'choose.station': 'עמדת עבודה',
  'choose.saw': 'מסור',
  'choose.laser': 'לייזר',
  'choose.finishing': 'גמר',
  'choose.remnants': 'שאריות',
  'choose.warehouse': 'מחסן',
  'choose.nc': 'אי-התאמה',
  'kiosk.switchStation': 'החלף עמדה',
  'kiosk.endDay': 'סיום יום עבודה',
  'kiosk.endDayTitle': 'סיום יום עבודה',
  'kiosk.endDayBody': 'כל השעונים הפתוחים שלך ייסגרו. כמה תקינים וכמה פסולים בכל אחד? הקלד 0 אם אין. אחר כך העמדה מתנתקת.',
  'kiosk.endDayConfirm': 'סגור הכול וסיים יום',
  'kiosk.endDayBoardBody': 'כל השעונים שעל הלוח ייסגרו. כמה תקינים וכמה פסולים לכל אחד? הקלד 0 אם אין.',
  'kiosk.endDayConfirmBoard': 'סגור את כל הלוח',
  'kiosk.endDayDone': 'כל השעונים נסגרו. יום טוב!',
  'kiosk.endDayNoClocks': 'לא היו שעונים פתוחים — התנתקת. יום טוב!',
  'kiosk.endDayAsk': 'לעצור את השעון ולהתנתק?',
  'kiosk.endDayYes': 'כן, סיים יום',
  'kiosk.cancel': 'ביטול',
  'saw.endDayDone': 'שעון המוט נעצר ({minutes} דק׳). המוט נשאר לבא. יום טוב!',
  'choose.warehouseLocked': 'דורש כניסה של איש מחסן',
  'wh.needsWarehouseLogin': 'העמדה הזו דורשת כניסה של איש מחסן — התנתק והיכנס עם משתמש מחסן',
  'wh.needsWarehouseLoginAnon': 'העמדה הזו דורשת כניסה של איש מחסן — היכנס עם משתמש מחסן',
  'wh.logoutToLogin': 'התנתק ועבור לכניסה',
  'wh.goToLogin': 'כניסה עם משתמש מחסן',
  'wh.boardError': 'רשימת הליקוט לא נטענה. נסה שוב; אם זה חוזר — דווח למנהל הייצור.',
  'wh.retry': 'נסה שוב',
  'fin.v2SwitchDept': 'החלף מחלקה',

  // ── התראות ──
  'ntf.title': 'התראות',
  'ntf.justNow': 'עכשיו',
  'ntf.agoMinutes': 'לפני {n} דק׳',
  'ntf.agoHours': 'לפני {n} ש׳',
  'ntf.markAllRead': 'סמן הכל כנקרא',
  'ntf.desktopBlocked': 'התראות דסקטופ חסומות בדפדפן — יש לאפשר בהגדרות האתר',
  'ntf.desktopEnable': 'הפעל התראות דסקטופ (קופצות גם כשהמערכת ברקע)',
  // ── התקנה + התראות לפלאפון (אלי 29/09/2026) ──
  'pwa.install': 'התקן את האפליקציה',
  'pwa.installShort': 'התקן',
  'pwa.bannerText': 'התקינו את EDAL MES למסך הבית — פתיחה מהירה והתראות לפלאפון',
  'pwa.dismiss': 'סגור',
  'pwa.guideTitle': 'התקנת האפליקציה',
  'pwa.iosStep1': 'לחצו על כפתור השיתוף בתחתית המסך',
  'pwa.iosStep2': 'בחרו „הוסף למסך הבית"',
  'pwa.iosStep3': 'לחצו „הוסף"',
  'pwa.openInSafari': 'באייפון אפשר להתקין רק מ-Safari: פתחו את הכתובת הזו ב-Safari ואז „הוסף למסך הבית".',
  'pwa.browserMenu': 'פתחו את תפריט הדפדפן (⋮) ובחרו „התקן אפליקציה" או „הוסף למסך הבית".',
  'pwa.push': 'התראות לפלאפון',
  'pwa.on': 'פועל',
  'pwa.off': 'כבוי',
  'pwa.pushDenied': 'ההתראות חסומות בדפדפן — יש לאפשר אותן בהגדרות האתר',
  'pwa.pushUnavailable': 'לא ניתן להפעיל התראות במכשיר הזה כרגע',
  'pwa.pushIosInstall': 'באייפון התראות לפלאפון עובדות רק אחרי התקנת האפליקציה למסך הבית, ופתיחתה משם.',
  'ntf.empty': 'אין התראות',
  'ntf.emailed': 'נשלח במייל',
  'ntf.emailedAt': 'נשלח מייל {when}',
  'ntf.measured': 'נמדד',
  'ntf.andMore': 'ועוד {n} — לחצו לרשימה המלאה',
  'ntf.dismiss': 'סגור התרעה',
  'ntf.details': 'פרטים ({n})',
  'ntf.hideDetails': 'הסתר פרטים',

  // ── דיווח בעיה מתוך המסך ──
  'ri.button': 'דווח בעיה',
  'ri.buttonTitle': 'דווח בעיה במסך הזה',
  'ri.what': 'מה קרה?',
  'ri.describeAria': 'תיאור הבעיה',
  'ri.placeholder': 'לחצתי על „שמור” ולא קרה כלום…',
  'ri.context': 'הדיווח יישלח עם המסך שאתה עומד בו ({path}) וגרסת המערכת — כדי שאפשר יהיה לשחזר בלי לשאול אותך.',
  'ri.send': 'שלח',
  'ri.sent': 'נשלח. תודה.',
  'ri.failed': 'הדיווח לא נשלח',
  'ri.mine': 'הדיווחים שלי',
  'ri.linkedWhat': 'הדיווח שלך',
  'ri.statusOpen': 'פתוח',
  'ri.statusDone': 'טופל',

  // ── אבטחת החשבון ──
  'sec.title': 'אבטחת החשבון',
  'sec.accountSecurity': 'אבטחת החשבון — סיסמה ואימות דו-שלבי',
  'sec.password': 'סיסמה',
  'sec.approvalCode': 'קוד אישור',
  'sec.twoFactor': 'אימות דו-שלבי',
  'sec.changePassword': 'החלפת סיסמה',
  'sec.passwordChanged': 'הסיסמה הוחלפה',
  'sec.passwordChangedNote': 'הסיסמה החדשה פעילה מעכשיו. בכניסה הבאה — גם מהעמדה וגם מהנייד — השתמש בה.',
  'sec.currentPassword': 'הסיסמה הנוכחית',
  'sec.newPassword': 'סיסמה חדשה',
  'sec.confirmPassword': 'אימות הסיסמה החדשה',
  'sec.ruleLength': 'לפחות {n} תווים',
  'sec.ruleLetterDigit': 'אות אחת לפחות וספרה אחת לפחות',
  'sec.passwordsDiffer': 'שתי הסיסמאות אינן זהות',
  'sec.setupFailed': 'ההגדרה נכשלה',
  'sec.codeMismatch': 'הקוד אינו מתאים',
  'sec.disableFailed': 'הכיבוי נכשל',
  'sec.statusUnavailable': 'לא ניתן לטעון את מצב האימות.',
  'sec.onWithCodes': 'פעיל. הכניסה דורשת קוד מהטלפון, ונותרו {n} קודי גיבוי.',
  'sec.offRecommended': 'כבוי. לתפקיד שלך יש גישה לכל הכסף, לכל הלקוחות ולמחיקת פרויקטים — סיסמה שדלפה מספיקה כדי להיכנס.',
  'sec.offOptional': 'כבוי. אפשר להפעיל, ובתפקיד שלך זו אינה דרישה.',
  'sec.scanInApp': 'סרוק באפליקציית האימות',
  'sec.anyAuthApp': 'Google Authenticator, Microsoft Authenticator או Authy — כולן עובדות.',
  'sec.qrAlt': 'קוד QR להגדרת האימות',
  'sec.typeSecret': 'או הקלד את המפתח ידנית, אם המצלמה אינה זמינה',
  'sec.codeNow': 'הקוד שמופיע עכשיו באפליקציה *',
  'sec.enable': 'הפעל',
  'sec.notActiveYet': 'עד שתאשר בקוד — האימות אינו פעיל, והכניסה שלך לא השתנתה.',
  'sec.backupCodesTitle': 'עשרה קודי גיבוי — שמור אותם עכשיו',
  'sec.backupCodesNote': 'כל קוד עובד פעם אחת, והם נשמרים אצלנו מוצפנים בלבד — אין דרך להציג אותם שוב. בלעדיהם טלפון שאבד נועל אותך מחוץ למערכת.',
  'sec.copyAll': 'העתק את כולם',
  'sec.copy': 'העתק',
  'sec.copied': 'הועתק',
  'sec.savedDone': 'שמרתי — סיום',
  'sec.yourPassword': 'הסיסמה שלך — לא קוד מהטלפון',
  'sec.yourPasswordWhy': 'הטלפון כבר בכיס שלך. סיסמה היא מה שמי שיושב מול מסך פתוח אינו יודע.',
  'sec.disableTwoFactor': 'כבה אימות דו-שלבי',
  'sec.enableTwoFactor': 'הפעל אימות דו-שלבי',
  'sec.turnOff': 'כבה',
  // ── כרטיס מחיר, קיוסק NFC, רשימת מחסן ──
  'nk.readFailed': 'הקריאה נכשלה',
  'nk.holdCard': 'הצמד את הכרטיס למכשיר…',
  'pt.noPrice': 'לא נרשם מחיר',
  'pt.history': 'היסטוריה',
  'pt.loadError': 'שגיאת טעינה',
  'pt.noHistory': 'אין רישום מחיר קודם',
  'pt.onePriceOnly': 'מחיר אחד בלבד ברשומות',
  'pt.from': 'מ־',
  'pt.to': 'ל־',
  'pt.range': 'טווח',
  'wl.lessThanHour': 'פחות משעה',
  'wl.hoursN': '{n} שעות',
  'wl.oneDay': 'יום',
  'wl.twoDays': 'יומיים',
  'wl.daysN': '{n} ימים',
  'wl.checkStock': 'לבדוק מלאי',
  'wl.awaitingPurchase': 'ממתין לרכש',
  'wl.toPick': 'ללקט',
  'wl.signInFirst': 'רשימת הליקוט תיטען אחרי שהעמדה תתחבר.',
  'wl.loadFailed': 'לא ניתן לטעון את רשימת הדרישות. זו תקלת טעינה — לא „אין מה ללקט”.',
  'wl.emptyProject': 'אין דרישות פתוחות לפרויקט הזה.',
  'wl.emptyWarehouse': 'אין דרישות פתוחות במחסן.',
  'wl.oldest': 'הוותיק:',
  'wl.inStock': 'במלאי',

  // ── החלטה על חומר לא מתאים (ISO 9001 8.7) ──
  'dsp.choose': 'מה נעשה עם החומר? (חובה)…',
  'dsp.qty': 'כמות יחידות',
  'dsp.qtyLabel': 'כמה יחידות (חובה)',
  'dsp.reasonRequired': 'נימוק (חובה)',
  'dsp.noteOptional': 'הערה (לא חובה)',
  'dsp.useAsIsNote': 'שימוש כמו-שהוא הוא אישור חריגה — שמך יירשם כמי שאישר.',
  'dsp.scrapNote': 'גריטה נרשמת עם הכמות ועם מי שאישר.',
  'dsp.needDisposition': 'יש לקבוע מה נעשה עם החומר',
  'dsp.needReason': 'יש לנמק',
  'dsp.needQty': 'יש לציין כמות',
  'dsp.needRootCause': 'יש לבחור סיבת שורש',
  'dsp.needAction': 'יש לתאר את הפעולה המתקנת',
  'dsp.needOwner': 'יש לקבוע אחראי',
  'dsp.needDueDate': 'יש לקבוע תאריך יעד',

  // ── סגירת אי-התאמה — מסך אחד (NcCloseDialog, 30/10/2026) ──
  'ncc.title': 'סגירת אי-התאמה',
  'ncc.whatToDo': 'מה עושים עם החלק',
  'ncc.kind.REWORK': 'תיקון — חוזר לתחנה',
  'ncc.kind.REPAIR': 'תיקון חריג (לא לפי השרטוט)',
  'ncc.kind.USE_AS_IS': 'שימוש כמו-שהוא',
  'ncc.kind.SCRAP': 'גריטה',
  'ncc.kind.RETURN': 'החזרה לספק',
  'ncc.kind.NOT_APPLICABLE': 'לא רלוונטי',
  'ncc.returnTo': 'חוזר לתחנה',
  'ncc.foundHere': 'כאן נמצא',
  'ncc.foundAt': 'נמצא ב{stage}',
  'ncc.units': '{qty} יח׳',
  'ncc.qty': 'כמה יחידות',
  'ncc.reason': 'נימוק (חובה)',
  'ncc.rootCause': 'סיבת שורש (חובה)',
  'ncc.whatDone': 'מה נעשה (לא חובה)',
  'ncc.action': 'פעולה מתקנת כדי שלא יחזור',
  'ncc.actionNo': 'לא נדרשת',
  'ncc.actionYes': 'נדרשת',
  'ncc.actionWhat': 'מה תהיה הפעולה',
  'ncc.owner': 'אחראי…',
  'ncc.due': 'תאריך יעד',
  'ncc.submit': 'סגור אי-התאמה',
  'ncc.cancel': 'ביטול',
  'ncc.loading': 'טוען את אי-ההתאמה…',
  'ncc.notOpen': 'אי-ההתאמה כבר נסגרה או לא נמצאה. רענן את המסך.',
  'ncc.failed': 'הסגירה נכשלה — נסה שוב',
  'ncc.done': 'נשמר ✓ אי-ההתאמה על {part} נסגרה',
  'ncc.selfClose': 'אתה פתחת את אי-ההתאמה הזאת — את הסגירה עושה אדם אחר (מהנדס או מנהל ייצור).',
  'ncc.outRework': 'החלק יחזור ל{stage} ויעבור שוב את המסלול משם ({qty} יח׳).',
  'ncc.outReworkNoStage': 'החלק יתוקן ויחזור לבדיקה ({qty} יח׳).',
  'ncc.outRepair': 'החלק מתוקן וממשיך במסלול — נרשם כתיקון שאינו לפי השרטוט.',
  'ncc.outUseAsIs': 'החלק ממשיך כמו-שהוא — החריגה נרשמת על שמך.',
  'ncc.outScrap': '{qty} יח׳ נגרטות — נפתחת משימה למנהל הייצור לייצר מחדש.',
  'ncc.outReturn': '{qty} יח׳ חוזרות לספק — נרשם בדו״ח האיכות.',
  'ncc.outNa': 'אי-ההתאמה נסגרת — לא היה חומר לא-מתאים.',
  'ncc.outUnblock': 'החסימה של התחנה הבאה משתחררת.',
  // ── פתיחת אי-התאמה בלחיצה (RaiseNcModal) ──
  'ncr.pickType': 'מה הבעיה? לחיצה פותחת מיד',
  'ncr.complaintWarranty': 'תלונת לקוח: באחריות?',
  'ncr.underWarranty': 'באחריות',
  'ncr.notUnderWarranty': 'לא באחריות',
  'ncr.openComplaint': 'פתח תלונת לקוח',
  'ncr.opened': 'נשמר ✓ נפתחה אי-התאמה על {part}',
  // ── צילום על אי-התאמה (30/10/2026) ──
  'ncr.photoAdd': 'צלם / צרף צילום',
  'ncr.photoMore': 'עוד צילום',
  'ncr.photoRemove': 'הסר צילום',
  'ncr.photoCount': '{n} צילומים יצורפו',
  'ncr.photoOne': 'צילום אחד יצורף',
  'ncr.photoSaving': 'שומר צילום…',
  'ncr.photoSaved': 'נשמר ✓ הצילום צורף',
  'ncr.photoOpen': 'צילום {n} — פתח בגודל מלא',
  // ── אי-התאמה משורת ערכה במחסן ──
  'ncr.lineButton': 'אי-התאמה',
  'ncr.lineTitle': 'פתח אי-התאמה על {item}',
  'ncr.lineOnSub': 'הפריט אינו חלק בעץ — נרשם על תת-ההרכבה {part}, עם המק"ט בתיאור.',
  'ncr.lineOnProject': 'הפריט אינו חלק בעץ — נרשם על הפרויקט (ההרכבה {part}), עם המק"ט בתיאור.',

  // ── ניהול סעיפי בדיקה לפני עבודה ──
  'chka.intro': 'סעיפים שחייבים אישור לפני שהעבודה מתחילה, לפי שלב. שלב ללא סעיפים אינו חוסם דבר — רק שלב שהוגדרו לו סעיפים ידרוש אישור.',
  'chka.pickStage': 'בחר שלב…',
  'chka.item': 'הסעיף',
  'chka.itemPlaceholder': 'לנקות את השולחן לפני ריתוך',
  'chka.deptOptional': 'מחלקה (לא חובה)',
  'chka.allDepts': 'כל המחלקות',
  'chka.needsValueHint': 'דורש ערך (למשל מידה מהתחנה הקודמת) — לא ניתן לאשר בלי להזין',
  'chka.needsEngineerHint': 'דורש אישור מהנדס — תג עובד לא יאשר את הסעיף הזה',
  'chka.add': 'הוסף',
  'chka.empty': 'אין סעיפים מוגדרים. אף שלב אינו חוסם עבודה כרגע.',
  'chka.itemCount': '{n} סעיפים',
  'chka.needsValue': 'דורש ערך',
  'chka.engineerApproval': 'אישור מהנדס',

  // ── פעולות מתקנות פתוחות ──
  'nco.title': 'פעולות מתקנות פתוחות ({n})',
  'nco.overdue': '{n} באיחור',
  'nco.closesOnlyAfterCheck': 'פעולה נסגרת רק אחרי בדיקה שהיא עבדה',
  'nco.supplier': 'ספק',
  'nco.production': 'ייצור',
  'nco.checkedFailed': 'נבדקה ולא עבדה',
  'nco.cause': 'סיבה: {name}',
  'nco.owner': 'אחראי: {name}',
  'nco.workedYes': 'נבדקה והועילה',
  'nco.workedNo': 'נבדקה ולא הועילה',
  'nco.noNote': 'לא נכתב נימוק',
  'nco.review': 'בדיקת אפקטיביות',
  'nco.notePlaceholder': 'מה נבדק, ועל סמך מה (לא חובה)',
  'nco.workedClose': 'עבדה — לסגור',
  'nco.didNotWork': 'לא עבדה',
  'nco.saveFailed': 'שגיאה בשמירת הבדיקה',

  // ── אי-התאמות ספקים ──
  'snc.title': 'אי-התאמות ספקים ({n})',
  'snc.subtitle': 'נפסלו בקבלה — ממתינות לסיבת שורש',
  'snc.rejectedOf': 'נפסלו {n} מתוך {total}',
  'snc.deliveryNote': 'ת.משלוח {no}',
  'snc.handle': 'טיפול',
  'snc.rootCause': 'סיבת שורש (חובה)…',
  'snc.actionNeeded': 'נדרשת פעולה מתקנת מול הספק',
  'snc.actionPlaceholder': 'מה תהיה הפעולה',
  'snc.owner': 'אחראי…',
  'snc.close': 'סגור אי-התאמה',
  'snc.closeFailed': 'שגיאה בסגירת אי-ההתאמה',
  'snc.closeDoesNotRelease': 'סגירת אי-ההתאמה אינה משחררת את החומר. השחרור נעשה בבדיקת קבלה במסך הרכש.',
  // ── ציורי החיתוך ודוח החיתוך ──
  'cut.sectionAria': 'חתך הפרופיל, מסובב {deg}°',
  'cut.topViewAria': 'מבט מלמעלה: להב בזווית {deg} מעלות',
  'cut.pieceAria': 'החתיכה המוגמרת: {len} מ״מ על הפאה הארוכה',
  'cut.thePiece': 'החתיכה',
  'cut.restOfBar': 'המשך המוט',
  'cut.longFaceHint': 'הפאה הארוכה — לפי זה מודדים',
  'cut.noRotationColumnA': 'רשימת החיתוך הזאת לא כללה עמודת סיבוב, ולכן',
  'cut.noRotationColumnB': 'אם הזווית השנייה נחתכת על אותה פאה. בדוק מול השרטוט לפני החיתוך.',
  'cut.redMarkNote': 'הסימון האדום בחתך הוא פאת ייחוס בלבד — הוא נועד להראות לך את גודל הסיבוב, לא איזו פאה למעלה. מה שחשוב: בין החיתוך הראשון לשני הפרופיל מסתובב',
  'cutrep.qty': 'כמות',
  'cutrep.projects': 'פרויקטים',
  'cutrep.nUnpriced': '{n} ללא תמחור',
  'cutrep.helpWhat': 'כל חתיכה שנחתכה בעמדת המסור, מקובצת לפי פרויקט, פרופיל ואורך: כמה יחידות, כמה מטרים, ומה זה עלה.',
  'cutrep.helpHow': 'שורה מופיעה ברגע שהחתיכה מסומנת כחתוכה בעמדת המסור — בסימון החתיכה או בסגירת המוט.',
  'cutrep.helpSource': 'עמדת מסור. העלות היא חלקה של החתיכה במוט שממנו נחתכה, לפי האורך שצרכה, כולל עבודה (תעריף המחלקה) וחומר גלם (מחיר למטר של הפרופיל), במחירי יום החיתוך. מוט שלא תומחר נספר בנפרד ולא מסוכם כאפס. עמודת העלות מוצגת רק למי שיש לו הרשאת VIEW_COSTS.',
  'cut.cutN': 'חיתוך {which}',
  'cut.fromSquare': 'מהניצב',
  'cut.fromProfile': 'מהפרופיל',
  'cut.layoutForCut': 'הנחת הפרופיל לחיתוך {n}',
  'cut.nCut': '{n} נחתכו',
  'cut.nWaiting': '{n} ממתינות',
  'cut.nSkipped': '{n} דולגו',
  'cut.barMapAria': 'מפת המוט: {len} מ״מ',

  // ── קיוסק המחסן ────────────────────────
  'wh.title': 'מחסן',
  'wh.openRows': 'שורות פתוחות',
  'wh.readyToPick': 'מוכן ללקט',
  'wh.atSupplier': 'הוזמן',
  'wh.notOrdered': 'טרם הוזמן',
  'wh.awaitingStock': 'ממתין לאישור מלאי',
  'wh.switch': 'החלף',
  'wh.scanBadge': 'סרוק את התג שלך כדי להתחיל',
  'wh.cantScan': 'לא מצליח לסרוק? בחר מהרשימה',
  'wh.noSku': 'בלי מק״ט — לא ניתן לסריקה',
  'wh.noLocation': 'אין מיקום',
  'wh.nothingToPick': 'אין כרגע מה ללקט.',
  'wh.allProjects': '← כל הפרויקטים',
  'wh.kitOpen': 'עגלה פתוחה',
  'wh.openKitPrint': 'פתח עגלה והדפס',
  'wh.badgeFirst': 'סרוק תג תחילה',
  'wh.needBadgeForKit': 'סרוק תג לפני פתיחת עגלה',
  'wh.nothingReady': 'אין כרגע שורה מוכנה ללקט בפרויקט הזה',
  'wh.pickSheetTitle': 'רשימת ליקוט',
  'wh.pickHeading': 'ליקוט',
  'wh.colSku': 'מק״ט',
  'wh.colLocation': 'מיקום',
  'wh.routeOrder': 'לפי מסלול הליכה — מה שמוכן ללקט, מדף אחר מדף',
  'wh.colDesc': 'תיאור',
  'wh.colQty': 'כמות',
  'wh.colState': 'מצב',
  'wh.colWaiting': 'ממתין',
  'wh.colPicked': 'נלקט',
  'wh.inKit': 'בעגלה',
  'wh.whoAreYou': 'מי אתה?',
  'wh.close': 'סגור',
  'wh.identifyFailed': 'לא הצלחנו לזהות — נסה שוב',
  'wh.unknownCode': '‎{code}‎ — לא מוכר במערכת',
  'wh.codeNotRecognised': 'הקוד לא זוהה',
  'wh.networkError': 'שגיאת רשת',
  'wh.rowsCount': 'שורות',
  'wh.rowsCount_one': 'שורה',
  'wh.rowsCount_many': 'שורות',
  'wh.oldest': 'הוותיקה {age}',
  'wh.days': '{n} ימים',
  'wh.days_one': 'יום',
  'wh.days_many': '{n} ימים',
  'wh.hours': '{n} שע׳',
  'wh.hours_one': 'שעה',
  'wh.hours_many': '{n} שע׳',

  // ── אות הרוויזיה נקבעת במערכת (אלי, 24/09/2026) ──
  'upload.revision': 'רוויזיה {rev}',
  'upload.revisionLabel': 'רוויזיה',
  'upload.revisionCorrected': 'הקובץ נשא {file} — נשמר כ-{assigned} (הרוויזיה הבאה)',
  'upload.revisionBySystem': 'נקבעת במערכת',
  'upload.revisionBySystemHint': 'אות הרוויזיה נקבעת במערכת: חלק חדש מקבל A, וחלק קיים — את האות הבאה אחרי האחרונה שהונפקה.',
  'upload.fileRevision': 'בשם הקובץ: {rev}',

  // ── אישור עבודה בלי שרטוט מלוח ההנדסה (משימה 4, אלי 24/09/2026) ──
  'engHold.wait': 'המתן לשרטוט',
  'engHold.waive': 'אשר עבודה בלי שרטוט',
  'engHold.waitState': 'ממתין לשרטוט',
  'engHold.waivedState': 'אושר לעבוד בלי שרטוט',
  'engHold.defaultTag': 'ברירת מחדל',
  'engHold.by': 'ע״י {name}',
  'engHold.error': 'ההכרעה לא נשמרה: {msg}',
  'engHold.networkError': 'אין חיבור לשרת — נסה שוב בעוד רגע.',

  // ── העמדה: חלק שאושר לעבוד בלי שרטוט ──
  'st.waivedTitle': 'אין שרטוט',
  'st.waivedBy': 'אושר לעבוד בלי שרטוט ע״י {name}',
  'st.waivedUnknown': 'מנהל',
  'st.waivedHint': 'כשיעלה שרטוט, העמדה תודיע ותבקש לעבור אליו.',

  // ── ביטול שורה ב„נחתכו בלייזר ואין להם שרטוט" (משימה 4b) ──
  'laserCancel.button': 'בטל',
  'laserCancel.title': 'ביטול שורה — {part}',
  'laserCancel.titleShort': 'ביטול שורה',
  'laserCancel.reasonLabel': 'למה? (חובה)',
  'laserCancel.reason.RENAMED': 'הועלה שרטוט בשם אחר',
  'laserCancel.reason.PROJECT_UNTRACKED': 'פרויקט לא נדרש למעקב',
  'laserCancel.reason.CUT_ERROR': 'שגיאה בחלק שנחתך',
  'laserCancel.reason.OTHER': 'אחר',
  'laserCancel.hint.RENAMED': 'בחר את החלק שהשרטוט שלו עלה בשם אחר. הכמות והדקות שנחתכו יירשמו עליו, ושלב הלייזר שלו יתקדם.',
  'laserCancel.hint.PROJECT_UNTRACKED': 'כל {n} השורות של פרויקט {project} ייסגרו, וחיתוכים עתידיים שלו ידולגו — בלי בקשת שרטוט ובלי התראה. אפשר לשחזר.',
  'laserCancel.hint.PROJECT_UNTRACKED_one': 'השורה של פרויקט {project} תיסגר, וחיתוכים עתידיים שלו ידולגו — בלי בקשת שרטוט ובלי התראה. אפשר לשחזר.',
  'laserCancel.hint.PROJECT_UNTRACKED_many': 'כל {n} השורות של פרויקט {project} ייסגרו, וחיתוכים עתידיים שלו ידולגו — בלי בקשת שרטוט ובלי התראה. אפשר לשחזר.',
  'laserCancel.hint.CUT_ERROR': 'יירשם פסול לייזר: {qty} יח׳ · {material} · וזמן המכונה שנחתך — לדוחות פסול ועלות, בלי אי-התאמה.',
  'laserCancel.hint.OTHER': 'כתוב במשפט קצר מה קרה — ההסבר נשמר ומופיע בדוחות.',
  'laserCancel.noProject': 'פרויקט {project} אינו במערכת, ולכן אין מה לסמן. בחר „אחר" כדי לסגור את השורה.',
  'laserCancel.noteRequired': 'הסבר (חובה)',
  'laserCancel.noteOptional': 'הערה (לא חובה)',
  'laserCancel.pickPart': 'בחר חלק…',
  'laserCancel.similar': 'דומה למק״ט בדוח',
  'laserCancel.loadingParts': 'טוען את חלקי הפרויקט…',
  'laserCancel.noParts': 'אין בפרויקט הזה חלקים לבחור מהם — העלה את השרטוט במקום לבטל.',
  'laserCancel.mm': 'מ״מ',
  'laserCancel.restorable': 'אפשר לשחזר מ„בוטלו לאחרונה".',
  'laserCancel.confirm': 'בטל את השורה',
  'laserCancel.close': 'סגור',
  'laserCancel.needReason': 'בחר סיבה',
  'laserCancel.networkError': 'אין חיבור לשרת — נסה שוב בעוד רגע.',
  'laserCancel.recentTitle': 'בוטלו לאחרונה',
  'laserCancel.project': 'פרויקט {project}',
  'laserCancel.linkedTo': 'נזקף על',
  'laserCancel.by': '{name} · {date}',
  'laserCancel.restore': 'שחזר',
  'laserCancel.restoreGroup': 'שחזר את כל {n} השורות ואת המעקב',
  'laserCancel.restoreGroup_one': 'שחזר את השורה ואת המעקב',
  'laserCancel.restoreGroup_many': 'שחזר את כל {n} השורות ואת המעקב',
  'laserCancel.restored': 'שוחזר ע״י {name}',
  'laserCancel.renamedNoRestore': 'נזקף על חלק — לתיקון פנה למנהל הייצור',

  // ── בונה הדוחות: ביטולי לייזר ופסול ──
  'rb.dataset.laserCancels': 'ביטולי לייזר ופסול',
  'rb.lc.rows': 'מספר ביטולים',
  'rb.lc.qty': 'כמות שנחתכה',
  'rb.lc.minutes': 'זמן מכונה (דק׳)',
  'rb.lc.kg': 'משקל (ק״ג)',
  'rb.lc.cost': 'עלות חומר',
  'rb.lc.records': 'ביטולים',
  'rb.lc.unknown': 'לא ידוע',
  'rb.lc.g.reason': 'סיבה',
  'rb.lc.g.project': 'פרויקט',
  'rb.lc.g.part': 'מק״ט',
  'rb.lc.g.by': 'מי ביטל',
  'rb.lc.g.material': 'חומר ועובי',
  'rb.lc.g.month': 'חודש',

  // ── מניעת ייצור כפול במיפוי (אלי, 25/09/2026) ──
  'dup.noDrawing': "אין שרטוט",
  'dup.compare': "השווה",
  'dup.checking': "משווה לשרטוטים הקיימים בפרויקט…",
  'dup.compareFailed': "ההשוואה לשרטוטים הקיימים לא נטענה — השרת יבדוק בשליחה.",
  'dup.looksLike': "נראה כמו {pn} ({pct}%)",
  'dup.titleBlock': "בגוש הכותרת כתוב {pn} — והוא כבר קיים בפרויקט",
  'dup.looksLikeHint': "אם זה אותו חלק במק״ט אחר — הוא ייוצר פעמיים. השווה, ובחר.",
  'dup.thisPage': "העמוד בקובץ",
  'dup.thisIsIt': "זה הוא — רוויזיה",
  'dup.notThis': "זה חלק חדש, לא {pn}",
  'dup.existingParts': "החלקים הקיימים בפרויקט — הדומים ראשונים. לחיצה = זה הוא (רוויזיה של אותו מק״ט).",
  'dup.showAll': "כל החלקים ({n})",
  'dup.showLess': "רק הדומים",
  'dup.newCount': "{n} חלקים חדשים",
  'dup.newCount_one': "חלק חדש אחד",
  'dup.newCount_many': "{n} חלקים חדשים",
  'dup.revCount': "{n} רוויזיות",
  'dup.revCount_one': "רוויזיה אחת",
  'dup.revCount_many': "{n} רוויזיות",
  'dup.summaryHint': "בפרויקט כבר יש חלקים. ודא שאף אחד מהחדשים אינו חלק קיים במק״ט אחר — אחרת הוא ייוצר פעמיים.",
  'dup.confirmAll': "בדקתי — אלה חלקים חדשים",
  'dup.confirmOne': "בדקתי — זה חלק חדש",
  'dup.retry': "העלה שוב",
  'dup.compareTitle': "עמוד {page} מול {pn}",
  'dup.existing': "השרטוט הקיים — {pn}",
  'dup.blockLooksLike': "עמוד {page} נראה כמו {pn} — בחר אותו („זה הוא\") או סמן „זה חלק חדש\"",
  'dup.blockConfirmAll': "יש לסמן „בדקתי — אלה חלקים חדשים\" (בתיבה הצהובה)",
  // ── הצעה שהמערכת אינה יכולה להבטיח (אלי, 27/09/2026) ────────────────────
  // מק״ט שנקרא בכל הגיליונות, או הצעה לפי דמיון חזותי. שניהם מוצעים ומסומנים
  // באדום, ושניהם דורשים אישור **לגיליון** — לא אישור אחד לקובץ.
  'sheet.repeatedMark': "זהה בכל הגיליונות — בדוק",
  'sheet.fromSimilarMark': "({pct}%) — בדוק",
  'sheet.checkAck': "בדקתי — עמוד {page} הוא {pn}",
  'sheet.checkAckWhole': "בדקתי — הקובץ הזה הוא {pn}",
  // ── ביטול חלק = ביטול שרטוט (U-0924-07) ──
  'pc.title': 'ביטול חלק {part} (= ביטול השרטוט)',
  'pc.restoreTitle': 'שחזור חלק {part}',
  'pc.titleLoading': 'ביטול חלק',
  'pc.retry': 'נסה שוב',
  'pc.cancelledBy': 'בוטל ע״י {who} ב-{when}. סיבה: {reason}',
  'pc.restoreWith': 'יחזרו איתו (בוטלו יחד איתו): {list}',
  'pc.restoreExplain': 'החלק חוזר לכל התורים והעמדות. שעונים שנעצרו אינם נפתחים מחדש, ודרישות רכש לא השתנו.',
  'pc.consequences': 'מה יקרה',
  'pc.clocks': '{n} שעונים פתוחים ייעצרו — העובדים יתבקשו לדווח כמה סיימו: {list}',
  'pc.clocks_one': 'שעון פתוח אחד ייעצר — העובד יתבקש לדווח כמה סיים: {list}',
  'pc.clocks_many': '{n} שעונים פתוחים ייעצרו — העובדים יתבקשו לדווח כמה סיימו: {list}',
  'pc.clocksNone': 'אין שעונים פתוחים על החלק.',
  'pc.produced': 'כבר יוצר: {list} — נשאר בהיסטוריה ובעלויות.',
  'pc.producedNone': 'עוד לא יוצר דבר מהחלק.',
  'pc.laser': 'שלב הלייזר לא הסתיים — החלק יוצא מתור הלייזר, והלייזר יקבל התראה להוציא אותו מנסטים.',
  'pc.saw': '{n} שורות בטבלת החיתוך — יוצאות מתור המסור, והמסור יקבל התראה.',
  'pc.saw_one': 'שורה אחת בטבלת החיתוך — יוצאת מתור המסור, והמסור יקבל התראה.',
  'pc.saw_many': '{n} שורות בטבלת החיתוך — יוצאות מתור המסור, והמסור יקבל התראה.',
  'pc.nc': '{n} אי-התאמות פתוחות — נשארות לאיכות עם תג „חלק בוטל", ואינן חוסמות משלוח.',
  'pc.nc_one': 'אי-התאמה פתוחה אחת — נשארת לאיכות עם תג „חלק בוטל", ואינה חוסמת משלוח.',
  'pc.nc_many': '{n} אי-התאמות פתוחות — נשארות לאיכות עם תג „חלק בוטל", ואינן חוסמות משלוח.',
  'pc.history': 'הזמן והעלות שכבר נרשמו נשארים בדוחות. החלק נשאר בעץ עם סימן „בוטל" וניתן לשחזור — גם העלאת שרטוט אליו מחזירה אותו.',
  'pc.children': 'בטל גם {n} חלקי בן',
  'pc.children_one': 'בטל גם חלק בן אחד',
  'pc.children_many': 'בטל גם {n} חלקי בן',
  'pc.reason': 'סיבת הביטול (חובה)',
  'pc.reasonPlaceholder': 'למשל: הלקוח ויתר על המסוע השני',
  'pc.reasonHint': 'זה מה שהעובד יראה כשיסרוק את החלק.',
  'pc.reasonMissing': 'כתוב סיבה כדי לבטל',
  'pc.note': 'הערה על מה שכבר יוצר (לא חובה)',
  'pc.notePlaceholder': 'למשל: 2 יחידות מרותכות על המדף ליד הצבע',
  'pc.submit': 'בטל חלק',
  'pc.restore': 'שחזר חלק',
  'pc.cancelShort': 'בטל חלק',
  'pc.restoreShort': 'שחזר חלק',
  'pc.cancelledBadge': 'בוטל',
  'pc.cancelledTip': 'בוטל ע״י {who}: {reason}',
  'upload.partRestored': 'החלק היה מבוטל ({reason}) — השרטוט החזיר אותו לחיים. שוחזר ע״י {who}.',
  'upload.partRestoredWith': 'חזרו איתו: {list}',
  'st.stoppedAUTO_CLOSED': 'השעון נסגר אוטומטית — המסך נשאר בלי מענה. נרשמו 0 יחידות.',
  'st.autoClosedSaved': 'נשלח למנהל הייצור לאישור. תודה!',
  'st.autoClosedHint': 'התשובה עוברת למנהל הייצור, שמאשר את הכמות והשעות. 0 = לא סיימתי יחידות.',
  'st.stoppedPART_CANCELLED': 'החלק בוטל — אין להמשיך לעבוד עליו',
  'st.stoppedOnPart': 'חלק {part}',
  'err.partCancelled': 'החלק {partNumber} בוטל — {cancelReason}. אין לעבוד עליו. אם זו טעות — פנה ל{cancelledBy}.',
  'pc.projectReqs': 'לפרויקט {project} יש {n} דרישות רכש פתוחות. הרכש יקבל התראה לבדוק אותן; שום דרישה לא תבוטל.',
  'pc.projectReqs_one': 'לפרויקט {project} יש דרישת רכש פתוחה אחת. הרכש יקבל התראה לבדוק אותה; היא לא תבוטל.',
  'pc.projectReqs_many': 'לפרויקט {project} יש {n} דרישות רכש פתוחות. הרכש יקבל התראה לבדוק אותן; שום דרישה לא תבוטל.',
  'pc.possiblyRelated': 'ייתכן שקשור (השם זהה למק״ט): {list}',
  'pc.menuCancelledNote': 'החלק מבוטל',
  'pc.ncPartCancelled': 'חלק בוטל',
  'pb.toolbar': 'פעולות על החלקים שנבחרו',
  'pb.selected': 'נבחרו: {n}',
  'pb.cancel': 'בטל חלקים',
  'pb.restore': 'שחזר ({n})',
  'pb.revision': 'העלה רוויזיה',
  'pb.print': 'הדפס מדבקות',
  'pb.clear': 'נקה בחירה',
  'pb.noneLive': 'כל החלקים שנבחרו כבר מבוטלים — אפשר לשחזר',
  'pb.noneCancelled': 'אין חלק מבוטל בבחירה',
  'pb.selectAll': 'בחר את כל המוצגים',
  'pb.selectBranch': 'בחר את כל ההרכבה',
  'pb.selectRow': 'בחר {part}',
  'pb.cancelTitle': 'ביטול חלקים (= ביטול שרטוטים) — נבחרו {n}',
  'pb.restoreTitle': 'שחזור חלקים — {n}',
  'pb.revisionTitle': 'העלאת רוויזיה לחלקים שנבחרו — {n}',
  'pb.blocked': 'לא יבוטל: {why}',
  'pb.withParent': 'יבוטל יחד עם {parent}',
  'pb.willCancel': 'יבוטל',
  'pb.clocksWillStop': 'שעונים פתוחים ייעצרו: {n} — העובד יישאל כמה סיים',
  'pb.clocksStopped': 'שעונים שנעצרו: {n}',
  'pb.includeChildren': 'בטל גם את חלקי הבן של כל חלק',
  'pb.reasonHint': 'סיבה אחת לכל החלקים — זה מה שהעובד יראה כשיסרוק.',
  'pb.noneReady': 'אין בבחירה חלק שאפשר לבטל',
  'pb.cancelSubmit': 'בטל ({n})',
  'pb.restoreExplain': 'החלקים חוזרים לכל התורים והעמדות — וכל מי שבוטל יחד איתם.',
  'pb.restoreSubmit': 'שחזר ({n})',
  'pb.resultSummary': 'בוצע: {done} · סורב: {refused}',
  'pb.outcome.CANCELLED': 'בוטל',
  'pb.outcome.CANCELLED_WITH_PARENT': 'בוטל עם האב',
  'pb.outcome.RESTORED': 'שוחזר',
  'pb.outcome.RESTORED_WITH_PARENT': 'שוחזר עם האב',
  'pb.outcome.REFUSED': 'סורב',
  'pb.revisionExplain': 'גרור את קובצי ה-PDF. כל קובץ משויך לחלק שנבחר לפי המק״ט שבשמו; אות הרוויזיה נקבעת במערכת.',
  'pb.pickFiles': 'בחר קבצים',
  'pb.dropHint': 'או גרור לכאן',
  'pb.matched': 'יעלו: {n}',
  'pb.unmatchedFiles': 'קבצים שלא הותאמו — לא יעלו: {n}',
  'pb.unmatchedParts': 'חלקים שנבחרו ולא הגיע להם קובץ: {n}',
  'pb.unmatched.NO_PART_NUMBER': 'אין מק״ט בשם הקובץ',
  'pb.unmatched.NOT_SELECTED': 'המק״ט {part} אינו בבחירה',
  'pb.unmatched.DUPLICATE': 'כבר יש קובץ לחלק {part}',
  'pb.noMatched': 'אין קובץ שהותאם לחלק שנבחר',
  'pb.uploadSubmit': 'העלה ({n})',
  'pb.uploadNetwork': 'החיבור לשרת נפל באמצע — ייתכן שחלק מהשרטוטים עלו. רענן את הרשימה לפני שמנסים שוב.',
  'pb.releaseRefused': 'הפרויקט עוד לא שוחרר לייצור — שרטוטים נשמרים רק יחד עם השחרור, בחלון האחד של מסך ההעלאה. מכאן לא נשמר כלום.',
  'pb.outcome.DONE': 'בוצע',
  'pb.outcome.SKIPPED': 'דולג',
  'pbm.route': 'שנה מסלול',
  'pbm.pin': 'ראשון בתור',
  'pbm.complete': 'סמן שלב כבוצע',
  'pbm.assign': 'שבץ לעובד',
  'pbm.routeTitle': 'שנה מסלול — נבחרו {n}',
  'pbm.pinTitle': 'ראשון בתור — נבחרו {n}',
  'pbm.completeTitle': 'סמן שלב כבוצע — נבחרו {n}',
  'pbm.assignTitle': 'שבץ לעובד היום — נבחרו {n}',
  'pbm.routeExplain': 'בוחרים מסלול אחד לכל החלקים. מה שכבר נעשה בשלב זהה (אותה מחלקה ואותו שם) עובר; השאר מתאפס — כמו בהחלפת מסלול של חלק בודד.',
  'pbm.hold': 'עצור',
  'pbm.holdTitle': 'עצור חלקים — נבחרו {n}',
  'pbm.holdExplain': 'החלקים נעצרים עד שמישהו משחרר אותם. הסיבה מוצגת לרצפה — כמו „עצור חלק" בתפריט של חלק בודד.',
  'pbm.release': 'החזר לעבודה',
  'pbm.releaseTitle': 'החזר לעבודה — נבחרו {n}',
  'pbm.releaseExplain': 'משחרר את העצירה של כל חלק עצור שנבחר. חלק שאינו עצור — מדולג. כתוב למה משחררים — מי שעצר יקבל את זה בהתראה.',
  'pbm.skip': 'דלג על מחלקה',
  'pbm.skipTitle': 'דלג על מחלקה — נבחרו {n}',
  'pbm.skipExplain': 'השלב של המחלקה מסומן „לא בוצע" עם הסיבה, והחלק ממשיך בלעדיו. המסלול עצמו לא משתנה, וחלקים אחרים עליו לא מושפעים.',
  'pbm.pull': 'משוך למחלקה / החזר לעיבוד',
  'pbm.unitsAll': 'הכול',
  'pbm.pullTitle': 'משוך למחלקה — נבחרו {n}',
  'pbm.pullExplain': 'קדימה: כל שלב שבדרך מסומן — בדקות מוערכות, או „לא בוצע" עם סיבה. אחורה: כמה יחידות חוזרות + סיבה, כמו „החזר לעיבוד". מחלקה שאינה במסלול — סמן „הוסף למסלול".',
  'pbm.reasonLabel': 'סיבה',
  'pbm.reasonPlaceholder': 'למה? (חובה)',
  'pbm.deptLabel': 'מחלקה',
  'pbm.pickDept': 'בחר מחלקה',
  'pbm.pullInRoute': 'למחלקה שבמסלול',
  'pbm.pullAdd': 'הוסף את המחלקה למסלול ועבור אליה',
  'pbm.pullMinutes': 'דקות מוערכות לכל שלב שבדרך (קדימה)',
  'pbm.pullMinutesHint': 'ריק = השלבים שבדרך נרשמים „לא בוצע" עם הסיבה.',
  'pbm.unitsBack': 'יחידות חוזרות',
  'pbm.pinExplain': 'כל חלק עולה לראש התור של המחלקה שבה הוא נמצא עכשיו. הסדר נשמר: הראשון שנבחר — ראשון.',
  'pbm.completeExplain': 'השלב הנוכחי של כל חלק יסומן כבוצע. אם השלב משתנה לפני שלוחצים — החלק ידולג ויוסבר למה.',
  'pbm.assignExplain': 'בוחרים עובד אחד; כל החלקים נכנסים לתוכנית שלו להיום (תפ"י).',
  'pbm.routeLabel': 'מסלול יעד',
  'pbm.employeeLabel': 'עובד',
  'pbm.pickRoute': 'בחר מסלול',
  'pbm.pickEmployee': 'בחר עובד',
  'pbm.noneReady': 'אין בבחירה חלק שאפשר לבצע עליו — הסיבה כתובה ליד כל חלק',
  'pbm.willSkip': 'ידולג: {why}',
  'pbm.minutesEachHint': 'זמן לכל שרטוט בנפרד — בשורה שלו ברשימה (סך הכול לשרטוט, לא ליחידה). המספר כאן ממלא כל שורה שנשארה ריקה. נרשם כ„זמן מוערך". חלק שעליו רץ שעון ידולג — סמן אותו לבד כדי לאשר את הזמן שנמדד.',
  'pbm.acceptEvict': 'אני מבין: השעונים הפתוחים ייסגרו עם 0 יחידות, והעובדים יצטרכו לסרוק מחדש',
  'pbm.submit': 'בצע ({n})',
  'pbm.resultSummary': 'בוצע: {done} · דולג: {refused}',
  // ── עבודה מחוץ למחלקה (U-0924-08) ──
  'ood.badge': 'מחוץ למחלקה',
  'ood.badgeTitle': 'עובד ממחלקה אחרת, בלי אישור מנהל — העבודה מותרת, ומסומנת למנהל הייצור',
  'ood.tab': 'מחוץ למחלקה',
  'ood.tabHint': 'עבודה של עובדים ממחלקה אחרת, בלי אישור מנהל',
  'ood.intro': 'עובד שעבד על שלב של מחלקה שאינה שלו, בלי אישור מנהל. העבודה לא נחסמה — היא כאן כדי שתדע.',
  'ood.from': 'מתאריך',
  'ood.to': 'עד תאריך',
  'ood.stageDept': 'מחלקת השלב',
  'ood.worker': 'עובד',
  'ood.all': 'הכל',
  'ood.count': 'שורות: {n} · רצות עכשיו: {open}',
  'ood.colWorker': 'עובד',
  'ood.colWorkerDepts': 'המחלקות שלו',
  'ood.colPart': 'חלק',
  'ood.colStage': 'שלב',
  'ood.colStageDept': 'מחלקת השלב',
  'ood.colWhen': 'מתי',
  'ood.colMinutes': 'זמן',
  'ood.running': 'רץ עכשיו',
  'ood.closed': 'נסגר',
  'ood.partner': 'שותף',
  'ood.owner': 'בעל השעון',
  'ood.noDept': 'ללא מחלקה',
  'ood.minutes': '{n} דק׳',
  'ood.empty': 'אין עבודה מחוץ למחלקה בטווח הזה ובסינון הזה. אפשר להרחיב את טווח התאריכים או לבחור „הכל".',
  'ood.loading': 'טוען…',
  'ood.errorLabel': 'עבודה מחוץ למחלקה',
  'ood.badRange': 'תאריך „מ" מאוחר מתאריך „עד" — שנה אחד מהם.',

  // ── „לא שוחרר לייצור = לא קיים" (אלי, 25/09/2026) ──
  'rel.title': "שחרור לייצור — פרויקט {project}",
  'rel.checkTitle': "פרויקט {project} כבר בייצור",
  'rel.nothingSaved': "הפרויקט עוד לא שוחרר, ולכן שום דבר לא נשמר עדיין — השרטוטים נשמרים רק יחד עם „שחרר לייצור\". יציאה מהמסך או „בטל\" = כלום לא נשמר.",
  'rel.checkNote': "שום דבר לא נשמר עדיין. בדוק שאף שרטוט כאן אינו כפול למה שכבר בוצע בייצור.",
  'rel.checkLabel': "בדקתי את השרטוטים — אינם כפולים למה שכבר בוצע",
  'rel.release': "שחרר לייצור",
  'rel.upload': "העלה",
  'rel.cancel': "בטל — שום דבר לא נשמר",
  'rel.new': "חלק חדש",
  'rel.revision': "רוויזיה",
  'rel.unchanged': "ללא שינוי",
  'rel.error': "לא ייכתב",
  'rel.colPart': "מק״ט",
  'rel.colAction': "מה ייכתב",
  'rel.colRevision': "רוויזיה",
  'rel.colQty': "כמות",
  'rel.panelRefused': "הפרויקט עוד לא שוחרר לייצור — שרטוט נשמר רק יחד עם השחרור, ממסך ההעלאה. מכאן לא נשמר כלום, והבקשה נשארת פתוחה; כשהשרטוט יעלה עם השחרור היא תיסגר והחיתוך יירשם על החלק.",
  'rel.toUpload': "למסך ההעלאה והשחרור",
  // ── חיפוש/סריקת חלק בקיוסקים (אלי 27/09/2026) ──────────────────────────
  // רכיב אחד — `components/scan/PartSearchPanel.tsx` — בגמר, במסור, בלייזר,
  // בשאריות ובמחסן. הכלל „התאמה מדויקת" יושב בשרת ולא כאן.
  'psearch.toggle': 'חיפוש או סריקה של חלק',
  'psearch.placeholder': 'חפש מק״ט או מספר פרויקט', // הסריקה — בשורה שמתחת (psearch.hint); הארוך נקטע ב-390
  'psearch.hint': 'סריקת ברקוד השרטוט פותחת את החלק מיד',
  'psearch.noParts': 'לא נמצא חלק. בדוק את המספר על השרטוט, או חפש לפי מספר הפרויקט.',
  'psearch.notFound': 'החלק לא נמצא. בדוק את המספר על השרטוט, או חפש לפי מספר הפרויקט.',
  'psearch.clear': 'נקה ובחר חלק אחר',
  'psearch.project': 'פרויקט',
  'psearch.whereNow': 'נמצא כעת ב',
  'psearch.stage': 'שלב',
  'psearch.unknownStage': 'לא ידוע',
  'psearch.routeDone': 'סיים את המסלול',
  'psearch.inMyDept': 'החלק במחלקה שלך',
  'psearch.notInMyDept': 'החלק אינו במחלקה שלך — לעבודה עליו נדרשת משיכה באישור מנהל',
  'psearch.missingChildren': 'חלקים חסרים: {n}',
  'psearch.openDrawing': 'פתח שרטוט',
  'psearch.drawingNeedsBadge': 'יש לסרוק תג לפני פתיחת שרטוט',
  'psearch.drawingRefused': 'לא ניתן לפתוח את השרטוט. פנה למנהל המשמרת.',
  'psearch.noThumb': 'אין תמונת שרטוט',
  'psearch.thumbAlt': 'תמונה ממוזערת של השרטוט',
  // חיפוש הגמר — השרטוט בתוצאות ובגדול (אלי 01/11/2026).
  'psearch.enlarge': 'הגדל את שרטוט {part}',
  'psearch.tapToEnlarge': 'לחץ על השרטוט להגדלה',
  'psearch.pickThis': 'בחר חלק',
  'psearch.scanForFull': 'זו תצוגה מקדימה. לגיליון המלא סרוק תג עובד.',
  'psearch.sheetRefusedPreview': 'מוצגת תצוגה מקדימה בלבד.',
  'psearch.sheetPage': 'עמוד {i} מתוך {n}',
  'psearch.prevPage': 'עמוד קודם',
  'psearch.nextPage': 'עמוד הבא',
  // ── שורת מספר שרטוט בראש כל מסך עובד (אלי 03/10/2026, U-1003-04) ──────
  'dbar.placeholder': 'מספר שרטוט — סרוק או הקלד. מספר מלא פותח את השרטוט',
  'dbar.placeholderStart': 'מספר שרטוט — סרוק או הקלד. מספר מלא פותח ומתחיל עבודה',
  'dbar.placeholderFinishing': 'מספר שרטוט — סרוק או הקלד (אחרי התג)',
  // ── מסך הבחירה אחרי סריקה (אלי 04/10/2026) ──────────────────────────────
  // ערכת הרכבה (אלי 11/10/2026, A4 + B1)
  'ot.title': 'ערכות הרכבה — פרויקט {n}',
  'ot.kits': 'ערכות הרכבה',
  'ot.singles': 'חלקים בודדים',
  'ot.kitMine': '{mine} אצלך · {done}/{n} גמורים',
  'ot.asm': 'ההרכבה',
  'ot.asmParts': 'החלקים שלה ({n})',
  'ot.groupMine': 'אצלך',
  'ot.groupDone': 'גמורים',
  'ot.noDept': 'ללא מחלקה',
  'ot.waiting': 'מחכה לך',
  'ot.doneView': 'גמור — ייפתח לעיון, בלי שעון',
  'ot.doneShort': 'גמור',
  'ot.elsewhere': '{d} — מבוצע בקיוסק אחר',
  'ot.viewNeedsOpen': 'הרכבה גמורה נפתחת רק יחד עם חלק פתוח — סמן קודם חלק',
  'ot.start': 'התחל {n} שעונים',
  'ot.plusView': ' + שרטוט ההרכבה לעיון',
  'ot.pickParts': 'סמן חלקים',
  'ot.pickOpen': 'סמן גם חלק פתוח',
  'ot.drawingOf': 'שרטוט ההרכבה {n}',
  'ot.noDrawing': 'אין שרטוט',
  'ot.viewOnly': 'לעיון בלבד — בלי שעון. הזמן נרשם ל-{n}',
  'ot.scanned': 'נסרק',
  'ot.done': 'הושלם — כל שלבי המסלול בוצעו',
  'ot.alone': 'פתח לבד',
  'ot.aloneWhy': 'נמצא כעת ב{d} — נפתח לבד, במשיכה באישור מנהל',
  'ot.aloneWhyNoDept': 'לא במחלקה שלך — נפתח לבד',
  'ot.alreadyOpen': 'פתוח אצלך',
  'ot.cancel': 'ביטול',
  'ot.limit': 'עד {max} במקביל · פתוחים לך {open} · אפשר לסמן עוד {left}',
  'ot.failed': 'לא נפתחו — פתח כל אחד לבד:',
  'ot.continue': 'המשך לשרטוטים שנפתחו ({n})',
  'ot.onboarding': 'מצב הטמעה — המסלול לא חוסם',
  'ot.empty': 'אין חלקים פעילים בפרויקט הזה.',
  // שאלות ותשובות (אלי 05/10)
  'qa.newQuestion': 'שאלה חדשה',
  'qa.newAnswer': 'תשובה חדשה',
  'qa.newMessage': 'הודעה חדשה',
  'qa.unanswered': 'לא נענתה מאתמול',
  'qa.stOpen': 'פתוחה',
  'qa.stAnswered': 'נענתה',
  'qa.stDone': 'טופל',
  'qa.stVerbal': 'נסגר — נענה בעל פה',
  'qa.myQuestions': 'השאלות שלי',
  'qa.openCount': '{n} פתוחות',
  'qa.tabTo': 'אליי',
  'qa.tabAsked': 'ששאלתי',
  'qa.tabClosed': 'נסגרו',
  'qa.loading': 'טוען…',
  'qa.loadError': 'לא ניתן לטעון את השאלות. רענן את הדף.',
  'qa.emptyTo': 'אין שאלות פתוחות אליך.',
  'qa.emptyAsked': 'אין שאלות פתוחות ששאלת.',
  'qa.emptyClosed': 'לא נסגרו שאלות ב-60 הימים האחרונים.',
  'qa.to': 'אל:',
  'qa.linkedTo': 'קשור ל:',
  'qa.askedFrom': 'נשאל מ:',
  'qa.reopen': 'פתח מחדש',
  'qa.reopened': 'נפתחה מחדש',
  'qa.dueBy': 'תשובה עד {d}',
  'qa.dueLabel': 'תשובה עד',
  'qa.now': 'עכשיו',
  'qa.minAgo': 'לפני {n} דק׳',
  'qa.hAgo': 'לפני {n} שע׳',
  'qa.yesterday': 'אתמול',
  'qa.question': 'שאלה',
  'qa.threadError': 'השאלה לא נטענה. פתח אותה שוב מ„השאלות שלי".',
  'qa.reply': 'תשובה',
  'qa.addToThread': 'להוסיף לשיחה…',
  'qa.yourAnswer': 'התשובה שלך…',
  'qa.send': 'שלח',
  'qa.answer': 'ענה',
  'qa.closeVerbal': 'סגור בלי תשובה — נענה בעל פה',
  'qa.markDone': 'טופל — סגור את השאלה',
  'qa.verbalHint': 'סגירה בלי תשובה ← השואל מקבל: „נסגר — נענה בעל פה"',
  'qa.linkProject': 'פרויקט',
  'qa.linkPart': 'חלק / מק״ט',
  'qa.linkPurchasing': 'רכש',
  'qa.linkEngineering': 'הנדסה',
  'qa.needProject': 'מספר פרויקט',
  'qa.needPart': 'מק״ט',
  'qa.removeLink': 'הסר את הקישור',
  'qa.link': 'קשר',
  'qa.cancel': 'ביטול',
  'qa.noLinkHint': 'או ללא קישור — ובחר נמען ב„אל:"',
  'qa.remove': 'הסר את {name}',
  'qa.pickRecipient': 'בחר נמען',
  'qa.pick': 'בחר…',
  'qa.addRecipient': '+ עוד נמען',
  'qa.purchaseHint': 'משורת רכש: פותח הדרישה + המנהל שלו לפי התפקיד. המנכ״ל לא מכותב.',
  'qa.text': 'השאלה',
  'qa.needRecipient': 'הוסף נמען ב„אל:" כדי לשלוח.',
  'qa.ask': 'שאלה על השורה',
  'qa.askOpen': 'שאלה על השורה ({n} פתוחות)',
  'qa.failed': 'הפעולה נכשלה. נסה שוב.',
  // סקר העמדה 11/10/2026
  'st.excelOnlyStage': 'החלק עומד ב{dept} — מחלקה שמדווחת בקובץ אקסל בעמדת הלייזר, לא כאן. אין צורך בקוד מנהל: פנה לעמדת הלייזר או למנהל העבודה.',
  'ot.goTo': 'עבור לשרטוט',
  'err.offline': 'אין חיבור לשרת — בדוק שהרשת מחוברת ונסה שוב. אם זה חוזר — קרא למנהל.',
  'net.reconnecting': 'המערכת מתעדכנת או שהחיבור נפל — מתחברת מחדש לבד, אין צורך ללחוץ',
  'err.serverDown': 'השרת לא ענה — נסה שוב בעוד רגע. אם זה חוזר — קרא למנהל.',
  'st.retry': 'נסה שוב',
  'st.badgeNextStep': 'סרוק שוב, או הקלד את המספר שעל התג. אם זה חוזר — פנה למנהל העבודה או למשרד לבדוק את התג.',
  // ── „בוקר טוב <שם>" — עבודות השטח שלי בעמדה (אלי 12/10/2026, החלטות 159–160) ──
  'fm.helloMorning': 'בוקר טוב, {name}',
  'fm.helloNoon': 'צהריים טובים, {name}',
  'fm.helloEvening': 'ערב טוב, {name}',
  'fm.todayField': 'היום אתה בשטח',
  'fm.tomorrowField': 'מחר אתה בשטח',
  'fm.kind.INSTALLATION': 'התקנה',
  'fm.kind.VISIT': 'ביקור שירות',
  'fm.kind.CALL': 'קריאת שירות',
  'fm.departure': 'יציאה',
  'fm.vehicle': 'רכב',
  'fm.days': 'ימים',
  'fm.dayOf': '{n} מתוך {m}',
  'fm.crewLead': 'ראש צוות: {name}',
  'fm.you': 'אתה',
  'fm.load': 'מה מעמיסים',
  'fm.loadLegend': 'אותה רשימה כמו „ציוד" באפליקציית השטח — מה שמסמנים כאן מסומן שם.',
  'fm.noEquipment': 'לא הוגדר ציוד לעבודה הזו — שאל את מנהל השירות.',
  'fm.todaySite': 'היום באתר',
  'fm.tomorrowSite': 'מחר באתר',
  'fm.contact': 'איש קשר: {name}',
  'fm.safety': 'בטיחות',
  'fm.noTasks': 'אין משימות רשומות לעבודה.',
  'fm.visitActions': '{n} פעולות בצ׳קליסט האחזקה',
  'fm.markAll': 'סמן הכל',
  'fm.showList': 'הצג את הרשימה',
  'fm.hideList': 'הסתר את הרשימה',
  'fm.officeNote': 'הערת משרד',
  'fm.group.PARTS': 'חלקים (מק״ט)',
  'fm.group.KIT': 'כלים וחומרים',
  'fm.group.MANUAL': 'נוסף ביד',
  'fm.openField': 'פתח באפליקציית השטח',
  'fm.toStation': 'לעמדה ←',
  'fm.tickFailed': 'הסימון לא נשמר — נסה שוב. אם זה חוזר — פנה למנהל השירות.',
  'fm.takenBy': 'סימן: {name}',
  'fm.cond.AT_HEIGHT': 'עבודה בגובה — רתמה ותדריך באתר',
  'fm.cond.SITE_WELD': 'ריתוך באתר — היתר עבודה בחום',
  'fm.cond.PHOTO_RESTRICTED': 'אתר ביטחוני — אסור לצלם בלי אישור',
  'err.fieldNotOnCrew': 'הפריט שייך לעבודה שאינך בצוות שלה — סרוק את התג שלך שוב, או פנה למנהל השירות.',
  'err.fieldJobClosed': 'העבודה כבר נסגרה — אין מה להעמיס. פנה למנהל השירות אם זה לא נכון.',
  // 15/10/2026 — קודי הרצפה שעוד לא תורגמו (ראה `lib/stationError.ts`).
  'err.drawingMissingWait': 'החלק ממתין לשרטוט — מנהל קבע שלא להתחיל עד שיעלה',
  'err.drawingMissing': 'חסר שרטוט לחלק הזה — דורש אישור מנהל',
  'err.badgeRevoked': 'התג הזה בוטל — הונפק במקומו תג חדש (גרסה {version}). סרוק את התג החדש, או פנה למנהל הייצור.',
  'err.badgeScanOnly': 'התג הזה אינו בתוקף, ומספר עובד לבד אינו פותח — סרוק את התג החדש. התג אבד? מנהל הייצור מדפיס תג חלופי.',
  'err.badgeOld': 'התג הזה ישן ואינו בתוקף עוד — בקש תג חדש ממנהל הייצור.',
  'err.photoPickOff': 'בחירה מהרשימה כבויה בעמדה הזו — סרוק את התג. אין תג? מנהל הייצור מדפיס תג חלופי.',
  'err.exceedsPreviousStageAll': 'כל {arrived} היחידות שהגיעו מ{prevStageName} כבר דווחו כאן. כדי לדווח {units} נוספות נדרש קוד מנהל.',
  'err.exceedsPreviousStage':'הגיעו מ{prevStageName} {arrived}, וכאן כבר דווחו {doneHere} (ופסולים {scrappedHere}) — אפשר לדווח עד {mayReport}. כדי לדווח {units} נדרש קוד מנהל.',
  'err.hasDrawing': 'לחלק הזה כבר יש שרטוט פעיל, ולכן אין צורך באישור. רענן את הרשימה.',
  'err.notAManager': 'הכרטיס שנסרק אינו של מנהל. אפשר להזין קוד מנהל במקום.',
  'err.invalidMinutes': 'הדקות של השלב הקודם צריכות להיות לפחות 1 ופחות מזמן העבודה הנוכחית',
  'err.notSelfPrior': 'השעון הזה אינו על שלב קודם — סיים כרגיל',
  'err.partDone': 'החלק הושלם בכל שלבי המסלול. תיקון על חלק גמור — דרך אי-התאמה.',
  'err.priorNotEarlier': 'אפשר לבצע בעצמך רק שלב שלפני השלב שלך',
  'err.priorNoOwnStage': 'למחלקה שלך אין שלב בחלק הזה — אי אפשר לבצע שלב קודם',
  'err.priorStageMachine': 'זה שלב מכונה (לייזר, כיפוף או עיבוד שבבי) — הוא נשאר במחלקה שלו',
  'err.routeChanged': 'המסלול של החלק השתנה הרגע. סרוק את השרטוט שוב.',
  'err.routeNotSequential': 'המסלול של החלק ממוספר עם חורים, ואי אפשר להוסיף לו שלב מהעמדה. פנה למנהל הייצור — הוא יוסיף את המחלקה דרך „משוך" בלוח הייצור.',
  'err.sharedScannedRoute': 'מסלול שנבנה מסריקות משותף לחלקים נוספים, ולכן אי אפשר להוסיף לו שלב — הוספת תחנה הייתה משנה את התהליך של חלקים אחרים. פנה למנהל הייצור כדי להעביר את החלק למסלול משלו.',
  'err.weldViaChildInvalid': 'זה אינו האבא שמחכה לריתוך של החלק שנסרק. סרוק את השרטוט שוב, ואם זה חוזר — פנה למנהל הייצור.',
  'err.partNotInWork': 'המק״ט קיים אך אינו בעבודה כרגע (פרויקט שאינו פעיל, או חלק שהוסר). פנה למנהל הייצור.',
  'err.projectClosed': 'הפרויקט נסגר, ולכן החלק אינו בעבודה. אם צריך לעבוד עליו — מנהל הייצור פותח אותו מחדש.',
  'err.sawBadgeRequired': 'סרוק תג עובד בעמדת המסור',
  'err.sawNotInDepartment': 'המכונה שנבחרה אינה מסור פעיל במחלקת המסור — בחר מסור מהרשימה בעמדה, או בקש ממנהל הייצור לעדכן את המכונות במחלקת המסור',
  'err.sawRequired': 'בחר על איזה מסור אתה עובד',
  'err.remainingUnanswered': 'נשארו חתיכות שלא סומנו. אמור אם חתכת אותן או לא — המערכת לא מנחשת.',
  'err.laserAlreadyUploaded': 'הניסט הזה כבר הועלה. המכונה רשמה את אותו זמן חיתוך בדיוק, כלומר זה אותו חיתוך. אם באמת חתכת שוב את אותה תוכנית — אפשר לאשר ולהעלות בכל זאת.',
  'err.noFileChosen': 'לא נבחר קובץ',
  'err.approvalCodeUnset': 'לא הוגדר קוד אישור מנהל במערכת. יש להגדיר אותו בהגדרות לפני שאפשר לאשר פעולות בעמדה.',
  'err.approvalCodeDefault': 'קוד אישור המנהל עדיין ברירת המחדל של המערכת. יש לשנות אותו בהגדרות — כל עוד הוא לא שונה, הוא לא מגן על כלום.',
  'err.cardRevoked': 'הכרטיס בוטל ואינו מזהה איש. יש לפנות למשרד לקבלת כרטיס חדש.',
  'fm.navigate': 'Waze',  'st.tree.running': 'בעבודה אצלך',
  'st.tree.awayShow': '{n} חלקים במחלקות אחרות',
  'st.tree.awayHide': 'הסתר חלקים של מחלקות אחרות',
  'st.tree.doneGroup': 'הסתיימו ({n})',
  'st.ncQty': 'כמה יחידות? (מתוך {max}, רשות)',
  'st.reportedNow': '✓ דווחו {n} יח׳ ({done}/{total}) · {stage}',
  'st.movedTo': ' · החלק עבר ל{dept}',
  'st.movedToParent': ' · החלק עבר להרכבה {n}',
  'st.nextPartOpened': 'המסך עבר לשרטוט הפתוח הבא: {n}',
  // ── „עודכן · מה חדש" בתחתית כל מסך (UX X4, אלי 30/10/2026) ──
  'upd.updated': 'עודכן {when}',
  'upd.dev': 'גרסת פיתוח',
  'upd.whatsNew': 'מה חדש',
  'upd.loading': 'טוען…',
  'upd.error': 'הרשימה לא נטענה — נסה שוב מ„מה חדש ומעודכן".',
  'upd.none': 'אין עדיין שינויים שפורסמו לצוות.',
  'upd.all': 'הכל ב„מה חדש ומעודכן"',
  // ── המחסן לפי התהליך של המחסנאי (31/10/2026) ──
  'whp.title': "מחסן",
  'whp.close': "סגור",
  'whp.loading': "טוען…",
  'whp.loadFail': "הרשימה לא נטענה. רענן את הדף; אם זה חוזר — דווח על תקלה.",
  'whp.nav.check': "בדיקת מלאי",
  'whp.nav.issue': "לניפוק",
  'whp.dock.check': "בדיקה",
  'whp.dock.issue': "ניפוק",
  'whp.noDateShort': "אין תאריך",
  'whp.date.release': "הורדה לייצור",
  'whp.date.productionEnd': "סיום ייצור",
  'whp.date.need': "תאריך נדרש",
  'whp.date.delivery': "אספקה ללקוח",
  'whp.date.fat': "FAT מתוכנן",
  'whp.date.install': "התקנה",
  'whp.date.promised': "הבטחת ספק",
  'whp.date.requested': "תאריך הדרישה",
  'whp.drain.left': "נשארו {n}",
  'whp.drain.checked': "נבדקו {done} מתוך {total}",
  'whp.drain.issued': "נופקו {done} מתוך {total}",
  'whp.drain.search': "חפש או סרוק: פריט, מק״ט, פרויקט",
  'whp.drain.groupBy': "קיבוץ",
  'whp.drain.by.project': "פרויקט",
  'whp.drain.by.sku': "מק״ט",
  'whp.drain.sortBy': "מיון לפי תאריך",
  'whp.drain.sortOption': "מיון: {name}",
  'whp.drain.project': "סינון לפי פרויקט",
  'whp.drain.allProjects': "כל הפרויקטים",
  'whp.drain.noSkuGroup': "בלי מק״ט",
  'whp.drain.need': "נדרש",
  'whp.drain.catalogHint': "במערכת {qty} (לא אומת)",
  'whp.drain.answer': "התשובה",
  'whp.drain.now': "נפק עכשיו",
  'whp.drain.nowDoes': "ללא ספירה",
  'whp.drain.filters': "סינון",
  'whp.drain.onOrder': "בהזמנה:",
  'whp.drain.onOrderTo': "ל{to}",
  'whp.drain.onOrderStock': "מלאי",
  'whp.drain.onOrderDue': "צפוי {date}",
  'whp.ready.title': "{name}: מוכן לניפוק",
  'whp.ready.sub': "השורה כבר ב„לניפוק\". אפשר לנפק מכאן לעובד שעומד מולך.",
  'whp.ready.issue': "נפק {qty} עכשיו",
  'whp.ready.later': "השאר ב„לניפוק\" והמשך",
  'whp.ready.to': "מנפק ל{name}",
  'whp.ready.doneTo': "{name}: נופק ל{worker}",
  'whp.ready.serial': "{n} שורות צריכות מספר סידורי. המשך ב„לניפוק\", שם נפתח השדה.",
  'whp.overStock': "במערכת רשום {n} בלבד. בדקת על המדף?",
  'whp.overStockOk': "יש על המדף, נפק",
  'whp.finderOnOrder': "{n} בהזמנה",
  'whp.drain.doneNow': "{name}: נופק לפרויקט",
  'whp.drain.doneNowLeft': "{name}: יש במלאי. {n} שורות נשארו ב„לנפק\", בדוק שם.",
  'whp.origin.item': "פריט",
  'whp.origin.qty': "כמות",
  'whp.origin.from': "מקור",
  'whp.origin.who': "מי",
  'whp.origin.when': "מתי",
  'whp.origin.shelf': "מדף",
  'whp.origin.counted': "נספר במלאי",
  'whp.origin.noCount': "במלאי, ללא ספירה",
  'whp.origin.supplier': "מספק",
  'whp.origin.box': "קופסא",
  'whp.origin.none': "לא נרשם",
  'whp.drain.have': "יש הכל",
  'whp.drain.haveDoes': "ללא ספירה → לניפוק",
  'whp.drain.none': "אין",
  'whp.drain.noneDoes': "→ עובר לרכש",
  'whp.drain.part': "חלק",
  'whp.drain.partDoes': "נספר, החסר → לרכש",
  'whp.drain.typeHint': "או הקלד כמה יש + Enter, או סרוק פריט",
  'whp.drain.backToAnswers': "חזרה לתשובות",
  'whp.drain.notInList': "„{code}\" לא ברשימה הזאת. סרוק פריט שמחכה לבדיקה, או הקלד כמות.",
  'whp.drain.doneHave': "יש הכל, ללא ספירה: {name} → לניפוק",
  'whp.drain.doneNone': "אין: {name} → עבר לרכש",
  'whp.drain.donePart': "{name}: יש {got} מתוך {of} → החסר עבר לרכש",
  'whp.drain.noneInFilter': "אין שורות בסינון הזה",
  'whp.drain.checkEmpty': "הכל נבדק",
  'whp.drain.checkEmptySub': "הרכש יכול להזמין.",
  'whp.drain.toIssueN': "המשך ללניפוק ({n})",
  'whp.drain.atEngineer': "מחכות למהנדס — חלופי ({n})",
  'whp.drain.linesReady': "{n} שורות מוכנות",
  'whp.drain.linesReady_many': "{n} שורות מוכנות",
  'whp.drain.linesReady_one': "שורה אחת מוכנה",
  'whp.drain.andMore': "ועוד {n}",
  'whp.drain.lastIssued': "נופק {n}: {lines} שורות · מדבקות:",
  'whp.drain.issueEmpty': "הכל נופק",
  'whp.drain.issueEmptySub': "אין עכשיו שורה מוכנה בפרויקט ששוחרר.",
  'whp.drain.backHome': "חזרה להיום",
  'whp.drain.noSkuN': "{n} בלי מק״ט",
  'whp.drain.inCellN': "{n} בתא",
  'whp.drain.unreleased': "עוד לא שוחררו לייצור ({n})",
  'whp.drain.continue': "המשך",
  'whp.drain.checkWhy': "הרכש מחכה לתשובה",
  'whp.drain.issueWhy': "הייצור מחכה לחומר",
  'whp.drain.statsToday': "נבדקו היום {c} · נופקו היום {i}",
  'whp.drain.allClear': "הכל נבדק והכל נופק",
  'whp.drain.allClearNone': "אין עכשיו עבודה פתוחה במחסן.",
  'whp.drain.nextRecv': "הבא: לקבל מ-{n} ספקים",
  'whp.drain.nextCount': "הבא: ספירת מדף ({n})",
  'whp.drain.nextUpcoming': "הבא: {n} פרויקטים יורדים לייצור בקרוב",
  'whp.issueN': "נפק {n} שורות",
  'whp.issueN_many': "נפק {n} שורות",
  'whp.issueN_one': "נפק שורה אחת",
  'whp.issuedSelf': "נופקו {n} שורות של {project} — נרשם עליך",
  'whp.issuedSelf_many': "נופקו {n} שורות של {project} — נרשם עליך",
  'whp.issuedSelf_one': "נופקה שורה אחת של {project} — נרשם עליך",
  'whp.badgeOptional': "כרטיס עובד (לא חובה) — בלי כרטיס נרשם עליך",
  'whp.sku.onShelf': "יש על המדף",
  'whp.sku.howMany': "כמה?",
  'whp.sku.howManyOf': "כמה יש על המדף — {name}",
  'whp.sku.total': "נדרש בסה״כ",
  'whp.sku.forProjects': "ל-{n} פרויקטים",
  'whp.sku.forProjects_many': "ל-{n} פרויקטים",
  'whp.sku.forProjects_one': "לפרויקט אחד",
  'whp.sku.needBy': "דרוש {date}",
  'whp.sku.need': "צריך",
  'whp.sku.gets': "מקבל",
  'whp.sku.getsOf': "כמה מקבל {project}",
  'whp.sku.toBuy': "{qty} לרכש",
  'whp.sku.hint': "החלוקה לפי מה שנדרש ראשון; אפשר לשנות לפני האישור. מה שעל המדף נרשם כספירה של הפריט.",
  'whp.sku.over': "חילקת {given} — יותר ממה שיש על המדף ({shelf}).",
  'whp.sku.needQty': "כתוב כמה יש על המדף, או לחץ „אין\".",
  'whp.sku.failed': "לא נשמר בכל הפרויקטים — {list}",
  'whp.sku.noneDone': "אין במלאי: {name} → הרכש יכול להזמין ({n} פרויקטים)",
  'whp.sku.haveDone': "יש במלאי: {name} ×{qty} חולק ל-{n} פרויקטים",
  'whp.sku.haveDone_many': "יש במלאי: {name} ×{qty} חולק ל-{n} פרויקטים",
  'whp.sku.haveDone_one': "יש במלאי: {name} ×{qty} לפרויקט אחד",
  'whp.sku.shortDone': "החסר ({qty}) עבר לרכש",
  'whp.sku.confirm': "אשר — לנפק אחר כך",
  'whp.sku.confirmPart': "אשר — החסר לרכש",
  'whp.sku.allBuy': "אין — הכול לרכש",
  'whp.dock.more': "עוד",
  'whp.issued.open': "פתח",
  'whp.issued.close': "סגור",
  'whp.nav.today': "היום",
  'whp.nav.kits': "ערכות",
  'whp.nav.projects': "פרויקטים",
  'whp.nav.recv': "קבלה",
  'whp.nav.stock': "מלאי",
  'whp.nav.clean': "לנקות",
  'whp.nav.pick': "ליקוט",
  'whp.nav.count': "ספירה",
  'whp.nav.desk': "דלפק",
  'whp.scanAll': "סרוק: פריט, ספק, תא, פרויקט או כרטיס עובד",
  'whp.scanMany': "נמצאו {n} עם „{v}\" — מוצגת הראשונה. הקלד עוד מילה כדי לצמצם.",
  'whp.searchResults': "תוצאות חיפוש",
  'whp.searchStock': "פריטים במלאי",
  'whp.searchWork': "עבודה פתוחה",
  'whp.searchNoStock': "אין פריט במלאי עם „{v}\".",
  'whp.notCounted': "לא נספר",
  'whp.kind.ask': "יש במלאי?",
  'whp.kind.issue': "ערכה",
  'whp.kind.receive': "קבלה",
  'whp.requests': "בקשות אליך",
  'whp.answer': "ענה",
  'whp.toIssue': "לנפק — מחכה בתא",
  'whp.issue': "נפק",
  'whp.toPlace': "לשים בתא",
  'whp.place': "שים בתא",
  'whp.linesInCell': "{n} שורות בתא",
  'whp.linesInCell_many': "{n} שורות בתא",
  'whp.linesInCell_one': "שורה אחת בתא",
  'whp.linesToPlace': "{n} שורות הגיעו ועוד לא בתא",
  'whp.linesToPlace_many': "{n} שורות הגיעו ועוד לא בתא",
  'whp.linesToPlace_one': "שורה אחת הגיעה ועוד לא בתא",
  'whp.linesN': "{n} שורות",
  'whp.linesN_many': "{n} שורות",
  'whp.linesN_one': "שורה אחת",
  'whp.all': "הכל",
  'whp.upcoming': "יורד לייצור בקרוב",
  'whp.inDays': "בעוד {n} ימי עבודה",
  'whp.todayWord': "היום",
  'whp.overdueRelease': "עבר לפני {n} ימי עבודה",
  'whp.noList': "אין רשימת רכש — {who} קיבל תזכורת",
  'whp.listState': "{n} במפעל · {order} לא הוזמנו",
  'whp.engineer': "המהנדס",
  'whp.check': "בדוק מלאי",
  'whp.see': "פתח",
  'whp.ask': "יש במלאי?",
  'whp.askSub': "שורה לכל פריט. כותבים פעם אחת כמה יש — מי שצריך ראשון מקבל ראשון.",
  'whp.askRest': "בלי מק״ט או עם חלופי — לפי פרויקט",
  'whp.skuNeed': "נדרש {qty} ל-{projects}",
  'whp.nothing': "אין כרגע עבודה פתוחה במחסן.",
  'whp.field': "ערכות שטח — עד הערב שלפני",
  'whp.leaves': "יציאה {date} {time}",
  'whp.fieldMissing': "חסרים {n}: {names}",
  'whp.fieldReady': "הכל נלקט",
  'whp.arriving': "מגיע — לפי ספק",
  'whp.toReceive': "לקבלה",
  'whp.noSupplier': "ספק לא רשום",
  'whp.supLines': "{n} שורות",
  'whp.supLines_many': "{n} שורות",
  'whp.supLines_one': "שורה אחת",
  'whp.lateN': "{n} באיחור",
  'whp.receive': "קלוט",
  'whp.waiting': "ממתין למישהו אחר",
  'whp.small': "קטן, אבל היום",
  'whp.countToday': "ספירה של היום · {n} פריטים",
  'whp.countDone': "נספרו היום {n} מתוך {of}",
  'whp.start': "התחל",
  'whp.belowMin': "{n} פריטים מתחת למינימום",
  'whp.cleanN': "לנקות · {n} שורות",
  'whp.doneToday': "נעשה היום · {n}",
  'whp.doneTitle': "נעשה היום במחסן",
  'whp.belowTitle': "מתחת למינימום",
  'whp.belowSub': "במלאי {qty} · מינימום {min}",
  'whp.restock': "דרוש למלאי",
  'whp.w.subOut': "לשלוח לקבלן: {name}",
  'whp.w.left': "יצא",
  'whp.w.subAt': "אצל {name}",
  'whp.w.back': "חזר",
  'whp.w.sub': "חלופי אצל המהנדס: {name}",
  'whp.w.qa': "בדיקת קבלה: {name}",
  'whp.w.ret': "להחזיר לספק: {name} ×{qty}",
  'whp.w.collected': "נאסף",
  'whp.w.retSub': "נפסל, לא במלאי · ×{qty}",
  'whp.kitTitle': "ערכה {n}",
  'whp.noKits': "כל הפרויקטים מכוסים. אף אחד לא מחכה למחסן.",
  'whp.proj.search': "חיפוש פרויקט לפי מספר או שם",
  'whp.proj.none': "אין פרויקט כזה. בדוק את המספר או נסה מילה אחרת.",
  'whp.proj.pick': "בחר פרויקט מהרשימה כדי לראות את כל הרכש שלו.",
  'whp.proj.loadFail': "טעינת הפרויקטים נכשלה. רענן את הדף.",
  'whp.proj.open': "{n} שורות פתוחות במחסן",
  'whp.proj.clear': "אין שורות פתוחות במחסן",
  'whp.proj.issue': "ניפוק לפרויקט",
  'whp.proj.ask': "בדיקת מלאי ({n})",
  'whp.proj.kit': "פתח ערכה",
  'whp.proj.back': "חזרה לרשימה",
  'whp.proj.work': "יש עבודה למחסן",
  'whp.proj.found': "נמצאו",
  'whp.proj.noWork': "אין כרגע פרויקט שמחכה למחסן. כל פרויקט אחר נמצא בחיפוש.",
  'whp.proj.rest': "שאר הפרויקטים נמצאים בחיפוש.",
  'whp.proj.allData': "כל נתוני הרכש",
  'whp.proj.print': "רשימת רכש להדפסה",
  'whp.proj.lines': "חזרה לטבלה",
  'whp.hist.filter': "סינון לפי מצב",
  'whp.hist.f.wait': "מחכה לתשובה",
  'whp.hist.f.open': "פתוח",
  'whp.hist.f.done': "נופק",
  'whp.hist.f.all': "הכול",
  'whp.hist.state': "מצב",
  'whp.hist.waits': "מחכה לתשובה",
  'whp.hist.done': "נופק",
  'whp.hist.what': "מה קרה בפועל?",
  'whp.hist.full': "היסטוריה מלאה",
  'whp.hist.title': "היסטוריית שורות",
  'whp.hist.asked': "דרש",
  'whp.hist.ordered': "הזמין",
  'whp.hist.issued': "ניפק",
  'whp.hist.notYet': "עדיין לא",
  'whp.hist.unknown': "לא נרשם מי",
  'whp.hist.empty': "אין שורות רכש בפרויקט הזה.",
  'whp.hist.loadFail': "טעינת ההיסטוריה נכשלה. רענן את הדף.",
  'whp.proj.waits': "{n} שורות מחכות לתשובה",
  'whp.proj.waitsAct': "ענה על {n} שורות",
  'whp.issued.title': "הרכש מחכה לתשובה: {n} שורות שסומנו נופק",
  'whp.issued.sub': "השורות נסגרו בלי בדיקת מלאי. בדוק על המדף וענה בכל שורה מה קרה בפועל.",
  'whp.issued.when': "סומן {at} {by}",
  'whp.issued.yes': "נופק בפועל",
  'whp.issued.have': "לא נופק, יש במלאי",
  'whp.issued.none': "לא נופק, אין",
  'whp.issued.doneISSUED': "{name}: נרשם שנופק בפועל",
  'whp.issued.doneHAVE': "{name}: חזר לרשימת „לנפק\"",
  'whp.issued.doneNONE': "{name}: הועבר לרכש",
  'whp.kitViewAria': "תצוגת הערכות",
  'whp.kitQueue': "תור",
  'whp.kitTable': "טבלה",
  'whp.printDay': "הדפס רשימת היום",
  'whp.showUnreleased': "הצג {n} שעוד לא שוחררו לייצור",
  'whp.hideUnreleased': "הסתר {n} שעוד לא שוחררו לייצור",
  'date.pick': "בחר תאריך מהלוח",
  'date.bad': "תאריך לא תקין — כתוב יום/חודש/שנה, למשל 31/10/2026.",
  'date.badRange': "התאריך לא בטווח המותר, או לא תקין — כתוב יום/חודש/שנה.",
  'kitOrder.title': "סדר ערכות",
  'kitOrder.hint': "הסדר שהמחסן רואה. מה שלא הזזת נשאר לפי תאריך; פרויקט חדש נכנס לפי התאריך שלו.",
  'kitOrder.notInQueue': "ל-{n} אין עכשיו ערכה פתוחה — אין מה לסדר.",
  'kitOrder.loading': "טוען את תור הערכות…",
  'kitOrder.loadError': "תור הערכות לא נטען.",
  'kitOrder.retry': "נסה שוב",
  'kitOrder.empty': "אין ערכות פתוחות.",
  'kitOrder.saveFailed': "הסדר לא נשמר — נסה שוב.",
  'kitOrder.need': "נדרש {d}",
  'kitOrder.noDate': "אין תאריך",
  'kitOrder.ready': "מוכן {n}",
  'kitOrder.missing': "חסר {n}",
  'kitOrder.byDate': "לפי תאריך",
  'kitOrder.top': "{n} לראש התור",
  'kitOrder.up': "{n} מקום אחד למעלה",
  'kitOrder.down': "{n} מקום אחד למטה",
  'whq.saveFailed': "הפעולה לא נשמרה. נסה שוב, ואם זה חוזר — דווח בכפתור „דווח על תקלה\".",
  'whq.lastCount': "ספירה אחרונה {d}",
  'whq.lastCountNever': "ספירה אחרונה: אף פעם",
  'whq.replacement': "חלופה",
  'whq.noSku': "בלי מק״ט",
  'whq.oneLabel': "מדבקה אחת",
  'whq.nLabels': "{n} מדבקות + מדבקת קיט",
  'whq.serialRequired': "לפריטים האלה חובה מספר סריאלי לכל יחידה — סרוק או הקלד, ואז „נפק\".",
  'whq.serialOf': "סריאלי {i} של {name}",
  'whq.writeHowMany': "כתוב כמה יש במדף, או לחץ „אין\".",
  'whq.shortToBuy': "החסר ({q}) עבר לרכש",
  'whq.issuedLine': "נופק {name} ×{q} → {p}",
  'whq.haveLine': "יש במלאי: {name} ×{q} → „לנפק\" של {p}",
  'whq.noneLine': "אין במלאי: {name} → הרכש יכול להזמין",
  'whq.zeroLine': "ב„{name}\" כתוב 0 — לחץ שם „אין\", או תקן את הכמות.",
  'whq.lines_one': "שורה אחת",
  'whq.lines_many': "{n} שורות",
  'whq.noneAll': "אין במלאי: {lines} של {p} → הרכש יכול להזמין",
  'whq.haveAll': "יש במלאי: {lines} של {p} → „לנפק\"",
  'whq.shortN': "ב-{n} החסר עבר לרכש",
  'whq.savedOf': "נשמרו {done} מתוך {total}.",
  'whq.answerAll': "ענה לכולם ({n}):",
  'whq.yes': "יש",
  'whq.no': "אין",
  'whq.savingAnswers': "שומר את התשובות…",
  'whq.answerHint': "קודם ענה בשורה על החריגים (כמות אחרת, חלופי) — ואז תשובה אחת לשאר.",
  'whq.confirmHave': "לסמן „יש\" ב-{n} שורות, בכמות שכתובה בכל שורה?",
  'whq.confirmNone': "לסמן „אין\" ב-{n} שורות? הרכש יזמין את כולן.",
  'whq.yesHaveAll': "כן, יש לכולם",
  'whq.yesNoneAll': "כן, אין לכולם",
  'whq.required': "נדרש",
  'whq.subProposed': "הוצע חלופי",
  'whq.waitsEngineer': "מחכה למהנדס",
  'whq.onShelf': "יש במדף",
  'whq.issueNow': "נפק עכשיו",
  'whq.issueLater': "יש — לנפק אחר כך",
  'whq.other': "יש משהו אחר",
  'whq.countNote': "הכמות שכתבת נרשמת גם כספירה של הפריט. יש פחות מהנדרש? כתוב כמה יש — החסר עובר לרכש.",
  'whq.notInList': "„{v}\" לא ברשימה של {p} — לא לנפק אותו לכאן.",
  'whq.alreadyScanned': "{name} — כבר נסרק ({lines}).",
  'whq.fromShelf': "מ-{loc}",
  'whq.scanItem': "סרוק פריט",
  'whq.scanPick': "סרוק פריט לליקוט — {p}",
  'whq.scanned': "נסרקו",
  'whq.clearAll': "נקה הכל",
  'whq.pickAll': "סמן הכל (בלי סריקה)",
  'whq.toIssue': "לנפק {name}",
  'whq.noLocation': "אין מיקום",
  'whq.boxSwap': "קופסא מחליפה קופסא",
  'whq.toStock': "העבר למלאי",
  'whq.labelsNote': "מדבקה לכל שורה, ומדבקת קיט כשיש יותר משורה אחת.",
  'whq.issuedLines': "נופקו {n} שורות → {p}",
  'whq.issueWithLabels': "נפק {n} + מדבקות",
  'whq.whereTo': "לאן לשים",
  'whq.whereToQ': "לאן לשים?",
  'whq.scanShelf': "סרוק מדף",
  'whq.wentToStock': "נכנס למלאי",
  'whq.issuedTo': "נופק ל-{p}",
  'whq.waitsIssue': "מחכה ב„לנפק\" של {p}",
  'whq.receivedFrom': "נקלט מ{s}: {lines}",
  'whq.supplier': "ספק",
  'whq.partNotIssued': "חלק לא נופק (סריאלי חסר או קליטה חלקית) — הוא ב„לנפק\"",
  'whq.arrived': "הגיע",
  'whq.stateOf': "מצב {name}",
  'whq.ok': "תקין",
  'whq.notOk': "לא תקין",
  'whq.deliveryNoteOpt': "תעודת משלוח (לא חובה)",
  'whq.deliveryNote': "תעודת משלוח",
  'whq.receiveHint': "„קלוט ונפק\" מדפיס מדבקה מיד. „רק קלוט\" מעביר ל„לנפק\" של {p}.",
  'whq.receiveHintStock': "הוזמן למלאי: נכנס למלאי בקליטה, אין מה לנפק.",
  'whq.receiveIssue': "קלוט ונפק",
  'whq.receiveOnly': "רק קלוט",
  'whq.receiveStock': "קלוט למלאי",
  'whq.finish': "סיום",
  'whq.act.have': "יש במלאי",
  'whq.act.none': "אין",
  'whq.act.issue': "נפק",
  'whq.act.receive': "הגיע",
  'whq.act.undoIssue': "ביטול ניפוק",
  'whq.act.return': "החזרה למחסן",
  'whq.closeAct': "סגור: {a}",
  'whq.saving': "נשמר…",
  'whq.moreOn': "פעולות נוספות על {name}",
  'whq.undoneIssue': "ניפוק בוטל: {name} — חזר ל„לנפק\"",
  'whq.barcodeUnknown': "הברקוד לא מוכר. כתוב את שם הפריט בשדה „פריט\", או חבר את הברקוד למק״ט בכרטיס הפריט.",
  'whq.scanOrName': "סרוק פריט או כתוב את שמו.",
  'whq.qtyAboveZero': "כתוב כמות גדולה מאפס.",
  'whq.pickProject': "בחר לאיזה פרויקט — העלות נרשמת לפרויקט.",
  'whq.deskIssued': "ניפוק בלי בקשה: {name} ×{q} → {p} (לא מתוכנן)",
  'whq.deskReturned': "החזרה למחסן: {name} ×{q}",
  'whq.deskIssueHint': "עובד בא לדלפק ומבקש. נרשם „לא מתוכנן\".",
  'whq.deskReturnHint': "מה שחזר מהרצפה. נכנס למלאי.",
  'whq.itemBarcode': "ברקוד פריט",
  'whq.item': "פריט",
  'whq.itemName': "שם פריט",
  'whq.qty': "כמות",
  'whq.inSystem': "במערכת",
  'whq.unverified': "(לא אומת)",
  'whq.locationIs': "מיקום: {loc}",
  'whq.project': "פרויקט",
  'whq.whichProject': "לאיזה פרויקט?",
  'whq.clearWorker': "נקה עובד",
  'whq.scanBadgeOpt': "סרוק כרטיס עובד (לא חובה)",
  'whq.badge': "כרטיס עובד",
  'whq.returnToStock': "החזר למלאי",
  'whq.serialEach': "מספר סריאלי לכל יחידה ({label}):",
  'whq.serialN': "סריאלי {i}",
  'whq.photo': "תמונה: {name}",
  'whq.subSkuReq': "חפש או סרוק את הפריט, או תאר אותו במילים.",
  'whq.subFind': "חפש לפי תיאור או מק״ט, או סרוק",
  'whq.subNotFound': "לא נמצא בקטלוג. אפשר להשאיר את התיאור ולצרף תמונה.",
  'whq.subChange': "בחר פריט אחר",
  'whq.subPhotoOn': "יש תמונה, החלף",
  'whq.subNoteReq': "כתוב למה זה מתאים — המהנדס מחליט לפי זה.",
  'whq.subSent': "הוצע חלופי ל{name} ({p}) — מחכה למהנדס הפרויקט או למנהל ההנדסה",
  'whq.subAsk': "יש משהו אחר במלאי במקום {name} ×{q}?",
  'whq.subSkuPh': "מק״ט במלאי (סרוק)",
  'whq.subSku': "מק״ט החלופי",
  'whq.subWhyPh': "למה זה מתאים (חובה)",
  'whq.subWhy': "למה מתאים",
  'whq.takePhotoOpt': "צלם (לא חובה)",
  'whq.sendApproval': "שלח לאישור",
  'whq.subNote': "מייל והתראה למהנדס הפרויקט ולמנהל ההנדסה — אחד מהם מספיק. עד התשובה השורה לא מוזמנת. אושר → עובר ל„לנפק\".",
  'whq.repOrdered': "הספק שולח חלופי (ב„לקבל\")",
  'whq.repSends': "הספק שולח חלופי",
  'whq.pickupWaits': "מחכה לאיסוף („להחזיר לספק\")",
  'whq.buyerDecides': "הרכש מחליט",
  'whq.rejectedDone': "{name} נפסל — {what}",
  'whq.rejectSaved': "נרשם על הפסילה של {name}. הרכש קיבל הודעה.",
  'whq.dontKnow': "לא יודע — הרכש מחליט",
  'whq.waitPickup': "מחכה לאיסוף",
  'whq.rejectAsk': "{name} נפסל — לא נכנס למלאי ולא לפרויקט. מה {s} אמר?",
  'whq.theSupplier': "הספק",
  'whq.supplierAnswer': "תשובת הספק",
  'whq.whenArrive': "מתי יגיע",
  'whq.saidPh': "מה הספק אמר (לא חובה)",
  'whq.said': "מה הספק אמר",
  'whq.photoDefect': "צלם את הפגם",
  'whq.rejectNote': "דרישה חלופית כבר נפתחה אוטומטית. „שולח חלופי\" מזמין אותה מאותו ספק. זיכוי וביטול — אצל הרכש.",
  'whq.pickedUp': "{name} נאסף ע״י {s}",
  'whq.pickedBtn': "נאסף",
  'whq.tostock.SURPLUS': "עודף",
  'whq.tostock.NOT_NEEDED': "לא נדרש",
  'whq.tostock.REPLACED': "הוחלף",
  'whq.tostock.PROJECT_CANCELLED': "הפרויקט בוטל",
  'whq.pickReason': "בחר סיבה.",
  'whq.movedToStock': "{name} ×{q} הועבר מ-{p} למלאי",
  'whq.stillNeeds': "הפרויקט עדיין צריך — חזר ל„יש במלאי?\"",
  'whq.toStockTitle': "העבר למלאי: {name} ×{q}",
  'whq.reason': "סיבה",
  'whq.reasonPick': "סיבה…",
  'whq.noteOpt': "הערה (לא חובה)",
  'whq.note': "הערה",
  'whq.stillNeedsShort': "הפרויקט עדיין צריך",
  'whq.move': "העבר",
  'whq.toStockNote': "מהנדס הפרויקט והרכש מקבלים הודעה. הכמות כבר במלאי מהקליטה.",
  'whq.undo.receipt': "בטל קליטה",
  'whq.undo.issue': "בטל ניפוק",
  'whq.undo.answer': "בטל תשובה",
  'whq.whyCancel': "כתוב למה מבטלים.",
  'whq.whyCancelPh': "למה מבטלים (חובה, נשמר ביומן)",
  'whq.cancelReason': "סיבת ביטול",
  'whq.undoneAnswer': "התשובה בוטלה — השורה חזרה ל„יש במלאי?\"",
  'whq.undoneSome': "בוטלו {done} מתוך {total}. השאר: {err}",
  'whq.doneToday': "נעשה היום במחסן:",
  'whq.listFailed': "הרשימה לא נטענה — רענן את הדף.",
  'whq.tool.all': "הכל",
  'whq.tool.in': "במחסן",
  'whq.tool.temp': "זמני",
  'whq.tool.perm': "קבוע",
  'whq.tool.late': "לא הוחזר",
  'whq.tool.damaged': "פגום",
  'whq.tool.lost': "אבד",
  'whq.since': "מאז {d}",
  'whq.jobEnded': "עבודה הסתיימה {d}",
  'whq.daysLate_one': "יום אחד באיחור",
  'whq.daysLate_many': "{n} ימים באיחור",
  'whq.takenToday': "נלקח היום {t}",
  'whq.taken': "נלקח {d}",
  'whq.days_one': "יום אחד",
  'whq.days_many': "{n} ימים",
  'whq.serialShort': "ס״ד {s}",
  'whq.noSerialOld': "בלי מספר סידורי · כלי ישן",
  'whq.warrantyTo': "אחריות עד {d}",
  'whq.warrantyOver': "אחריות פגה {d}",
  'whq.printLabel': "הדפס מדבקה",
  'whq.backInUse': "{name} — חזר לשימוש",
  'whq.toolFilter': "סינון כלים",
  'whq.searchName': "חיפוש לפי שם",
  'whq.searchTool': "חיפוש כלי לפי שם",
  'whq.newTool': "+ כלי חדש",
  'whq.yourTools': "הכלים שאצלך. ניפוק והחזרה — בדלפק המחסן.",
  'whq.loading': "טוען…",
  'whq.toolsFailed': "רשימת הכלים לא נטענה. רענן את הדף; אם זה חוזר — דווח על תקלה.",
  'whq.noToolName': "אין כלי בשם הזה. כלי ישן שעוד לא נרשם — „+ כלי חדש\" ← כלי ישן.",
  'whq.noTools': "אין כאן כלים.",
  'whq.stickerN': "מדבקה {s}",
  'whq.invoice': "חשבונית",
  'whq.toFix': "לתיקון או לאחריות ספק",
  'whq.shelfN': "מדף {s}",
  'whq.return': "החזרה",
  'whq.issueBtn': "ניפוק",
  'whq.fixedBack': "תוקן, חזר לשימוש",
  'whq.foundBack': "נמצא, חזר לשימוש",
  'whq.backOk': "{name} — חזר למחסן",
  'whq.backDamaged': "{name} — חזר פגום, יצא משימוש. המנהל קיבל התראה.",
  'whq.backLost': "{name} — סומן „אבד\". המנהל קיבל התראה.",
  'whq.returnState': "מצב בהחזרה",
  'whq.defectQ': "מה הפגם? (לא חובה)",
  'whq.lostQ': "איפה אבד? (לא חובה)",
  'whq.returnNote': "הערה להחזרה",
  'whq.returnBtn': "החזר",
  'whq.badBadge': "זה לא כרטיס עובד מוכר. סרוק את התג של העובד, או בדוק במסך העובדים שהוא פעיל.",
  'whq.scanBadge': "סרוק כרטיס עובד",
  'whq.continue': "המשך",
  'whq.toolsOf': "כלים — {name}",
  'whq.badgeN': "כרטיס {n}",
  'whq.lateN': "{n} באיחור",
  'whq.hasNow': "אצלו עכשיו",
  'whq.hasNone': "אין אצלו כלים מהמחסן.",
  'whq.oldTool': "כלי ישן",
  'whq.pickTool': "בחר כלי — סרוק את המדבקה או חפש לפי שם.",
  'whq.tempJob': "בחר עבודת שטח, או עבור ל„פרויקט\".",
  'whq.tempProject': "בחר פרויקט: חפש לפי מספר או שם.",
  'whq.tool': "כלי",
  'whq.clearTool': "נקה כלי",
  'whq.scanOrSearch': "סרוק מדבקה או חפש לפי שם",
  'whq.dest.internal': "פנימי",
  'whq.pickDest': "בחר לאן הכלי יוצא: פרויקט, פנימי או עבודת שטח.",
  'whq.findProject': "חפש פרויקט לפי מספר או שם",
  'whq.noProject': "אין פרויקט פתוח כזה. בדוק את המספר, או בחר „פנימי\".",
  'whq.clearProject': "נקה פרויקט",
  'whq.issuedBy': "ניפק: {name}",
  'whq.unknownTool': "מדבקה {s} לא רשומה. בדוק את המספר, או רשום את הכלי ב„+ כלי חדש\" בלשונית „כלים\".",
  'whq.toolNotYours': "הכלי {s} לא רשום עליך. לפרטים פנה למחסן.",
  'whq.notTool': "זו לא מדבקת כלי מוכרת. סרוק את המדבקה, או חפש את הכלי לפי שם.",
  'whq.noMatch': "לא נמצא. בדוק את האיות, או סרוק.",
  'whq.scanBadgeOrName': "סרוק כרטיס עובד או חפש לפי שם",
  'whq.scanBadgeToolOrName': "סרוק כרטיס או מדבקה, או חפש לפי שם",
  'whq.blockedOut': "{name} אצל {who} מאז {d} · {dest}.",
  'whq.blockedDamaged': "{name} פגום ויצא משימוש. אחרי תיקון לחץ „תוקן, חזר לשימוש\".",
  'whq.blockedLost': "{name} מסומן „אבד\". אם נמצא לחץ „נמצא, חזר לשימוש\".",
  'whq.alreadyHis': "{name} כבר אצל {who}.",
  'whq.moveTo': "העבר אל {name}",
  'whq.movedNote': "הועבר אל {name}",
  'whq.openHolder': "פתח את הלוח של {who}",
  'whq.camera': "מצלמה",
  'whq.projectTools': "כלים בפרויקט",
  'whq.kind': "סוג",
  'whq.issueKind': "סוג ניפוק",
  'whq.whereReq': "לאן (חובה)",
  'whq.destination': "יעד",
  'whq.fieldJob': "עבודת שטח",
  'whq.job': "עבודה",
  'whq.noJobs': "אין לו עבודות שטח פתוחות. בחר „פרויקט\" או „פנימי\".",
  'whq.noFreeTool': "אין כלי פנוי במחסן בשם הזה.",
  'whq.toolNameReq': "כתוב את שם הכלי.",
  'whq.toolSaved': "{name} נרשם",
  'whq.ifKnown': "אם ידוע",
  'whq.newToolName': "כלי חדש",
  'whq.newToolHint': "כלי חדש — כל השדות חובה",
  'whq.oldToolHint': "כלי ישן — רק שם חובה",
  'whq.toolName': "שם הכלי",
  'whq.makerModel': "יצרן ודגם",
  'whq.serialNumber': "מספר סידורי",
  'whq.supplierCap': "ספק",
  'whq.purchaseDate': "תאריך רכישה",
  'whq.warrantyMonths': "אחריות (חודשים)",
  'whq.until': "עד {d}",
  'whq.invoiceDoc': "חשבונית / תעודה",
  'whq.shelf': "מדף",
  'whq.savePrint': "שמור והדפס מדבקה",
  'whq.oldToolLabel': "מדבקה לכלי ישן — רשות. בלי מדבקה מוצאים אותו בחיפוש לפי שם.",
  'whq.inStockNoReturn': "{name} במחסן — אין מה להחזיר.",
  'whq.scanReceiver': "{name} — סרוק עכשיו את כרטיס העובד שמקבל אותו.",
  'whq.notRecognized': "לא זיהיתי כרטיס עובד או מדבקת כלי. כלי ישן בלי מדבקה — חפש אותו לפי שם בלשונית „כלים\".",
  'whq.scanBadgeDot': "סרוק את כרטיס העובד.",
  'whq.scanBadgeOrTool': "סרוק את כרטיס העובד או את מדבקת הכלי.",
  'whq.badgeOrTool': "כרטיס עובד או מדבקת כלי",
  'whq.scanBadgeOrToolPh': "סרוק כרטיס עובד או מדבקת כלי",
  'whp.rankNote': "{n} הוקדם ע״י {by} · {at}",
  'whp.rankFirst': "העבר לראש התור",
  'whp.rankClear': "החזר לסדר לפי תאריך",
  'whp.rankDone': "{n} בראש התור",
  'whp.rankCleared': "{n} חזר לסדר לפי תאריך",
  'whp.pickHint': "בחר ערכה, או סרוק פרויקט או תא למעלה.",
  'whp.subProjects': "{n} — תתי-פרויקטים, ערכה לכל אחד",
  'whp.allHere': "הכל במפעל",
  'whp.kitMissing': "חסרים {n} מתוך {of}",
  'whp.needs': "הייצור צריך",
  'whp.col.project': "פרויקט",
  'whp.col.name': "שם",
  'whp.col.need': "הייצור צריך",
  'whp.col.plant': "במפעל",
  'whp.col.notOrdered': "לא הוזמן",
  'whp.col.late': "באיחור",
  'whp.col.cell': "תא",
  'whp.light.r': "הייצור מחכה",
  'whp.light.y': "חסר",
  'whp.light.g': "הכל במפעל",
  'whp.none': "אין.",
  'whp.stockWord': "מלאי",
  'whp.clean.late': "הובטח ועבר, אין קליטה",
  'whp.clean.lateSub': "{sup} · הובטח {date} · עברו {days} ימים · {project}",
  'whp.clean.arrived': "הגיע",
  'whp.clean.stuck': "התקבל לפרויקט ולא נופק מעל שבוע",
  'whp.clean.stuckSub': "{project} · {days} ימים במחסן",
  'whp.sub.items': "פריטים",
  'whp.sub.count': "ספירה",
  'whp.sub.loc': "מיקומים",
  'whp.sub.labels': "מדבקות",
  'whp.sub.tools': "כלים",
  'whp.desk.issue': "ניפוק בלי בקשה",
  'whp.desk.return': "החזרה למחסן",
  'whp.desk.unplanned': "קליטה בלי הזמנה",
  'whp.desk.toolIssue': "ניפוק כלי",
  'whp.desk.toolReturn': "החזרת כלים",
  'whp.cellN': "תא {cell}",
  'whp.noCell': "אין תא",
  'whp.cellOnFirst': "התא נפתח עם השורה הראשונה",
  'whp.needDate': "הייצור צריך {date}",
  'whp.noDate': "אין תאריך",
  'whp.notReleased': "עוד לא בייצור",
  'whp.needShort': "צריך {date}",
  'whp.firstStation': "לתחנה הראשונה ({station})",
  'whp.ready': "מוכן",
  'whp.missingN': "חסר {n}",
  'whp.answerAsk': "ענה „יש במלאי?\" ({n})",
  'whp.placeN': "לשים בתא ({n})",
  'whp.issueToWorker': "נפק ({n})",
  'whp.profiles': "סיכום פרופילים",
  'whp.moveCell': "העבר לתא אחר",
  'whp.cellMoved': "{n} עבר לתא {cell}",
  'whp.material': "מה במפעל ומה לא",
  'whp.counterForProject': "נפק פריט שלא ברשימה",
  'whp.lineMenu': "פעולות על {name}",
  'whp.ownerOf': "של: {who}",
  'whp.owner.wh': "מחסן",
  'whp.owner.saw': "מסור",
  'whp.owner.purchase': "רכש",
  'whp.owner.sub': "קבלן",
  'whp.owner.supplier': "ספק · הרכש עוקב",
  'whp.line.inCell': "בתא",
  'whp.line.qa': "ממתין לבדיקת קבלה",
  'whp.line.qaReject': "נפסל בבדיקת קבלה",
  'whp.owner.qa': "איכות",
  'whp.line.toPlace': "במחסן — לשים בתא",
  'whp.line.saw': "במחסן — ממתין למסור",
  'whp.line.notOrdered': "טרם הוזמן",
  'whp.line.atSub': "אצל {name}",
  'whp.line.toSub': "לשלוח לקבלן משנה",
  'whp.line.promised': "הובטח {date}",
  'whp.line.noPromise': "הוזמן, בלי תאריך",
  'whp.placeTitle': "לשים בתא · {project}",
  'whp.placeInCell': "הנח {n} בתא {cell}, העובד יאסוף",
  'whp.placeInNew': "הנח {n} בתא, העובד יאסוף",
  'whp.addItem': "הוסף פריט שלא ברשימה",
  'whp.addSearch': "חיפוש פריט לפי שם או מק״ט",
  'whp.addIssue': "נפק",
  'whp.addSerial': "כתוב {label} לכל יחידה",
  'whp.proj.find': "חיפוש בשורות הפרויקט",
  'whp.notAsked': "לא ביקשו את הפריט בדרישות של הפרויקט ({n}). לאשר ניפוק?",
  'whp.notAskedOk': "אשר ונפק",
  'whp.placeHint': "סרוק פריט, או לחץ על שורה.",
  'whp.placed': "{n} שורות של {project} בתא {cell}",
  'whp.placed_many': "{n} שורות של {project} בתא {cell}",
  'whp.placed_one': "שורה אחת של {project} בתא {cell}",
  'whp.scanItem': "סרוק פריט",
  'whp.scanned': "נסרקו {n} מתוך {of}",
  'whp.markAll': "סמן הכל (בלי סריקה)",
  'whp.clearAll': "נקה הכל",
  'whp.scanNotInKit': "„{code}\" לא ברשימה של {project} — לא לשים אותו כאן.",
  'whp.scanAgain': "{name} — כבר נסרק.",
  'whp.noSku': "בלי מק״ט",
  'whp.issueTitle': "ניפוק · {project}",
  'whp.scanBadge': "העובד סורק כרטיס",
  'whp.issueNeedsBadge': "סרוק כרטיס עובד כדי לנפק",
  'whp.issueToName': "נפק {n} ל{name}",
  'whp.changeWorker': "עובד אחר",
  'whp.partialHint': "חלקי? בטל שורה — היא נשארת במחסן, והערכה ממשיכה.",
  'whp.issuedTo': "נופקו {n} שורות של {project} ל{name}",
  'whp.issuedTo_many': "נופקו {n} שורות של {project} ל{name}",
  'whp.issuedTo_one': "נופקה שורה אחת של {project} ל{name}",
  'whp.fact.qty': "כמות",
  'whp.fact.shelf': "מדף",
  'whp.fact.requested': "מי ביקש",
  'whp.fact.supplier': "ספק",
  'whp.fact.note': "הערה",
  'whp.lineNote': "הערה בשורה — לקרוא לפני שמנפקים:",
  'whp.fact.changed': "שינוי אחרון",
  'whp.placeOne': "לשים בתא",
  'whp.placedOne': "{name} בתא",
  'whp.removeOne': "הוצא מהתא",
  'whp.removedOne': "{name} יצא מהתא",
  'whp.receiveNow': "הגיע — קלוט",
  'whp.subOut': "יצא לקבלן",
  'whp.subOutDone': "נרשם: יצא ל{name}",
  'whp.subBack': "חזר — קלוט",
  'whp.subOutUndo': "בטל יציאה",
  'whp.subOutUndone': "היציאה בוטלה",
  'whp.toStock': "העבר למלאי",
  'whp.other': "יש משהו אחר",
  'whp.subWaits': "הוצע חלופי: {name} — מחכה למהנדס",
  'whp.countAllDone': "סיימת להיום — {n} פריטים נספרו.",
  'whp.countNeedQty': "כתוב כמה ספרת, 0 ומעלה.",
  'whp.countMatch': "{name}: תואם למערכת",
  'whp.countDiff': "{name}: {d} מול המערכת — המלאי עודכן",
  'whp.countMoved': "דרישות שחיכו: {issue} ללניפוק, {buy} לרכש",
  'whp.countStep': "פריט {n} מתוך {of}",
  'whp.neededSoon': "פרויקט יבקש בקרוב",
  'whp.countBoxes': "כמה קופסאות מלאות?",
  'whp.countQty': "כמה ספרת?",
  'whp.countShelf': "איפה זה? סרוק מדף",
  'whp.countSave': "שמור · הבא",
  'whp.countSkip': "לא מוצא — דלג",

  // צילום מהטלפון ב-QR (אלי 01/11/2026).
  'plink.button': "צלם מהטלפון",
  'plink.title': "צילום מהטלפון",
  'plink.scan': "סרוק את הקוד במצלמת הטלפון וצלם. התמונה תופיע כאן.",
  'plink.valid10': "בתוקף 10 דקות",
  'plink.qrAlt': "קוד QR לצילום מהטלפון",
  'plink.creating': "מפיק קוד…",
  'plink.createFailed': "הקוד לא נוצר. לחץ „קוד חדש” ונסה שוב.",
  'plink.expired': "תוקף הקוד פג. הפק קוד חדש כדי להמשיך לצלם.",
  'plink.newCode': "קוד חדש",
  'plink.received': "התקבלו {n} תמונות",
  'plink.received_one': "התקבלה תמונה אחת",
  'plink.received_many': "התקבלו {n} תמונות",
  'plink.loading': "בודק את הקישור…",
  'plink.take': "צלם",
  'plink.takeMore': "צלם עוד",
  'plink.preparing': "מכין את התמונה…",
  'plink.uploading': "מעלה… {pct}%",
  'plink.saved': "התמונה נשמרה",
  'plink.usedUp': "הגעת למספר התמונות של הקוד הזה. לעוד תמונות סרוק קוד חדש מהמחשב.",
  'plink.gone': "הקישור הזה כבר לא פעיל. סרוק קוד חדש מהמחשב.",
  'plink.badType': "זו לא תמונת JPG, PNG או WebP. צלם שוב במצלמה, או בחר תמונה אחרת.",
  'plink.tooBig': "התמונה גדולה מ-5MB. צלם שוב, או בחר תמונה קטנה יותר.",
  'plink.slow': "יותר מדי ניסיונות ברצף. המתן כמה דקות ונסה שוב.",
  'plink.failed': "התמונה לא נשמרה. נסה שוב, ואם זה חוזר סרוק קוד חדש מהמחשב.",
  'plink.network': "אין חיבור לשרת. בדוק את הקליטה בטלפון ונסה שוב.",
  'plink.kind.FAULT': "תמונה לאי-התאמה",
  'plink.kind.GAP': "תמונה לפער",
  'plink.kind.SERVICE_JOB': "תמונה לעבודת שטח",
  'plink.kind.SITE_PHOTO': "תמונת אתר",
  'plink.kind.STOCK_ITEM': "תמונה לפריט במחסן",
  'plink.kind.SHELF_CARD': "תמונה לכרטיס מדף",
  'plink.kind.PART': "תמונה למק״ט",
  'plink.kind.PROJECT_IMAGE': "תמונת פרויקט",
  'plink.kind.ORDER_IMAGE': "תמונה להזמנה",
  'plink.kind.EMPLOYEE': "תמונת עובד",
  'plink.kind.USER': "תמונת משתמש",
  'plink.kind.CATALOG_ITEM': "תמונה לפריט בקטלוג",
  'plink.kind.CATALOG_FOLDER': "תמונה לתיקייה בקטלוג",
  'plink.kind.RETURN_PHOTO': "תמונה לשורת רכש",
  'plink.kind.EXPENSE_RECEIPT': "צילום קבלה",
  'plink.kind.DELIVERY_PROOF': "צילום תעודת משלוח",
  'plink.kind.SUBSTITUTE_PHOTO': "תמונה לפריט חלופי",
  'plink.kind.CUSTOMER_EVIDENCE': "צילום אישור לקוח",
  'plink.kind.APPROVAL_EVIDENCE': "צילום אישור לקוח",
  'plink.savedTitle': "נשמר",
  'plink.savedHint': "אפשר להוסיף עכשיו תמונה מהטלפון.",
  // ── דף הרכבה מודפס + קיוסק הרכבה (אלי 02/11/2026) ──
  'asm.sheetButton': "דף הרכבה להדפסה",
  'asm.sheetFail': "לא הצלחתי להכין את דף ההרכבה — נסה שוב; אם זה חוזר, פנה למנהל המערכת.",
  'asm.popupBlocked': "הדפדפן חסם את חלון ההדפסה — אפשר חלונות קופצים לאתר הזה ונסה שוב.",
  'asm.sheet.customer': "לקוח",
  'asm.sheet.devEng': "מהנדס מתכנן",
  'asm.sheet.name': "שם הפרויקט",
  'asm.sheet.delivery': "מסירה",
  'asm.sheet.plannedShort': "מתוכננת",
  'asm.sheet.prodEng': "מהנדס ייצור",
  'asm.sheet.delivered': "נמסר",
  'asm.sheet.planned': "מסירה מתוכננת",
  'asm.sheet.scan': "סרוק להרכבה",
  'asm.sheet.project': "פרויקט",
  'asm.sheet.title': "דף הרכבה",
  'asm.sheet.noImage': "אין תמונה לפרויקט",
  'asm.title': "הרכבה",
  'asm.scanBadge': "סרוק את התג כדי להתחיל הרכבה",
  'asm.badgeHint': "או הקלד את מספר התג",
  'asm.badgePlaceholder': "מספר תג",
  'asm.loading': "טוען…",
  'asm.loadFail': "הדף לא נטען — בדוק שאתה ברשת המפעל ונסה שוב.",
  'asm.drawings': "שרטוטים להרכבה",
  'asm.noDrawings': "אין בפרויקט שרטוטים עם שלב הרכבה — פנה למהנדס הייצור.",
  'asm.notInModel': "לא במודל",
  'asm.assembled': "הורכב",
  'asm.mark': "הורכב",
  'asm.markN': "הורכב ({n})",
  'asm.pick': "בחר שרטוט ברשימה או במודל, ואז לחץ „הורכב“.",
  'asm.openDrawing': "שרטוט",
  'asm.openParent': "שרטוט ההרכבה",
  'asm.finish': "סיים",
  'asm.finished': "שעון ההרכבה נסגר. אפשר לסרוק שוב כדי להמשיך.",
  'asm.since': "בהרכבה מ-{t}",
  'asm.marked': "נרשם: {part} — {min} דק׳",
  'asm.missing': "שים לב — חסרים חלקים: {list}",
  'asm.stillHere': "אתה עדיין כאן?",
  'asm.stillHereYes': "כן, אני כאן",
  'asm.stillHereClose': "בלי תשובה שעון ההרכבה ייסגר בעוד {m} דק׳.",
  'asm.stillHereShift': "השעון ממשיך לרוץ. לחץ כדי לאשר שאתה עדיין מרכיב.",
  'asm.no3d': "אין מודל תלת-ממד לפרויקט — עבוד מהרשימה.",
  'asm.needCode': "נדרש קוד מנהל כדי לסמן — בקש ממנהל הייצור להקליד אותו כאן.",
  'asm.managerCode': "קוד מנהל",
  'asm.send': "אשר",
  'asm.cancel': "ביטול",
  'asm.progress': "{done} מתוך {all} הורכבו",
  'asm.otherWorker': "עובד אחר",
  'asm.todo': "להרכבה",
  'asm.doneList': "הורכבו",
  'asm.qty': "×{n}",
  'asm.myMarks': "סימנת היום",
  'err.asmSheetUnknown': "הדף הזה אינו מוכר — הדפס דף הרכבה חדש מכרטיס הפרויקט.",
  'err.asmStationClockOpen': "יש לך שעון פתוח בעמדה על {partNumber} — סיים אותו בעמדה, ואז סרוק כאן שוב.",
  'err.asmAlreadyAssembled': "{partNumber} כבר סומן הורכב — בחר שרטוט אחר.",
  'err.asmNotAssemblyDrawing': "לשרטוט הזה אין שלב הרכבה בפרויקט — בחר שרטוט מהרשימה.",
  'err.asmSessionEnded': "שעון ההרכבה כבר נסגר — סרוק את התג שוב כדי לפתוח חדש.",
  // ── „הבוקר" של מנהל הייצור (03/11/2026) ──
  'morning.title': 'הבוקר',
  'morning.weekUntil': 'השבוע = עד {date}',
  'morning.whatsNew': 'מה חדש',
  'morning.weekView': 'לתצוגת השבוע ←',
  'morning.updates': 'שרטוטים חדשים שלא ראית, בפרויקטים שבייצור',
  'morning.updates_one': 'שרטוט חדש שלא ראית, בפרויקטים שבייצור',
  'morning.updates_many': 'שרטוטים חדשים שלא ראית, בפרויקטים שבייצור',
  'morning.updatesNc': '{n} אחרי אי-התאמה',
  'morning.updatesNc_one': 'אחד אחרי אי-התאמה',
  'morning.updatesNc_many': '{n} אחרי אי-התאמה',
  'morning.updatesOpen': 'פתח עדכוני ייצור',
  'morning.ship': 'מסירות השבוע',
  'morning.shipEmpty': 'אין מסירה באיחור ואין מסירה בשבוע הקרוב.',
  'morning.prod': 'סיום ייצור השבוע',
  'morning.prodEmpty': 'אין פרויקט שצריך לסיים ייצור בשבוע הקרוב.',
  'morning.dateOnly': 'ועוד {n} פרויקטים שעוד לא התחילו — רק תאריך, בלי חלקים. בלוח ←',
  'morning.dateOnly_one': 'ועוד פרויקט אחד שעוד לא התחיל — רק תאריך, בלי חלקים. בלוח ←',
  'morning.dateOnly_many': 'ועוד {n} פרויקטים שעוד לא התחילו — רק תאריך, בלי חלקים. בלוח ←',
  'morning.lateCount': '{n} באיחור',
  'morning.stuck': 'מה עוצר את השבוע',
  'morning.stuckEmpty': 'שום דבר לא עוצר את הפרויקטים של השבוע.',
  'morning.projects': '{n} פרויקטים',
  'morning.projects_one': 'פרויקט אחד',
  'morning.projects_many': '{n} פרויקטים',
  'morning.moreInProject': 'עוד {n} בפרויקט',
  'morning.open': 'פתח',
  'morning.decisions': 'מחכה להחלטה שלי',
  'morning.decisionsEmpty': 'אין החלטות שמחכות לך.',
  'morning.hours': 'שעות לחתימה',
  'morning.hoursAuto': 'שעונים נסגרו בלי דיווח',
  'morning.hoursAuto_one': 'שעון נסגר בלי דיווח',
  'morning.hoursAuto_many': 'שעונים נסגרו בלי דיווח',
  'morning.hoursUnconfirmed': '{time} לא מאושרות',
  'morning.hoursAssembly': '{n} מעמדת ההרכבה',
  'morning.hoursAssembly_one': 'אחד מעמדת ההרכבה',
  'morning.hoursAssembly_many': '{n} מעמדת ההרכבה',
  'morning.hoursNoMinutes': 'שלבים בלי דקות',
  'morning.hoursNoMinutes_one': 'שלב בלי דקות',
  'morning.hoursNoMinutes_many': 'שלבים בלי דקות',
  'morning.load': 'עומס השבוע מול קיבולת',
  'morning.loadEmpty': 'אין נתוני קיבולת לשבוע הזה.',
  'morning.loadOf': '{load} מתוך {cap} ש׳',
  'morning.loadUnscheduled': 'עוד {h} ש׳ לא משובצות',
  'morning.loadPlan': 'לתכנון הקיבולת ←',
  'morning.who': 'מי סרק, על מה וכמה זמן',
  'morning.noParts': 'אין חלקים במערכת',
  'morning.partsDone': '{done}/{total} חלקים גמורים',
  'morning.afterFat': 'עבר FAT, ממתין למשלוח',
  'morning.openNc': '{n} אי-התאמות פתוחות',
  'morning.openNc_one': 'אי-התאמה פתוחה אחת',
  'morning.openNc_many': '{n} אי-התאמות פתוחות',
  'morning.urgent': 'דחוף',
  'morning.handle': 'טפל',
  'morning.lateDays': 'באיחור {n} ימים',
  'morning.lateDays_one': 'באיחור יום אחד',
  'morning.lateDays_many': 'באיחור {n} ימים',
  'morning.today': 'היום',
  'morning.tomorrow': 'מחר',
  'morning.inDays': '{date} · {n} ימים',
  'workersDay.min': '{n} דק׳',
  'workersDay.hours': '{h} ש׳',
  'workersDay.assemblyStage': 'הרכבה',
  'workersDay.today': 'היום',
  'workersDay.onDay': 'ב-{date}',
  'workersDay.todayTab': 'היום',
  'workersDay.prevTab': 'יום קודם · {date}',
  'workersDay.summary': '{seen} מתוך {total} סרקו',
  'workersDay.error': 'אי אפשר לטעון את הסריקות. רענן את המסך.',
  'workersDay.notScanned': 'לא סרקו {when} ({n}):',
  'workersDay.nobody': 'אף עובד לא סרק {when}.',
  'workersDay.badgeAt': 'תג ב-{time}',
  'workersDay.noBadge': 'בלי תג',
  'workersDay.badgeOnly': 'סרק תג, אין דיווח עבודה',
  'workersDay.working': 'עובד עכשיו',
  'workersDay.assembly': 'עמדת הרכבה',
  'workersDay.unconfirmed': 'לא מאושר — לחתימה',
};
